import { z } from 'zod'
import { queryBoolean } from './zod-query'
import { idParam } from './id-validators'
// batch-67 meldingen-ontvangers (FO-500) - het "waarom"-spoor op het lijst-DTO.
import type { NotificatieOntvangerBronDTO } from './notificatie-bron'

// ─── Enums (matching Prisma enums as string unions) ──────────────────────────

export const NOTIFICATIE_CATEGORIE = [
  'HOGE_PRIO',
  'OPERATIONEEL',
  'AANKONDIGING',
  'REMINDER',
  // Ronde 3 — PR-8 AI-rapportage kritieke aandachtspunten
  'AI_RAPPORTAGE',
  // Ronde 4 W7 (spec ronde-4/07 §1.9) — signalering-engine categorieën
  'COMPLIANCE',
  'HR_SIGNALERING',
  'PLANNING_ALERT',
  'SYSTEEM_ERROR',
] as const
export type NotificatieCategorie = (typeof NOTIFICATIE_CATEGORIE)[number]

export const NOTIFICATIE_KANAAL_TYPE = ['IN_APP', 'PUSH', 'EMAIL'] as const
export type NotificatieKanaalType = (typeof NOTIFICATIE_KANAAL_TYPE)[number]

export const NOTIFICATIE_NIVEAU = ['STIL', 'NORMAAL', 'BELANGRIJK'] as const
export type NotificatieNiveau = (typeof NOTIFICATIE_NIVEAU)[number]

export const NOTIFICATIE_AFLEVERING_STATUS = [
  'WACHTEND',
  'GELEVERD',
  'GEFAALD',
  'OVERGESLAGEN',
] as const
export type NotificatieAfleveringStatus = (typeof NOTIFICATIE_AFLEVERING_STATUS)[number]

// ─── Label constants (NL-talig voor UI) ──────────────────────────────────────

export const NOTIFICATIE_CATEGORIE_LABELS: Record<NotificatieCategorie, string> = {
  HOGE_PRIO:      'Belangrijk',
  OPERATIONEEL:   'Operationeel',
  AANKONDIGING:   'Aankondiging',
  REMINDER:       'Herinnering',
  AI_RAPPORTAGE:  'AI-rapportage',
  COMPLIANCE:     'Compliance / Arbo',
  HR_SIGNALERING: 'HR-signalering',
  PLANNING_ALERT: 'Planning-alert',
  SYSTEEM_ERROR:  'Systeemfout',
}

export const NOTIFICATIE_KANAAL_LABELS: Record<NotificatieKanaalType, string> = {
  IN_APP: 'In-app',
  PUSH:   'Push',
  EMAIL:  'E-mail',
}

export const NOTIFICATIE_AFLEVERING_STATUS_LABELS: Record<NotificatieAfleveringStatus, string> = {
  WACHTEND:     'Wachtend',
  GELEVERD:     'Geleverd',
  GEFAALD:      'Gefaald',
  OVERGESLAGEN: 'Overgeslagen',
}

// Status badge variant voor de admin-tabel.
export const NOTIFICATIE_AFLEVERING_BADGE_VARIANT: Record<
  NotificatieAfleveringStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  WACHTEND:     'info',
  GELEVERD:     'success',
  GEFAALD:      'danger',
  OVERGESLAGEN: 'secondary',
}

// ─── Event-types ─────────────────────────────────────────────────────────────

export const NOTIFICATIE_EVENT_TYPE_KEY = [
  'WERKBON_TOEGEWEZEN', 'WERKBON_DAG_VERPLAATST', 'WERKBON_TIJDEN_GEWIJZIGD', 'WERKBON_DAG_VERWIJDERD',
  'VERLOF_INGEDIEND', 'VERLOF_GOEDGEKEURD', 'VERLOF_AFGEKEURD',
  // Spec 34 — verlof-intrekking als 2-staps-goedkeuringsketen (medewerker →
  // teamleider → HR). AANGEVRAAGD → teamleider (leave.teamleiders);
  // TL_GOEDGEKEURD → HR (all-hr); INGETROKKEN/AFGEWEZEN → de medewerker
  // (leave.requester) en zijn `mandatory: true` op template-niveau zodat ze
  // via notify() altijd geleverd worden (zie registry.ts + leave-v2.ts).
  'VERLOF_INTREKKING_AANGEVRAAGD',
  'VERLOF_INTREKKING_TL_GOEDGEKEURD',
  'VERLOF_INGETROKKEN',
  'VERLOF_INTREKKING_AFGEWEZEN',
  'VERZUIM_GEMELD', 'VERZUIM_HERSTELD', 'VERZUIM_LANGDURIG',
  'DECLARATIE_INGEDIEND', 'DECLARATIE_GOEDGEKEURD', 'DECLARATIE_AFGEKEURD', 'DECLARATIE_UITBETAALD',
  'MUTATIEVERZOEK_INGEDIEND', 'MUTATIEVERZOEK_BESLIST',
  'KLANTPORTAAL_AANVRAAG_BINNENGEKOMEN',
  'UREN_INVULLEN_REMINDER',
  'COMMUNICATIE_BERICHT_NIEUW',
  // F3.7 — onboarding in-app/push channel (mail-zijde wordt door
  // `mailService.fromTrigger({ triggerKey: 'ONBOARDING_REMINDER' })` afgehandeld).
  // Door óók een NotificatieEvent te firen krijgt de medewerker zijn herinnering
  // in de portal-bell + (indien VAPID-keys gezet) op zijn device — naast de mail.
  'ONBOARDING_HERINNERING',
  // Fase 6.3 — taak met prioriteit URGENT: push + in-app naar de eigenaar
  // zodra een nieuwe URGENT-taak is gegenereerd (verlopen certificaat,
  // overschreden deadline, …). Wordt verstuurd vanuit `syncTakenVoorUser()`
  // bij `created` of een upgrade naar URGENT.
  'TAAK_URGENT',
  // Fase 6.5 — status van een lead in een acquisitie-lijst is gewijzigd.
  // Notificatie gaat naar de toegewezen-aan (eigenaar van de lijst). Komt
  // vooral van waarde bij KLANT_GEWORDEN → sales-team weet dat een prospect
  // is geconverteerd zonder de lijst-detail open te hebben.
  'TARGETING_LEAD_STATUS_CHANGED',
  // APK-verval-notificatie aan de vaste bestuurder (bestuurder-arm van de
  // FO-394-verval-signaalreeks; PUSH + EMAIL + IN_APP). Sinds batch-46 gevuurd
  // vanuit lib/voertuig-verval/verval-reeks.ts (de losse materieel-apk-reminder
  // is vervallen), met linkOverride naar /mijn-voertuig (portal).
  'MATERIEEL_APK_BINNENKORT',
  // Materieel — nieuwe openstaande RDW-terugroepactie (recall) ontdekt door de
  // dagelijkse RDW-sync. PUSH + EMAIL + IN_APP naar de toegewezen bestuurder +
  // fleet (planner/manager). Cron: apps/api/src/lib/cron/materieel-rdw-sync.ts.
  'MATERIEEL_TERUGROEPACTIE',
  // Fase 7.3 — formulier-inzending binnengekomen. Recipient = all-hr default
  // (HR keurt formulieren goed). Categorie OPERATIONEEL: in-app aan,
  // push/email default uit zodat HR-mailboxen niet vollopen bij high-volume.
  'FORMULIER_INGEDIEND',
  // Spec-uitbreiding 2026-05 — Reisuren-afwijking ingediend door monteur,
  // notify naar planner/manager voor goedkeuring (resolver `intake.team`).
  'REISUREN_AFWIJKING_INGEDIEND',
  // PR-B (spec 18) — 12 nieuwe event-types: certificaat-/contract-/proeftijd-
  // /rijbewijs-/voertuig-keuring-verloop (cron), planning-wijziging, 3 werkbon
  // status-events, en VERJAARDAG_VANDAAG met configurable.recipients-resolver.
  'CERTIFICAAT_VERLOOPT_BINNENKORT',
  'CERTIFICAAT_VERLOPEN',
  'CONTRACT_VERLOOPT_BINNENKORT',
  'CONTRACT_VERLOPEN',
  'VERJAARDAG_VANDAAG',
  'PLANNING_GEWIJZIGD',
  'WERKBON_INGEDIEND',
  'WERKBON_GOEDGEKEURD',
  'WERKBON_AFGEKEURD',
  'PROEFTIJD_VERLOOPT',
  'VOERTUIG_KEURING_VERLOPEN',
  'RIJBEWIJS_VERLOOPT',
  // PR-B follow-up (spec 18) — events voor downstream PR's J/L/M/K. PR-B is
  // registry-owner; deze keys worden door J/L/M/K aangeroepen zodra hun
  // stub-triggers vervangen worden door de echte notify-call.
  // J — reisuren-afwijking respons (terug naar de monteur).
  'REISUREN_AFWIJKING_GOEDGEKEURD',
  'REISUREN_AFWIJKING_AFGEKEURD',
  // L — keuzebudget-flow (4 statussen). Triggers in routes/benefit-requests.ts.
  'KEUZEBUDGET_AANVRAAG_INGEDIEND',
  'KEUZEBUDGET_AANVRAAG_GOEDGEKEURD_TEAMLEIDER',
  'KEUZEBUDGET_AANVRAAG_GOEDGEKEURD_HR',
  'KEUZEBUDGET_AANVRAAG_AFGEKEURD',
  // M — gesprek-flow. AANGEVRAAGD gebruikt resolver `gesprek.recipient` die
  // VERTROUWENSPERSOON-gesprekken STRIKT naar de gekoppelde partner-user
  // routeert (geen HR-leak).
  'GESPREK_AANGEVRAAGD',
  'GESPREK_INGEPLAND',
  'GESPREK_AFGEROND',
  'GESPREK_GEANNULEERD_DOOR_MEDEWERKER',
  'GESPREK_GEANNULEERD_DOOR_ADMIN',
  // K — facturatie: nieuwe factuur klaar; configurable.recipients (klant +
  // accounting).
  'FACTUUR_INGEDIEND',
  // PR-B1 (spec verzuim-workflow-accordering §6.1) — 5 nieuwe events voor de
  // accordering-workflow. WACHT_OP_ACCORDERING gaat naar TL + vaste
  // contactpersonen (caller-supplied userIds), GEACCORDEERD/AFGEKEURD_HR naar
  // all-hr, GEACCORDEERD_MEDEWERKER/AFGEKEURD naar employee.userId.
  'VERZUIM_WACHT_OP_ACCORDERING',
  'VERZUIM_GEACCORDEERD',
  'VERZUIM_GEACCORDEERD_MEDEWERKER',
  'VERZUIM_AFGEKEURD',
  'VERZUIM_AFGEKEURD_HR',
  // PR-C (voorraad-magazijn spec §6.3) — voorraad bereikt of zakt onder de
  // ingestelde minimumvoorraad. Rising-edge trigger vanuit boekMutatie().
  // Doelgroep `all-hr` (HR/inkoop bepaalt of er besteld moet worden).
  'VOORRAAD_LAAG',
  // FO-310 (batch-34) — karren & koffers: controle-vervaldatum-signaal
  // (verantwoordelijke + magazijn), monteur-meldingen (ontbrekend/kapot) naar
  // magazijn (resolver `voorraad.beheer`), en bestelling-ontvangen-terugkoppeling.
  'KARREN_CONTROLE_VERVALDATUM',
  'KARREN_MELDING_ONTBREEKT',
  'KARREN_MELDING_KAPOT',
  'KARREN_BESTELLING_ONTVANGEN',
  // Track-H quickwins-p2 — externe API-key/integratie verloopt binnen de
  // ingestelde reminder-window. Dagelijkse cron triggert dit naar ADMIN-users
  // zodat Kasper handmatig kan rouleren (auto-rotation = out of scope).
  'INTEGRATIE_EXPIRY_NABIJ',
  // Track-I (klantenportaal-audit) — klant-aanvraag afgewezen of afgerond
  // door backoffice. Notificatie-event-zijde dient als INTERN signaal voor de
  // backoffice-handler (`actor.requester` met `requesterUserId`). De mail
  // naar de klant gaat APART via `mailService.fromTrigger`.
  'KLANTPORTAAL_AANVRAAG_AFGEWEZEN',
  'KLANTPORTAAL_AANVRAAG_AFGEROND',
  // Track-I — klant accordeerde of weigerde een werkbon via het portaal.
  // Doel: werkbon-eigenaar (project-leider / aangemaakt-door) op de hoogte
  // brengen zodat de werkbon-flow door kan (factureren of corrigeren).
  'KLANTPORTAAL_WERKBON_GEACCORDEERD',
  'KLANTPORTAAL_WERKBON_GEWEIGERD',
  // Batch-10 #12 — periodieke klant-beoordeling: cron zet beoordeling(en) klaar
  // (intern signaal) + lage-score-signaal bij het invullen.
  'KLANTPORTAAL_BEOORDELING_KLAARGEZET',
  'KLANTPORTAAL_BEOORDELING_LAGE_SCORE',
  // Verjaardag-keuze (jaarlijkse januari-campagne) — 4 medewerker-events
  // (START, HERINNERING, DEADLINE, BEVESTIGD) + 1 TL-event (VRIJ_OMPLANNEN).
  // Resolvers: `verjaardag.medewerker` + `verjaardag.teamleider`. Het bestaande
  // VERJAARDAG_VANDAAG-event blijft onveranderd (felicitatie-broadcast).
  'VERJAARDAG_KEUZE_START',
  'VERJAARDAG_KEUZE_HERINNERING',
  'VERJAARDAG_KEUZE_DEADLINE',
  'VERJAARDAG_KEUZE_BEVESTIGD',
  'VERJAARDAG_VRIJ_OMPLANNEN',
  // Vakantie-campagne (PR #170) — 8 events voor jaarlijkse campagne-flow.
  'CAMPAGNE_EERSTE_MELDING',
  'CAMPAGNE_HERINNERING',
  'CAMPAGNE_DEADLINE',
  'CAMPAGNE_BEOORDELING_OPEN',
  'CAMPAGNE_GOEDGEKEURD',
  'CAMPAGNE_AFGEKEURD',
  'CAMPAGNE_AFGEWEZEN',
  'CAMPAGNE_WACHTLIJST',
  // Medewerker-app (PR #174) — 10 nieuwe events.
  'WEEKSTAAT_INGEDIEND',
  'WEEKSTAAT_GOEDGEKEURD',
  'WEEKSTAAT_AFGEKEURD',
  'DAG_UREN_HERINNERING',
  'DOCUMENT_ONDERTEKEND',
  'DOCUMENT_WACHT_OP_HANDTEKENING',
  'DOCUMENT_DEADLINE_NADERT',
  // batch-45 (FO-385) — document zichtbaar geworden in de portal van de medewerker.
  'DOCUMENT_ZICHTBAAR_VOOR_MEDEWERKER',
  // FO-126 (batch-24) — herinnering dat een document je leesbevestiging vereist.
  'DOCUMENT_LEESBEVESTIGING_VEREIST',
  'PLANNING_WIJZIGING_INGEDIEND',
  'PLANNING_WIJZIGING_GOEDGEKEURD',
  'PLANNING_WIJZIGING_AFGEKEURD',
  // Spec A §SS18.1 — werkbon-modificatie + reistijd-override
  'WERKBON_MEDEWERKER_VERVANGEN',
  'WERKBON_EXTRA_INZET',
  'REISTIJD_OVERRIDE',
  'WERKBON_SCOPE_WIJZIGING',
  // Spec C §4.4 — contactverzoeken (oude GESPREK_* keys blijven staan tijdens
  // de Track C migratie en zijn @deprecated zodra alle routes overgezet zijn).
  'CONTACT_VERZOEK_NIEUW',
  'CONTACT_VERZOEK_GELEZEN',
  'CONTACT_VERZOEK_INGEPLAND',
  'CONTACT_VERZOEK_AFGEHANDELD',
  'CONTACT_VERZOEK_GEANNULEERD',
  'CONTACT_VERZOEK_URGENT',
  // Spec D §14.1 — opleidings-events
  'OPLEIDING_AANGEVRAAGD',
  'OPLEIDING_AANVRAAG_GOEDGEKEURD',
  'OPLEIDING_AANVRAAG_AFGEWEZEN',
  'OPLEIDING_INGEPLAND',
  'OPLEIDING_HERINNERING',
  'OPLEIDING_AFGEROND',
  'OPLEIDING_GEANNULEERD',
  'OPLEIDING_HERHALING_NODIG',
  // Batch-6 voertuig-stream — cron-signaal: verstreken sessie nog niet afgerond.
  'OPLEIDING_SESSIE_AFRONDEN',
  // Ronde 4 W4 (spec 04 §5) — verplichte werkbon-notificaties.
  // De `mandatory`-flag op de template (zie registry.ts) zorgt dat deze
  // events user-voorkeuren / mute / snooze bypassen. Spec §11.1 — medewerker
  // moet ALTIJD weten dat zijn werkbon heropend of gecorrigeerd is.
  'WERKBON_HEROPEND_VERPLICHT',
  'WERKBON_GECORRIGEERD_VERPLICHT',
  // ─── Ronde 4 W7 (spec ronde-4/07 §1.8) — signalering-engine event-types ───
  // Gevuurd door de NotificationRule-engine (fireSignal). De engine rendert
  // titel/inhoud uit de rule-templates; deze keys hoeven dus geen rijke
  // registry-template — een neutrale fallback-template volstaat.
  // §3 — planning
  'PLANNING_OVERLAP',
  'PLANNING_REISTIJD_CONFLICT',
  'PLANNING_MAX_UREN_OVERSCHREDEN',
  'PLANNING_RUSTTIJD_ONVOLDOENDE',
  'PLANNING_REISTIJD_LANG',
  'PLANNING_MEDEWERKER_NIEUW_BIJ_KLANT',
  'PLANNING_PROFIEL_MISMATCH',
  'PLANNING_BV_MISMATCH',
  // §4 — verzuim
  'VERZUIM_EINDE_DAG_CHECK',
  'VERZUIM_GEEN_TL_CONTACT',
  'VERZUIM_5_WERKDAGEN',
  'VERZUIM_FREQUENT',
  'VERZUIM_OPENSTAANDE_DIENST',
  'VERZUIM_VERVANGING_NODIG',
  // ── batch-40 uren-toeslag ──────────────────────────────────────────────────
  // FO-344 §11 — verdachte toeslagclassificatie op een klantrooster (≥3-ploegen
  // zonder nachtdienst of omgekeerd). Naar HR/SALARIS, zodat een verdachte
  // classificatie niet alleen een UI-melding is die weggeklikt wordt.
  'ROOSTER_CLASSIFICATIE_VERDACHT',
  // FO-353 §9 — automatische samenloop-beslissing ziekte/verlof of een
  // HR-override daarop. Naar de verzuim-doelgroep.
  'SAMENLOOP_ZIEKTE_VERLOF',
  // §5 — verlof
  'VERLOF_HOOG_PERCENTAGE_VRIJ',
  'VERLOF_KORT_VOORAF',
  'VERLOF_LANG_AANEENGESLOTEN',
  'VERLOF_SALDO_DREIGT_NEGATIEF',
  'VERLOF_ATV_VERVALT_BINNENKORT',
  'VERLOF_BOVENWETTELIJK_VERVALT',
  // §6 — uren
  'UREN_NIET_INGEVULD_HERINNERING_2',
  'UREN_TL_MELDING_NIET_INGEVULD',
  'WERKBON_WEEK_NIET_GEREED',
  'WERKBON_NIET_GEREEDGEMELD',
  // batch-63 werkbon-facturatie (FO-474) — dagelijkse controle: werkbonnen die
  // "niet te factureren" staan zonder reden (verwacht 0). Gebundeld per NL-dag
  // (bronEntiteitId = de dag), resolver `administratie`, FO-416-instelbaar.
  // ADD VALUE-migratie (20261231002300_b63_werkbonfact_enums), CC.2 los.
  'WERKBON_NIET_FACTURABEL_ZONDER_REDEN', // -> administratie (NOTIFICATIE, dagcontrole)
  // §8 — systeem
  'SYSTEEM_API_FAILURE',
  'SYSTEEM_SYNC_PROBLEEM',
  'SYSTEEM_VALIDATIE_FOUT',
  // ─── Ronde 4 W8 (spec ronde-4/08 §2.2) — capaciteits-signalen ─────────────
  // Gevuurd door de wekelijkse capacity-check cron via triggerNotificatieEvent
  // (NIET via W7 fireSignal — spec §2.2 schrijft het oudere event-pad voor).
  // W7's dashboard ziet ze via de Notificatie-tabel.
  'CAPACITEIT_OVERBELAST',
  'CAPACITEIT_BIJNA_VOL',
  'CAPACITEIT_ONDERBENUT',
  'CAPACITEIT_STRUCTUREEL_OVERWERK',
  'CAPACITEIT_KOSTEN_LEEGLOOP',
  // Notificatie-throttling (spec notificatie-throttling §3) — digest-templates (notify()-only).
  'TAAK_DAILY_DIGEST',
  'BULK_WIJZIGING_DIGEST',
  // DORMANT sinds de DagTaak-retirement — werd alleen door de verwijderde cron
  // `dagtaken-doorschuiven` gevuurd. Blijft als ongebruikte event-key (de Prisma
  // enum-waarde is niet veilig te droppen); geen producer meer.
  'DAGTAAK_VAAK_DOORGESCHOVEN',
  // Meldformulier onveilige situatie (Veiligheid-module) — monteur meldt vanaf
  // de portal een onveilige situatie; notify naar de opvolgers (TEAMLEIDER + HR)
  // via resolver `veiligheid.opvolgers`. Gate-key VEILIGHEID.
  'VEILIGHEID_ONVEILIGE_SITUATIE_GEMELD',
  // Werkbon wacht op interne goedkeuring (Deel 3) — vuurt zodra een monteur
  // zijn werkbon gereedmeldt (goedkeuringStatus → WACHT_OP_INTERN). Naar de
  // teamleider van de monteur via resolver `leave.teamleiders` (zelfde
  // two-step-routing als VERLOF_INGEDIEND). Krijgt een deadline mee zodat de
  // inbox-deadline-escalatie-cron de prioriteit kan opschalen bij uitstel.
  'WERKBON_WACHT_OP_INTERN',
  // ── Felicitaties + jubilea (B6 / C8 / C9) ─────────────────────────────────
  // Twee felicitatie-events naar de medewerker zelf (resolver
  // `verjaardag.medewerker`): verjaardag + dienstjaar. Beide zijn `mandatory`
  // op template-niveau zodat ze user-voorkeuren bypassen (een felicitatie wil
  // je niet kunnen mute'n voor je collega's). Drie HR/TL-vooraankondigingen
  // (resolver `jubileum.hr+teamleider`): aankomend/vandaag dienstjubileum en
  // verjaardag-vooraf, zodat HR/teamleider een attentie kan voorbereiden.
  'VERJAARDAG_FELICITATIE',
  'DIENSTJAAR_FELICITATIE',
  'DIENSTJUBILEUM_AANKOMEND',
  'DIENSTJUBILEUM_VANDAAG',
  'VERJAARDAG_VOORAF_HRTL',
  // ── Vervanging bij verlof (B6) ────────────────────────────────────────────
  // Twee vervangings-events (resolver `vervanging.opvolgers` → TEAMLEIDER van
  // de afwezige + PLANNER). bronEntiteitId = VervangingOpvolging.id.
  // INTERN_NODIG = taak (actie vereist), KLANT_REACTIE = informatief.
  'VERVANGING_INTERN_NODIG',
  'VERVANGING_KLANT_REACTIE',
  // FO-304-B (batch-33) — klant reageert (akkoord/bezwaar) op een
  // afwezigheidsmelding. Niet-bindend; informatief signaal naar PLANNER/TL.
  'KLANT_AFWEZIGHEID_REACTIE',
  // FO-304-C — klant-geschiktheids-feedback op een monteur (HR/PLANNER).
  'KLANT_MONTEUR_FEEDBACK',
  // FO-304-F — klant-reactie op een werkbon/factuur (behandelaar, Postvak In).
  'KLANT_DOCUMENT_REACTIE',
  // FO-308-P2a — klant-besluit op een offerte (eigenaar/verkoop).
  'KLANT_OFFERTE_BESLUIT',
  // FO-308-P2c — klant meldt een storing (storingsdienst/planner, hoge prio).
  'KLANT_STORING_MELDING',
  // ── Spec 30 Fase 8A — voorraad uniek-item cron-checks ────────────────────
  // Gevuurd door de dagelijkse voorraad-uniek-checks cron. bronEntiteitId =
  // VoorraadUniekItem.id. Resolver `voorraad.beheer` (HR + backoffice-toegang).
  // NIET_RETOUR / SLEUTEL_TE_LANG_UIT richten zich ook op de houder (via extras).
  'VOORRAAD_KEURING_VERLOOPT',
  'VOORRAAD_PBM_VERVANG',
  'VOORRAAD_NIET_RETOUR',
  'VOORRAAD_SLEUTEL_TE_LANG_UIT',
  'VOORRAAD_SIM_ZONDER_MEDEWERKER',
  'VOORRAAD_ITEM_VERMIST',
  // ── Ongevallen-/incidentenregistratie (Ongevallen-module §7) ───────────────
  // GEREGISTREERD → naar de ongeval-beheerders (resolver `ongevallen.beheerders`,
  // users met ONGEVALLEN.canApprove). ARBEIDSINSPECTIE_VEREIST → zelfde groep +
  // de invuller, gevuurd zodra de triage meldingsplichtig is (ziekenhuisopname /
  // blijvend letsel / overlijden); `mandatory: true` op template-niveau.
  'ONGEVAL_GEREGISTREERD',
  'ONGEVAL_ARBEIDSINSPECTIE_VEREIST',
  // Spec 33 fase 3 — week afgesloten met uren onder de norm: HR-actie-item voor
  // goedkeuring min-uren verzoek. bronEntiteitId = MinUrenVerzoek.id.
  // Resolver `all-hr` (HR beoordeelt). Categorie HR_SIGNALERING, actie vereist.
  'WEEKSTAAT_MINUREN_INGEDIEND',
  // ── Loonstroken-module (spec B, stap 4a) ──────────────────────────────────
  // KLAARGEZET → naar de medewerker zelf (resolver `employee.self`,
  // bronEntiteitId = employeeId) zodra een loonstrook/jaaropgave klaargezet is.
  // Persoonlijk + belangrijk; bevat NOOIT loonbedragen/BSN. UPLOAD_HERINNERING
  // → cron-driven (26e van de maand) naar de loonstrook-verantwoordelijke(n)
  // (resolver `loonstrook.verantwoordelijken`, fallback rol SALARIS).
  'LOONSTROOK_KLAARGEZET',
  'LOONSTROOK_UPLOAD_HERINNERING',
  // #28 Deel 4 — vraag/correctieverzoek van een medewerker bij zijn loonstrook;
  // naar de loonstrook-beheerders (resolver `loonstrook.beheerders`).
  'LOONSTROOK_VRAAG',
  // ── NHG werkgeversverklaring (lane O1) ─────────────────────────────────────
  // AANGEVRAAGD → naar HR (resolver `all-hr`); bronEntiteitId =
  // WerkgeversverklaringAanvraag.id. VERSTREKT → naar de medewerker (resolver
  // `actor.requester` + requesterUserId in extras); bronEntiteitId =
  // Werkgeversverklaring.id, deep-link naar portal-documenten.
  'WERKGEVERSVERKLARING_AANGEVRAAGD',
  'WERKGEVERSVERKLARING_VERSTREKT',
  // ── Operationele takenmodule (spec 34 §4 + §11) ───────────────────────────
  // Acht events voor de operationele-takenmodule. De escalatie-service
  // (`taak-escalatie.ts`) en de plan-/deadline-routes vuren deze; de escalatie
  // wist `uitvoerderId` op de taak, dus de uitvoerder-bound resolvers krijgen
  // de Employee-id via `resolverExtras.employeeId` van de caller (de taak kent
  // de uitvoerder op resolve-moment niet meer). Resolvers: taak.uitvoerder /
  // taak.teamleider / taak.uitvoerder-tl / taak.escalatie / taak.escalatie-aanmaker.
  'TAAK_TOEGEWEZEN',
  'TAAK_HOOG_GEPLAND',
  'TAAK_NIET_UITGEVOERD_HOOG',
  'TAAK_2X_NIET_UITGEVOERD',
  'TAAK_PRIO_VERHOOGD',
  'TAAK_DEADLINE_VANDAAG',
  'TAAK_DEADLINE_VERLOPEN',
  'TAAK_HERINNERING_EINDE_DAG',
  // batch-61 taken-filters-status (FO-447, FO-424-lijn): uitvoerder uit dienst,
  // toegewezen taak terug naar Open; resolver taak.escalatie-aanmaker.
  'TAAK_UITVOERDER_UIT_DIENST',
  // batch-62 taken-ux (FO-464): de aanvrager hoort dat zijn taak gereedgemeld is;
  // resolver taak.aanmaker.
  'TAAK_GEREED_GEMELD',
  // Spec 35 — Vertrouwenspersoon-module.
  'VP_VERVOLGSTAP_HERINNERING',
  'VP_ROL_GEWIJZIGD',
  // ── Batch-6 verlof: ouderschapsverlof + seniorendagen (Deel 7) ────────────
  // KIND_TOEGEVOEGD → HR (selfservice-verzoek of HR-invoer; bron = employeeId).
  // RECHT_BESCHIKBAAR → medewerker + HR bij kind-activatie (employee.self+hr).
  // BETAALD_VERLOOPT → medewerker + HR, X weken vóór de uiterste opnamedatum
  // (drempel instelbaar via VerlofBeleid.ouderschapBetaaldVerlooptSignaalWeken).
  // UWV_AANVRAAG_NODIG → HR bij goedkeuring betaald deel. SALDO_BIJNA_OP →
  // medewerker (drempel-systematiek). SENIORENDAGEN_* → batch/keuze/toekenning.
  'KIND_TOEGEVOEGD',
  'OUDERSCHAPSVERLOF_RECHT_BESCHIKBAAR',
  'OUDERSCHAPSVERLOF_BETAALD_VERLOOPT',
  'OUDERSCHAPSVERLOF_UWV_AANVRAAG_NODIG',
  'OUDERSCHAPSVERLOF_SALDO_BIJNA_OP',
  'SENIORENDAGEN_CONCEPT_KLAAR',
  'SENIORENDAGEN_KEUZE_NODIG',
  'SENIORENVERLOF_TOEGEKEND',
  'SENIORENDAGEN_BIJNA_GERECHTIGD',
  // Spec 42 — verlofverval 640a-waarschuwing + geblokkeerd-signaal.
  'VERLOF_VERVAL_WAARSCHUWING',
  'VERLOF_VERVAL_GEBLOKKEERD',
  // ── Batch-6 hrm — HRM gespreksagenda ───────────────────────────────────────
  // Resolver `hr-gesprek.betrokkenen` (deelnemers + gesprekspartner);
  // bronEntiteitId = HrGesprek.id. Geen HR/TL-fan-out: het gesprekstype kan
  // gevoelig zijn (verzuimgesprek); template-vars bevatten een al-geredigeerde
  // titel, nooit het rauwe type.
  'HR_GESPREK_GEPLAND',
  'HR_GESPREK_VERZET',
  'HR_GESPREK_GEANNULEERD',
  'HR_GESPREK_HERINNERING',
  // ── Batch-6 cert — certificaten-opvolgflow ──────────────────────────────────
  // bronEntiteitId = Employee.id. Statussen komen uit de afgeleide resolver
  // (bepaalTypeStatus); de cron meldt alléén over de leidende actieve versie.
  'CERTIFICAAT_WACHT_OP_UPLOAD',
  'CERTIFICAAT_NIET_OPGEPAKT',
  'CERTIFICAAT_TOEGEVOEGD',
  'GEPLAND_ZONDER_GELDIG_CERTIFICAAT',
  // ── FO-309 P4 (batch-34) — cursist-gerichte certificaat-upload-melding ──────
  // bronEntiteitId = OpleidingDeelname.id. GEVRAAGD bij afronden, HERINNERING na
  // 14 dagen zonder upload; beide stoppen zodra het certificaat er is.
  'CERTIFICAAT_UPLOAD_GEVRAAGD',
  'CERTIFICAAT_UPLOAD_HERINNERING',
  // ── batch-54 certificaten (FO-408 §7) — zelf-aangeleverde certificaten ─────
  // CERTIFICAAT_CONTROLE_GEVRAAGD: GEBUNDELD HR-signaal ("{n} certificaten
  //   wachten op controle"), bronEntiteitId = Employee.id, doorklik naar de
  //   te-controleren-lijst. Nooit een mail per medewerker (FO-350 + FO-409 §C).
  // CERTIFICAAT_CONCEPT_BEVESTIGD/_AFGEWEZEN: de uitkomst naar de MEDEWERKER
  //   zelf (persoonsgebonden, FO-409 §B2), bronEntiteitId = EmployeeCertificate.id.
  'CERTIFICAAT_CONTROLE_GEVRAAGD',
  'CERTIFICAAT_CONCEPT_BEVESTIGD',
  'CERTIFICAAT_CONCEPT_AFGEWEZEN',
  // ── Batch-7 recruitment — check-ins + recruitment-reminders ────────────────
  // ACQUISITIE_CHECKIN_VANDAAG: bronEntiteitId = LeadCheckIn.id.
  // RECRUITMENT_AANBOD_*: bronEntiteitId = Kandidaat.id (resolver leest de
  // recruiter van de kandidaat); overige RECRUITMENT_* idem.
  'ACQUISITIE_CHECKIN_VANDAAG',
  'RECRUITMENT_NIEUW_ONOPGEVOLGD',
  'RECRUITMENT_NABELLEN_VANDAAG',
  'RECRUITMENT_GESPREK_MORGEN',
  'RECRUITMENT_AANBOD_VERLOOPT',
  'RECRUITMENT_AANBOD_GEEN_REACTIE',
  'RECRUITMENT_TALENTPOOL_HERBEOORDELING',
  'RECRUITMENT_AFWIJZING_NIET_AFGEROND',
  // FO-517 (spec 217) — beoordelingsworkflow.
  'RECRUITMENT_BEOORDELING_HERINNERING',
  'RECRUITMENT_BEOORDELING_ESCALATIE',
  'RECRUITMENT_SECOND_REVIEW_GEVRAAGD',
  'RECRUITMENT_ADVIES_AFWIJKING',
  'RECRUITMENT_INFO_VERZOEK',
  'RECRUITMENT_BESLUIT_GEVRAAGD',
  // Batch-8 rapportage — reistijd-afwijking-week-signaal naar de teamleider.
  'REISTIJD_AFWIJKING_SIGNAAL',
  // Batch-10 portal-stream #24 — idee/verbetervoorstel ingediend (ideeenbox).
  'IDEE_INGEDIEND',
  // FO-003 (batch-12) — onboarding-startworkflow deadline-signalen (verplicht
  // certificaat nadert/overschrijdt zijn relatieve deadline). Alleen signaleren.
  'ONBOARDING_CERT_DEADLINE_NADERT',
  'ONBOARDING_CERT_DEADLINE_VERLOPEN',
  // FO-016 (batch-14) — ideeenbox. IDEE_INGEDIEND (nieuwe inzending -> behandelaar)
  // bestaat al. Append-only (registry-eigenaar = signaleringen-stream):
  //  - IDEE_STATUS_GEWIJZIGD    -> de indiener (resolver `idee.indiener`), bij elke
  //    statuswijziging met terugkoppeling.
  //  - IDEE_REACTIE_TER_MODERATIE -> de teamleider van de reagerende medewerker
  //    (resolver `idee.reactie-teamleider`), ACTIE-item in het postvak.
  //  - IDEE_VOORSTEL_GEPUBLICEERD -> de organisatie (resolver `idee.organisatie`),
  //    stil in-app zodra een idee als openbaar voorstel is gepubliceerd.
  'IDEE_STATUS_GEWIJZIGD',
  'IDEE_REACTIE_TER_MODERATIE',
  'IDEE_VOORSTEL_GEPUBLICEERD',
  // FO-015 (batch-14) — periodieke inspectie/controlelijst nadert (NADERT) of
  // overschrijdt (VERLOPEN) zijn vervaldatum. Cron-driven; resolver veiligheid.opvolgers.
  'INSPECTIE_VERVALDATUM_NADERT',
  'INSPECTIE_VERVALDATUM_VERLOPEN',
  // FO-035 (batch-16) — HR-gespreksverslag: te maken (gespreksvoerder bij
  // plannen), klaargezet (medewerker bij vrijgave), reactie/bezwaar (HR).
  'GESPREKSVERSLAG_TE_MAKEN',
  'GESPREKSVERSLAG_KLAARGEZET',
  'GESPREKSVERSLAG_REACTIE',
  // ── FO-309 P3 (batch-34) — verslag-termijn + afspraak-deadline-signalen ─────
  // TERMIJN_HERINNERING/ESCALATIE: bronEntiteitId = HrGesprek.id (deeplink editor).
  // MEDEWERKER_HERINNERING: bronEntiteitId = Gespreksverslag.id (portal).
  // AFSPRAAK_DEADLINE: bronEntiteitId = Gespreksverslag.id (actiehouder).
  'GESPREKSVERSLAG_TERMIJN_HERINNERING',
  'GESPREKSVERSLAG_TERMIJN_ESCALATIE',
  'GESPREKSVERSLAG_MEDEWERKER_HERINNERING',
  'GESPREKSVERSLAG_AFSPRAAK_DEADLINE',
  // ── batch-39 gesprekken (FO-332) — actiepunt-/vervolg-/stilzwijgend-akkoord ──
  //  ACTIEPUNT_TOEGEWEZEN: bronEntiteitId = Gespreksverslag.id (actiehouder,
  //    bij definitief). VERVOLG_PLANNEN: plan-taak "te plannen door {naam}".
  //  ACTIEPUNT_ESCALATIE: actiehouder uit dienst → gespreksvoerder.
  //  STILZWIJGEND_AANGEKONDIGD (dag 5) / _VASTGESTELD (dag 7): beide partijen.
  'GESPREKSVERSLAG_ACTIEPUNT_TOEGEWEZEN',
  'GESPREKSVERSLAG_VERVOLG_PLANNEN',
  'GESPREKSVERSLAG_ACTIEPUNT_ESCALATIE',
  'GESPREKSVERSLAG_STILZWIJGEND_AANGEKONDIGD',
  'GESPREKSVERSLAG_STILZWIJGEND_VASTGESTELD',
  // ── batch-47 gespreksverslagen (spec 90 §10) — event-gedreven statusverloop ──
  //  TER_CONTROLE / _TERUG: bronEntiteitId = HrGesprek.id (deeplink editor).
  //  ONDERTEKENING_VERZOEK / _GEWEIGERD: extra verplichte ondertekenaars resp.
  //    HR + opsteller bij een weigering (ACTIE, HR bepaalt de opvolging).
  //  VOLLEDIG_ONDERTEKEND / GELEZEN / ADDENDUM_TOEGEVOEGD: informatief (bel).
  //  TERMIJN_TL_ESCALATIE (dag 7) / VERLOPEN (dag 14): werkvoorraad.
  'GESPREKSVERSLAG_TER_CONTROLE',
  'GESPREKSVERSLAG_TER_CONTROLE_TERUG',
  'GESPREKSVERSLAG_ONDERTEKENING_VERZOEK',
  'GESPREKSVERSLAG_ONDERTEKENING_GEWEIGERD',
  'GESPREKSVERSLAG_VOLLEDIG_ONDERTEKEND',
  'GESPREKSVERSLAG_GELEZEN',
  'GESPREKSVERSLAG_TERMIJN_TL_ESCALATIE',
  'GESPREKSVERSLAG_VERLOPEN',
  'GESPREKSVERSLAG_ADDENDUM_TOEGEVOEGD',
  // FO-058 (batch-18) — onderhoudsschema-controle + geautomatiseerde placeholder-mails.
  //  - ONDERHOUD_CONTROLE_AFWIJKING -> planner (rol PLANNER) bij een niet/onvolledig/
  //    onjuist geplande onderhoudsweek, met deep-link naar de dekking van die klant.
  //  - MAIL_AUTOMATISERING_CONCEPT     -> beheerder: concept-mail klaar ter controle.
  //  - MAIL_AUTOMATISERING_GEBLOKKEERD -> beheerder: verzending geblokkeerd doordat
  //    verplichte placeholder-data (geplande monteurs) ontbreekt.
  'ONDERHOUD_CONTROLE_AFWIJKING',
  'MAIL_AUTOMATISERING_CONCEPT',
  'MAIL_AUTOMATISERING_GEBLOKKEERD',
  // FO-311 (batch-34) — in-app/push-notice naar een gekozen interne ontvanger dat
  // de inhoud van een mail-automatisering (voor die ontvanger) is bezorgd.
  'MAIL_AUTOMATISERING_VERSTUURD',
  // FO-059 (batch-18) — retourcheck bij uitdienst: alle gekoppelde middelen van
  // de uitdienst-medewerker innemen (voertuig/tankpas/apparatuur/gereedschap/
  // uitrusting). Deep-link naar de Middelen-tab op het medewerker-dossier.
  'MIDDELEN_RETOURCHECK',
  // FO-093 (batch-21) — commercieel contract verloopt (bijna): T-90/T-60/T-30/T-0
  // signaal naar de eindverantwoordelijke, deep-link naar de relatie-contracten-tab.
  'CONTRACT_VERLOOP',
  // Achtergrond-imports — vuurt naar de gebruiker die de import startte zodra een
  // achtergrond-import (factuur/brandstof/relaties) is afgerond (VOLTOOID of MISLUKT).
  // Recipient wordt door de runner direct als `{ userId }` meegegeven aan notify();
  // de seed-recipientResolver is inert en dient puur als DB-placeholder.
  'IMPORT_AFGEROND',
  // FO-128 (batch-24) — materieel-abonnement nadert opzegtermijn of verloopt;
  // signaal naar de ASSETS-beheerders (administratie/HR). Cron geeft de recipient
  // direct als `{ userId }` mee + expliciete kanalen, dus geen seed-resolver nodig.
  'ABONNEMENT_VERLOOP',
  // FO-144 (batch-28) RI&E — plan-van-aanpak-signalering + herzieningssignaal.
  //  - RIE_MAATREGEL_DEADLINE_NADERT/-VERLOPEN → de actiehouder van de maatregel
  //    (resolver `werkbon.assigned-employee`, cron geeft assignedEmployeeUserId mee).
  //  - RIE_HERZIENING_NADERT → HR/preventie (resolver `veiligheid.opvolgers`),
  //    wanneer `volgendeHerziening` nadert of verstreken is.
  'RIE_MAATREGEL_DEADLINE_NADERT',
  'RIE_MAATREGEL_DEADLINE_VERLOPEN',
  'RIE_HERZIENING_NADERT',
  // FO-146 (batch-28, stream mail-melding) — proactieve melding aan de afzender
  // (OutgoingEmail.createdById, of ADMIN-fallback bij systeem-/cron-mail) zodra
  // een mail definitief niet bezorgd is: FAILED na uitgeputte retries, BOUNCED
  // (bounce-scan) of DROPPED_ALLOWLIST (env-gestuurd, default alleen productie).
  // Resolver `mail.afzender-of-admin`; bronEntiteitId = OutgoingEmail.id;
  // deeplink naar /mail/uitbox?id={ID} (opnieuw versturen in één klik).
  'MAIL_NIET_BEZORGD',
  // FO-314 (batch-34) — abonnementen-completering. Beide naar de ASSETS-beheerders;
  // cron/route geven de recipient direct als `{ userId }` mee + expliciete kanalen,
  // dus geen seed-resolver nodig (mirror van ABONNEMENT_VERLOOP).
  //  - ABONNEMENT_INDEXERING_SAMENVATTING → jaarlijkse samenvatting geïndexeerde abonnementen.
  //  - ABONNEMENT_FACTUUR_AFWIJKING → gefactureerd bedrag wijkt af van het afgesproken maandbedrag.
  'ABONNEMENT_INDEXERING_SAMENVATTING',
  'ABONNEMENT_FACTUUR_AFWIJKING',
  // ── FO-307-A2 (batch-33, stream portal-audit) — consignatie-ruilverzoek ─────
  // Drie partijen: aanvrager (monteur), voorgestelde collega en planner.
  // bronEntiteitId = ConsignatieRuilVerzoek.id; deeplink naar het portal-blok.
  'CONSIGNATIE_RUIL_AANGEVRAAGD', // -> collega + planner
  'CONSIGNATIE_RUIL_COLLEGA_GEREAGEERD', // -> aanvrager + planner
  'CONSIGNATIE_RUIL_BESLIST', // -> aanvrager + collega (push aan)
  // ── FO-321 (batch-36, stream catalogus) — gereedschap-catalogus-scheefstand ──
  // Dagelijkse lichte cron bundelt template-regels zonder catalogus-item +
  // catalogus-items met gedeactiveerde voorraad-bron in één signaal naar magazijn
  // (resolver voorraad.beheer). Geen automatische mutaties; de wizard is de fix.
  'CATALOGUS_SCHEEFSTAND',
  // ── FO-322 (batch-36) — AVG-toestemmingen ──────────────────────────────────
  // bronEntiteitId = employeeId (resolver employee.self). Tekstwijziging => het
  // type kreeg een nieuwe versie; herinnering na 14 dagen openstaand.
  'TOESTEMMING_OPNIEUW_BEANTWOORDEN', // -> betrokken medewerker (nieuwe tekstversie)
  'TOESTEMMING_HERINNERING', // -> medewerker met openstaande verplichte toestemming (14d)
  // ── FO-323 (batch-36) — NEN 3140-aanwijzingen ──────────────────────────────
  // bronEntiteitId = Nen3140Aanwijzing.id. Verloop-drempels 90/60/30/0 +
  // certificaat-steunt-signaal + skill-voorstel + planning-override.
  'NEN3140_AANWIJZING_VERLOOPT', // -> medewerker + HR (drempel 90/60/30)
  'NEN3140_AANWIJZING_VERLOPEN', // -> medewerker + HR (drempel 0)
  'NEN3140_CERTIFICAAT_STEUNT_VERLOOPT', // -> HR (onderliggend certificaat verloopt eerder)
  'NEN3140_SKILL_VOORSTEL', // -> HR/TL (aanwijzing actief -> skill bijwerken?)
  'NEN3140_TEKENEN_VERZOCHT', // -> medewerker (jouw aanwijzing wacht op je handtekening)
  // batch-58 nen3140-v2 (FO-431) — laag 3 (klantspecifieke autorisatie) + de
  // taak-suggestie na een bevestigd certificaat (nooit een auto-aanwijzing).
  'NEN3140_KLANTAUTORISATIE_VERLOOPT',
  'NEN3140_KLANTAUTORISATIE_VERLOPEN',
  'NEN3140_KLANTAUTORISATIE_INGETROKKEN',
  'NEN3140_BEOORDEEL_VOOR_AANWIJZING',
  'NEN3140_PLANNING_OVERRULE', // -> HR (aanwijzing-vereiste ge-overruled bij plannen)
  // ── batch-37 onderhoudsrapport (FO-327) — telematica-onderhoudsignalen ─────
  // bronEntiteitId = Voertuig.id (behalve INGEST_FOUT/STILTE: OnderhoudRapport-
  // MailIngest.id resp. Voertuig-loos). Drempels in de wagenpark-instellingen.
  'VOERTUIG_ONDERHOUD_BINNENKORT', // -> wagenparkbeheer (binnen X dagen of Y km)
  'VOERTUIG_ONDERHOUD_ACHTERSTALLIG', // -> wagenparkbeheer, hoge prio (negatieve km / onderhoud nu)
  'VOERTUIG_ONDERHOUD_RAPPORT_STILTE', // -> wagenparkbeheer (geen rapport sinds N dagen)
  'VOERTUIG_ONDERHOUD_INGEST_FOUT', // -> wagenparkbeheer (kapotte/ontbrekende PDF)
  // ── FO-326 (batch-37 schadeformulier) — Europees aanrijdingsformulier ───────
  // bronEntiteitId = SchadeMelding.id. Nieuw-signaal naar wagenparkbeheer
  // (hoge prioriteit bij gewonden via de aparte template-variant), blad-2-
  // herinnering + status-updates naar de bestuurder, en het gebundelde
  // "gegevens ontbraken bij schademelding"-beheersignaal (§2.0).
  'SCHADEMELDING_NIEUW', // -> wagenparkbeheer (VOERTUIGEN.canEdit)
  'SCHADEMELDING_BLAD2_HERINNERING', // -> bestuurder (blad 2 na X dagen nog leeg)
  'SCHADEMELDING_STATUS_UPDATE', // -> bestuurder (verzonden/in behandeling/afgehandeld)
  'SCHADEMELDING_GEGEVENS_ONTBRAKEN', // -> wagenparkbeheer (voorinvul-bronnen aanvullen)
  // batch-38 garagefacturen (FO-329) — werkvoorraad-signaal naar administratie:
  // een garagefactuur kon niet automatisch aan een voertuig gekoppeld worden.
  'GARAGE_ONDERHOUD_WERKVOORRAAD', // -> administratie (FACTUREN.canView)
  // batch-39 onboarding (FO-337) — procesconfiguratiefout: een actieve
  // onboarding-stap is niet invulbaar in de wizard (stap zonder template-
  // koppeling); blokkeert het indienen tot HR de configuratie herstelt.
  'ONBOARDING_CONFIGURATIEFOUT', // -> all-hr
  // batch-40 status (FO-340 §13.4) — medewerkerstatus in twee lagen.
  // LET OP: deze events gaan naar INTERNE rollen en noemen nooit een
  // ziektereden; werkstatus is verzuim-adjacent en verlaat het bedrijf niet.
  'MEDEWERKER_NON_ACTIEF_GEZET', // -> teamleider + planner (HR stelt non-actief)
  'MEDEWERKER_NON_ACTIEF_VERLOPEN', // -> HR + teamleider (venster verstreken, weer inzetbaar)
  'MEDEWERKER_STATUS_DRIFT', // -> HR (reconciliatie vond een inconsistentie)
  'DIENSTVERBAND_VENSTER_AFWIJKING', // -> HR (endDate verstreken maar nog IN_DIENST, of andersom)
  // ── batch-40 meldingen ────────────────────────────────────────────────────
  // FO-345 — vangnet-signaal: de verzuim-doelgroep is ná alle fallbacks leeg,
  // dus een ziek-/betermelding zou niemand bereiken. Zonder dit signaal valt
  // dat stil weg met alleen een log-warn — precies hoe deze meldingen ongemerkt
  // stopten.
  'VERZUIM_DOELGROEP_LEEG', // -> ADMIN
  // FO-354 — loonstroken staan klaar voor beoordeling + vrijgave-planning.
  // Gebundeld per import-batch/upload-sessie; nooit bedragen of BSN in de
  // payload (alleen aantallen, periode en koppelstatus).
  'LOONSTROKEN_TE_BEOORDELEN', // -> loonstrook.verantwoordelijken
  // FO-354 §7.4 — vangnet naar hetzelfde model als VERZUIM_DOELGROEP_LEEG: geen
  // loonstrook-verantwoordelijken én geen SALARIS-gebruikers, dus de
  // beoordeel-melding zou niemand bereiken.
  'LOONSTROKEN_DOELGROEP_LEEG', // -> ADMIN
  // Fase F (spec 44) — mail-ingest malware-scan-gate: een Defender-verdict
  // blokkeerde een bijlage; alleen ADMINs kunnen beoordelen/vrijgeven. Geen
  // PII (bestandsnaam/afzender) in de melding — die staan achter de ADMIN-tab.
  'MALWARE_BESTAND_GEVONDEN', // -> ADMIN
  // ── batch-41 dossier ──────────────────────────────────────────────────────
  // FO-355 — dossier-volledigheid: gebundelde melding per medewerker (alleen
  // labels van wat ontbreekt, nooit waarden), wekelijkse herinnering met
  // teller en 30-dagen-escalatie. FO-358 — handmatig informatie-opvragen door
  // HR: verzoek + herinnering (max 2) + onbeantwoord-signaal en
  // afrondings-bevestiging naar de aanvrager.
  'DOSSIER_ONVOLLEDIG', // -> medewerker (zelf-aanleverbaar) + HR
  'DOSSIER_ONVOLLEDIG_HERINNERING', // -> idem, wekelijks met teller
  'DOSSIER_NIET_AANGEVULD', // -> all-hr (escalatie na drempel)
  'INFORMATIE_OPGEVRAAGD', // -> medewerker (in-app + push + mail)
  'INFORMATIE_VERZOEK_HERINNERING', // -> medewerker (7 dagen stil, max 2)
  'INFORMATIE_VERZOEK_ONBEANTWOORD', // -> aanvrager (ACTIE-item)
  'INFORMATIE_VERZOEK_AFGEROND', // -> aanvrager (bevestiging)
  // ── batch-42 onboarding (FO-369) — onboarding blijft openstaan ─────────────
  // ACTIE-items voor HR, gemaakt via createInboxItem (niet de orchestrator):
  // reconciliatie sluit ze zodra de blokkade weg is. Curatie conform FO-350.
  'ONBOARDING_GEBLOKKEERD', // -> HR (deferred contract / aanvullen vereist, direct)
  'ONBOARDING_VOORBIJ_START', // -> HR (gebundelde wekelijkse nudge, startdatum verstreken)
  // ── batch-42 leverwijze (FO-373) ──────────────────────────────────────────
  // Een goedgekeurde uitrusting-bestelling levert (deels) uit voorraad: het
  // magazijn krijgt een picklijst (welke artikelen voor wie klaarleggen).
  // Doelgroep = de bestaande voorraad-beheer-groep (resolver `voorraad.beheer`).
  'UITRUSTING_PICKLIJST', // -> voorraad.beheer + uitrusting.verwerkers (magazijn legt klaar)
  // ── Verwerk-stap uitrustingbestellingen (2026-08) ──────────────────────────
  // Goedkeuren en het versturen van de leveranciersmail zijn losgekoppeld. Eén
  // ACTIE-event voor de verwerkers, vier informatieve events voor de medewerker
  // zodat hij de bestelling in de portal kan volgen zonder te hoeven bellen.
  'UITRUSTING_BESTELLING_TE_VERWERKEN', // -> uitrusting.verwerkers (ACTIE)
  'UITRUSTING_BESTELLING_GOEDGEKEURD', // -> medewerker (mail + push)
  'UITRUSTING_BESTELLING_AFGEKEURD', // -> medewerker (mail + push, met notitie)
  'UITRUSTING_BESTELLING_VERWERKT', // -> medewerker (wat uit magazijn, wat extern)
  'UITRUSTING_BESTELLING_GELEVERD', // -> medewerker (ook per deellevering)
  'UITRUSTING_BESTELLING_GEANNULEERD', // -> medewerker (na goedkeuring ingetrokken)
  // ── batch-46 verzuim (FO-386 + FO-387) ─────────────────────────────────────
  // POORTWACHTER_STAP_DEADLINE: Wet Poortwachter-termijn nadert/verstreken op een
  //   gematerialiseerde re-integratiestap (T-14/T-7/T-0) -> verzuim.contactpersonen
  //   (ACTIE; T-0-wettelijk = mandatory HOOG). Nooit medische inhoud.
  // FREQUENT_VERZUIM_GESIGNALEERD: medewerker boven de frequentie-drempel ->
  //   verzuim.contactpersonen + teamleider (ACTIE). Payload alleen aantal/
  //   periodes/verzuim%.
  'POORTWACHTER_STAP_DEADLINE',
  'FREQUENT_VERZUIM_GESIGNALEERD',
  // ── batch-46 indexatie (FO-391) ────────────────────────────────────────────
  // Jaarlijkse herinnering (1 dec) naar de gate-houders (FACTUREN.canApprove)
  // zodra er ACTIEVE contracten met indexeringPct zijn zonder toegepaste run met
  // peildatum in het komende jaar. ACTIE-item, idempotent per jaar.
  'INDEXATIE_HERINNERING_NODIG', // -> facturen.approvers (ACTIE)
  // ── batch-46 inkoop (FO-392) — inkoopbudget-tellers ───────────────────────
  // Uitputting van een inkoopbudget (obligo + gerealiseerd t.o.v. bedrag) en
  // reconciliatie-drift. 80% informatief (STIL), 100% ACTIE naar de budgethouder,
  // DRIFT naar de administratie-rol.
  'INKOOP_BUDGET_80', // -> budgethouder (informatief, 80% bereikt)
  'INKOOP_BUDGET_OVERSCHREDEN', // -> budgethouder (ACTIE, 100% bereikt)
  'INKOOP_BUDGET_DRIFT', // -> administratie (teller week scheef, gecorrigeerd)
  // ── batch-46 inkoop (FO-393) — 3-way-match ────────────────────────────────
  'INKOOP_MATCH_AFWIJKING', // -> inkoop-behandelaar (ACTIE, controleer voor betaling)
  'INKOOP_ONTVANGST_ZONDER_FACTUUR', // -> inkoop-behandelaar (gebundeld dagsignaal)
  // ── batch-59 interne-facturen (FO-432) - interne (intercompany) inkoopstroom ─
  // De dagelijkse patroon-analyse (SQL-aggregatie per BV-paar, geen AI) vindt een
  // terugkerende interne factuur en stelt een herhaal-regel voor: ja / nee /
  // niet meer vragen. GEPAUZEERD volgt wanneer twee periodes achtereen niet
  // bevestigd zijn (geen stapel spookconcepten). Beide ACTIE, FO-416-instelbaar.
  'INTERNE_FACTUUR_PATROON_VOORSTEL', // -> administratie (ACTIE, ja/nee/niet meer vragen)
  'INTERNE_FACTUUR_HERHAALREGEL_GEPAUZEERD', // -> administratie (ACTIE, 2 periodes onbevestigd)
  // ── batch-63 facturen-bronregel (FO-471) - reeksbewaking ────────────────
  // Nieuwe OPEN afwijking in een verkoopfactuurreeks (BV-code-drift, afgeleide
  // reeks, dubbel volgnummer, afwijkend nummer, sprong). ACTIE per afwijking
  // (bronModule 'factuurreeks'); BV-code-drift wordt dagelijks herhaald zolang
  // hij open staat. FO-416-instelbaar.
  'FACTUURREEKS_AFWIJKING', // -> administratie (ACTIE, sluit zodra verklaard/opgelost)
  // FO-388 (batch-46) — contract-verleng-traject. AANZEGTERMIJN_KRITIEK = harde
  // T-30-escalatie naar HR + management (mandatory ACTIE, dagelijks tot opgelost);
  // EINDE_OFFBOARDING = ACTIE naar HR bij een niet-verleng-besluit (deeplink naar
  // de uitdienst-flow).
  'CONTRACT_AANZEGTERMIJN_KRITIEK', // -> hr+manager (ACTIE)
  'CONTRACT_EINDE_OFFBOARDING', // -> all-hr (ACTIE)
  // ── FO-396 (batch-46) — offboarding-workflow (uitdienst) ───────────────────
  // Deadline-/samenloop-signalen van de uitdienst-workflow. Alle ACTIE-items
  // dragen bronModule 'offboarding-workflow' + bronEntiteitId = employee-id (zodat
  // de {ID}-deeplink naar de Offboarding-tab wijst), en de reconciliatie-resolver
  // sluit ze zodra er geen LOPENDE workflow meer is (AFGEROND/GEANNULEERD).
  // Idempotent per (workflow × fase × NL-dag) voor de deadline-nudges; samenloop
  // één keer per (workflow × type).
  'OFFBOARDING_STAP_DEADLINE_NADERT', // -> all-hr (ACTIE, einddatum nadert, open verplichte stappen)
  'OFFBOARDING_STAP_DEADLINE_VERLOPEN', // -> all-hr (ACTIE, einddatum verstreken, open verplichte stappen)
  'OFFBOARDING_VERLOF_NA_EINDDATUM', // -> all-hr (ACTIE, goedgekeurd verlof ná einddatum - voorstel intrekken)
  'OFFBOARDING_PLANNING_NA_EINDDATUM', // -> planner (ACTIE, geplande werkbon-dagen ná einddatum)
  'OFFBOARDING_ZIEKTE_BIJ_UITDIENST', // -> all-hr (ACTIE, open verzuim loopt door bij uitdienst)
  'OFFBOARDING_GRAPH_BLOKKADE_MISLUKT', // -> all-hr (ACTIE, automatische Entra-blokkade faalde - handmatig blokkeren)
  'OFFBOARDING_DOCUMENT_VERSTREKT', // -> medewerker (in-app, getuigschrift/eindebrief nu beschikbaar in de portal)
  // batch-48 offboarding-cockpit (spec 94 §7) — het GEBUNDELDE HR-item per
  // offboarding: "offboarding {naam}: n stappen open, einddatum over X dagen",
  // met doorklik naar de cockpit. Bewust NOTIFICATIE (geen ACTIE): de losse
  // ACTIE-items (deadline nadert/verstreken, samenloop) blijven het werk sturen;
  // dit is het overzicht dat HR in één regel vertelt hoe een offboarding ervoor
  // staat (FO-350-curatie: niet nóg een af te vinken item erbij).
  'OFFBOARDING_OPEN_STAPPEN_OVERZICHT', // -> all-hr (NOTIFICATIE, stand van zaken per offboarding)
  // ── batch-46 materieel-rdw (FO-394) — voertuig-vervalbewaking ──────────────
  // Eén generieke verval-signaalreeks (T-60/30/14/0) op APK / verzekering /
  // lease: naar wagenparkbeheer (resolver `intake.team`). Type NOTIFICATIE +
  // postvak-config ITEM (afsluitbare werkvoorraad), zodat het per (event, inbox,
  // NL-dag) BUNDELT - net als VOERTUIG_KEURING_VERLOPEN (bundeling werkt alleen op
  // NOTIFICATIE-items, FO-350 §7). De APK-reeks stuurt daarnaast een portal-
  // notificatie aan de vaste bestuurder (MATERIEEL_APK_BINNENKORT). Vervangt de
  // losse materieel-apk-reminder + check-expiries-APK-tak (geen dubbel signaal).
  'VOERTUIG_APK_VERVAL', // -> wagenparkbeheer (APK verloopt/verlopen)
  'VOERTUIG_VERZEKERING_VERVAL', // -> wagenparkbeheer (verzekering verloopt/verlopen)
  'VOERTUIG_LEASE_EINDE', // -> wagenparkbeheer (leasecontract loopt af)
  // RDW kende het kenteken eerder wél maar nu niet meer (geëxporteerd/gestolen/
  // uitgeschreven): per-voertuig ACTIE naar wagenparkbeheer om te onderzoeken.
  'VOERTUIG_RDW_UITGESCHREVEN', // -> wagenparkbeheer (niet meer geregistreerd bij RDW)
  // ── batch-46 relaties-kvk (FO-395) ────────────────────────────────────────
  // Hoge-prio ACTIE naar administratie zodra de KVK-mutatiescan een
  // faillissement/surseance/opheffing detecteert (acuut debiteurenrisico).
  // Doelgroep = `administratie`; ACTIE-item (afhandelbaar).
  'RELATIE_KVK_STATUS', // -> administratie (debiteurenrisico beoordelen)
  // ── batch-46 adres-reis (FO-390) — adreswijziging triggert reis-herberekening
  // ADD VALUE-migratie (notificatie_event_type_key). Info = ter kennisname naar
  // HR (all-hr); de ACTIE gaat naar de reisafwijking-beoordelaars.
  'ADRES_REISIMPACT_INFO', // -> all-hr (kennisname: oud->nieuw km/reistijd + indicatieve kosten)
  'ADRES_REISCORRECTIE_HERBEOORDELEN', // -> reisafwijking.beoordelaars (ACTIE: herbeoordeel structurele correcties)
  // ── batch-49 in-te-plannen (FO-398 §8) ────────────────────────────────────
  // Instroom in het vak "In te plannen" (uitdienst/ziekte/verlof) + de
  // urgentie-escalatie (< 48 uur tot uitvoering, of te lang open). Beide naar de
  // planners, beide NOTIFICATIE + GEBUNDELD per (event, inbox, NL-dag) conform
  // de FO-350-curatie: het planbord-vak IS de werkvoorraad, het postvak zet er
  // geen n losse af te vinken items naast.
  'DIENST_IN_TE_PLANNEN', // -> planning.planners (diensten zonder uitvoerder)
  'DIENST_IN_TE_PLANNEN_URGENT', // -> planning.planners (< 48 uur / te lang open)
  // batch-60 vaste-monteur (FO-440 §B.5 + §6.4) — dagelijkse escalatie: een
  // dienst van een vaste monteur die binnen de drempel nog te plannen is, of een
  // vaste monteur die gepland staat terwijl hij afwezig is (conflict). Naar de
  // planners, NOTIFICATIE + GEBUNDELD per (event, inbox, NL-dag).
  'VASTE_MONTEUR_VERVANGING_ESCALATIE', // -> planning.planners (vervanging binnen drempel / conflict)
  // ── batch-50 ai-tegoed (FO-402 §7) ────────────────────────────────────────
  // De centrale AI-pauze gaat in of wordt opgeheven. Statuswaardig, niet per
  // factuur (FO-350): één melding voor de hele storing, naar de beheerders.
  // Naast de melding gaat er een aparte hoge-prioriteitsmail naar het
  // ingestelde alarm-adres. ADD VALUE-migratie
  // (20261201000100_b50_ai_tegoed_event_keys), CC.2 los.
  'AI_TEGOED_OP', // -> ADMIN (AI-verwerking gepauzeerd, tegoed aanvullen)
  'AI_TEGOED_HERSTELD', // -> ADMIN (AI-verwerking hervat, achterstand wordt ingehaald)

  // batch-50 reistijd-geocode (FO-403 §7)
  'REISTIJD_CACHE_GESANEERD', // -> planning.planners (verantwoording na de bulk-run)

  // batch-51 finqle-api (FO-406 §3.5 + §11.5). ADD VALUE-migratie
  // (20261210000300_b51_finqle_notificatie_event_keys), CC.2 los.
  'FINQLE_VERZOEK_WACHT_OP_AKKOORD', // -> administratie (GEBUNDELD, NOTIFICATIE)
  'FINQLE_VERZOEK_AFGEKEURD', // -> administratie (ACTIE, per verzoek)
  'FINQLE_INTEGRATIE_STORING', // -> ADMIN (ACTIE, sync/auth ligt stil)
  'FINQLE_INTEGRATIE_HERSTELD', // -> ADMIN (informatief, sync loopt weer)

  // ── batch-53 acquisitie-vervolg (FO-407 §C) ───────────────────────────────
  // De DAGBUNDEL met geplande vervolgcontacten, één melding per lead-eigenaar
  // ("Vandaag {n} vervolgcontacten (+{m} verlopen)"). De cron aggregeert zelf
  // per eigenaar, dus dit event vuurt bewust nooit per lead: 30 leads = 1
  // melding, geen 30 (FO-350-curatie). NOTIFICATIE-type, dus default STIL: de
  // melding staat in de bel, het kopje "Vervolgcontacten" IS de werkvoorraad.
  // ADD VALUE-migratie (20261208000100_b53_acquisitie_vervolg_event_key), CC.2 los.
  'ACQUISITIE_VERVOLGCONTACTEN', // -> acquisitie.vervolg-eigenaar (dagbundel)
  // ── batch-54 klant-akkoord-mail (FO-412 §7) ────────────────────────────────
  // De werkvoorraad rond automatisch verwerkte klant-replies op de goedkeurings-
  // mail. Beide GEBUNDELD (FO-350-curatie): één item met het aantal, doorklik
  // naar /facturen/akkoord-mails - nooit een melding per mail. Doelgroep
  // `facturen.approvers`. Een automatische AFKEURING krijgt hier bewust geen
  // eigen event: die loopt via de BESTAANDE afkeur-werkvoorraad, exact als een
  // portaal-afkeur. ADD VALUE-migratie
  // (20261212000200_b54_klant_akkoord_mail_event_keys), CC.2 los.
  'KLANT_AKKOORD_MAIL_TE_BEOORDELEN', // -> facturen.approvers (te beoordelen mail-reacties)
  'KLANT_AKKOORD_MAIL_NIET_GEKOPPELD', // -> facturen.approvers (niet-gekoppelde mail-reacties)
  // ── batch-56 loonstroken (FO-417) — beoordelingsflow medewerker-verzoeken ──
  // TE_BEOORDELEN gaat naar de beoordelaars-doelgroep (resolver
  // `loonstrook.verzoek-beoordelaars`: instelling -> beheerders-cascade; NOOIT
  // een hardcoded persoon). Na de notificatie-override-merge (FO-416) is de
  // ontvangerset ook centraal per event instelbaar.
  // De twee uitkomst-keys gaan naar de MEDEWERKER zelf en zijn PERSOONSGEBONDEN
  // (FO-409 §B2): alleen het besluit + de reden, nooit interne discussie.
  // ADD VALUE-migratie (20261227000000_b56_loonstroken_enums), CC.2 los.
  'LOONSTROOK_VERZOEK_TE_BEOORDELEN', // -> loonstrook.verzoek-beoordelaars
  'LOONSTROOK_VERZOEK_BESLIST', // -> de medewerker zelf (persoonsgebonden)
  'LOONSTROOK_VERZOEK_AFGEROND', // -> de medewerker zelf (persoonsgebonden)

  // ── batch-57 overeenkomst-vervolg (FO-415 §7) — auto-bruikleen ────────────
  // NODIG = ACTIE per uitgifte-moment naar middelenbeheer ("stel de
  // bruikleenovereenkomst op voor {middel} - {medewerker}"). OPEN = de
  // GEBUNDELDE HR-escalatie zodra taken langer dan de drempel blijven liggen
  // (FO-350: één regel met teller + doorklik naar de werklijst).
  'BRUIKLEEN_OVEREENKOMST_NODIG', // -> voorraad.beheer (ACTIE)
  'BRUIKLEEN_OVEREENKOMST_OPEN', // -> all-hr (GEBUNDELD, NOTIFICATIE)

  // ── batch-59 voertuig-afspraken (FO-435 stap 5 + FO-436 §5) ───────────────
  // De opvolging rond garage-afspraken en de terugroep-afhandeling. Alle vier
  // naar wagenparkbeheer (resolver `intake.team`, FO-416-instelbaar):
  //   TE_PLANNEN      GEBUNDELD (NOTIFICATIE + postvak-ITEM-curatie): er staat
  //                   een afspraak op de lijst maar nog geen datum, terwijl de
  //                   vervaldatum binnen de drempel valt.
  //   MORGEN          informatief (NOTIFICATIE, STIL): reminder aan berijder +
  //                   planner dat de auto morgen naar de garage moet.
  //   NIET_AFGEROND   ACTIE per afspraak: "is de afspraak nagekomen?" - iemand
  //                   moet 'm afronden of op niet-verschenen zetten.
  //   TERUGROEP_HEROPEND  ACTIE: RDW meldt een intern afgehandelde recall weer
  //                   openstaand -> één bevestigingsvraag, geen stille heropening.
  //   TERUGROEP_VOORSTEL_OPEN  GEBUNDELD: berijder-voorstellen die te lang op
  //                   bevestiging door beheer wachten.
  // ADD VALUE-migratie (20261230000000_b59_voertuig_afspraken_enums), CC.2 los.
  'VOERTUIG_AFSPRAAK_TE_PLANNEN', // -> intake.team (GEBUNDELD, NOTIFICATIE)
  'VOERTUIG_AFSPRAAK_MORGEN', // -> berijder + intake.team (NOTIFICATIE)
  'VOERTUIG_AFSPRAAK_NIET_AFGEROND', // -> intake.team (ACTIE)
  'VOERTUIG_TERUGROEP_HEROPEND', // -> intake.team (ACTIE)
  'VOERTUIG_TERUGROEP_VOORSTEL_OPEN', // -> intake.team (GEBUNDELD, NOTIFICATIE)
  // ── batch-59 loonstrook-controles (FO-433) ────────────────────────────────
  // Twee BEHEER-signalen per verloningsrun. De controle-stap aggregeert zelf al
  // (een fire per run met tellingen), dus ze gaan NIET door de BUNDEL_EVENTS-
  // vouwer heen; ze zijn ACTIE, want er moet iets mee gebeuren (afwijking
  // afhandelen resp. boete verrekenen of afboeken). Doelgroep via de bestaande
  // `loonstrook.verantwoordelijken`-cascade en daarmee centraal instelbaar
  // (FO-416) - initieel Jelle + Natasja is configuratie, geen code.
  // PRIVACY: alleen aantallen + periode in titel/body, nooit een bedrag of BSN.
  // ADD VALUE-migratie (20261230000000_b59_loonstrook_controles_enums), CC.2 los.
  'LOONSTROOK_CONTROLE_AFWIJKINGEN', // -> loonstrook.verantwoordelijken (ACTIE)
  'LOONSTROOK_BOETE_NIET_VERREKEND', // -> loonstrook.verantwoordelijken (ACTIE)
  // ── batch-59 onboarding-review (FO-437) ───────────────────────────────────
  // De klant heeft de relatie-onboarding (her)ingediend: er ligt een concrete
  // beoordeel-taak bij de CRM-beoordelaars. ACTIE-item met deep-link naar het
  // reviewscherm + inline sectie-review in het Postvak. De herinnering is
  // bewust een aparte, GEBUNDELDE dagmelding (NOTIFICATIE): de taak zelf staat
  // al als ACTIE in het postvak, dus een tweede af te vinken item per
  // onboarding zou juist de ruis zijn die FO-350 wegneemt.
  // ADD VALUE-migratie (notificatie_event_type_key), zie de batch-59-migratie.
  'RELATIE_ONBOARDING_TER_BEOORDELING', // -> relatie-onboarding.beoordelaars (ACTIE)
  'RELATIE_ONBOARDING_REVIEW_HERINNERING', // -> relatie-onboarding.beoordelaars (NOTIFICATIE, dagbundel)
  // batch-61 klant-onboarding-docgen (FO-450 §2.3) - de KLANT-kant: onboardings
  // die te lang bij de klant open staan (gebundelde dagmelding intern; de klant
  // zelf krijgt de bestaande herinneringsmail).
  'RELATIE_ONBOARDING_KLANT_HERINNERING', // -> relatie-onboarding.beoordelaars (NOTIFICATIE, dagbundel)
  // ── FO-438 B2 (batch-60) — klantportaal-urencache ─────────────────────────
  // Dagelijkse consistentiecheck op de klant-monteur-urencache: paren met
  // gewerkte inzet maar zonder verse berekening. Hun kaart toont dan "nog niet
  // berekend"; dit signaal zorgt dat die stilte opvalt. GEBUNDELD (één melding
  // per cron-run), in-app, geen mail.
  // ADD VALUE-migratie (20261231000300_b60_klantportaal_testbevindingen_enums).
  'KLANTPORTAAL_ERVARING_CACHE_GAT', // -> all-admins (NOTIFICATIE, dagbundel)
  // ── batch-60 klantportaal-zichtbaar-verzoeken (FO-439b K4) ──────────────
  // Het verzamel-event KLANTPORTAAL_AANVRAAG_BINNENGEKOMEN is gesplitst in één
  // event-key PER SOORT klantverzoek, zodat ontvangers en kanalen per soort
  // instelbaar zijn (FO-416) en de curatie per soort ACTIE/STIL kan zijn. Elke
  // key vuurt met bronEntiteitId = KlantVerzoekOpvolging.id (de werkvoorraad-rij)
  // en bronModule 'klantverzoeken'. Doelgroep: de instelbare ontvangersgroep
  // 'Klantverzoeken' (resolver klantverzoeken.ontvangers, terugval intake.team).
  // ADD VALUE-migratie (20261231000400_b60_klantverzoeken_enums), CC.2 los.
  'KLANTVERZOEK_STORING', // -> klantverzoeken.ontvangers (ACTIE, HOGE_PRIO)
  'KLANTVERZOEK_EXTRA_INZET', // -> klantverzoeken.ontvangers (ACTIE)
  'KLANTVERZOEK_KLACHT', // -> klantverzoeken.ontvangers (ACTIE, HOGE_PRIO)
  'KLANTVERZOEK_VRAAG', // -> klantverzoeken.ontvangers (ACTIE)
  'KLANTVERZOEK_OFFERTE', // -> klantverzoeken.ontvangers (ACTIE)
  'KLANTVERZOEK_PLANNING_WIJZIGING', // -> klantverzoeken.ontvangers (ACTIE)
  'KLANTVERZOEK_GEGEVENS_WIJZIGING', // -> klantverzoeken.ontvangers (ACTIE)
  'KLANTVERZOEK_CONTACTGEGEVENS', // -> klantverzoeken.ontvangers (ACTIE)
  'KLANTVERZOEK_MONTEUR_PROFIEL', // -> klantverzoeken.ontvangers (ACTIE)
  'KLANTVERZOEK_GEREEDSCHAP', // -> klantverzoeken.ontvangers (ACTIE)
  'KLANTVERZOEK_MONTEURKEUZE', // -> klantverzoeken.ontvangers (ACTIE)
  'KLANTVERZOEK_SLUITINGSDAG', // -> klantverzoeken.ontvangers (ACTIE)
  'KLANTVERZOEK_BEOORDELING', // -> klantverzoeken.ontvangers (NOTIFICATIE: feedback, geen actie)
  // Gebundelde escalatie: verzoeken die langer dan X uur (default 24, instelbaar)
  // onbeantwoord zijn. Eén melding per cron-run, naar de escalatie-doelgroep.
  'KLANTVERZOEK_ONBEANTWOORD', // -> klantverzoeken.escalatie (NOTIFICATIE, gebundeld)
  // ── batch-61 portal-collega (FO-446 §C) ──────────────────────────────────
  // Persoonsgebonden (FO-409): de bezetting van JOUW dienst wijzigt binnen 24
  // uur vóór aanvang (collega erbij of eraf). Tak op WERKBON_TOEGEWEZEN /
  // WERKBON_DAG_VERPLAATST / WERKBON_DAG_VERWIJDERD; één korte in-app/push-
  // melding per gewijzigde collega, nooit een postvak-item (NOTIFICATIE, STIL).
  // ADD VALUE-migratie (20261231000700_b61_portal_collega_enums), CC.2 los.
  'PLANNING_COLLEGA_BEZETTING_GEWIJZIGD', // -> employee.self (persoonlijk, notify())

  // ── batch-61 uitrusting-credits-maten (FO-445 §3C.7) ────────────────────
  // Van Ysselt meldt een ander creditsaldo dan STREEP: werklijst-item voor de
  // uitrusting-verwerkers met de mutaties sinds de vorige afstemming en de
  // één-klik-correctie. bronEntiteitId = VanYsseltAfstemming.id, bronModule
  // 'uitrusting'; sluit automatisch bij corrigeren/accepteren.
  // ADD VALUE-migratie (20261231000700_b61_uitrusting_credits_sync_enums), CC.2 los.
  'VANYSSELT_SALDO_AFWIJKING', // -> uitrusting.verwerkers (ACTIE)

  // ── batch-62 garage-voertuig (FO-465 §8/§D) ─────────────────────────────
  // REMINDER: de volgende actie (beurt/APK/banden) valt binnen de drempel en
  // er staat nog geen afspraak - ACTIE per reminder-cyclus (bronEntiteitId =
  // voertuig, deeplink naar het dossier), sluit bij plannen/uitvoeren.
  // FACTUUR_CONTROLE: open review-punten op een garagefactuur (km, datum,
  // categorie, schade) - ACTIE per factuur, sluit als alles is afgehandeld.
  // ADD VALUE-migratie (20261231000900_b62_garage_voertuig_enums), CC.2 los.
  'VOERTUIG_ONDERHOUD_REMINDER', // -> intake.team (ACTIE)
  'GARAGE_FACTUUR_CONTROLE', // -> administratie (ACTIE)
  // ── batch-62 kostprijs-werkelijk (FO-460 + FO-461) ────────────────────────
  // Gebundelde dagsignalen rond de werkelijke kostprijs: één melding per NL-dag
  // (bronEntiteitId = de dag, idempotencyKey per dag), NOTIFICATIE - de
  // werkvoorraad zelf staat in /inkoop/brandstof en /payroll/loon-component-mapping.
  // Alleen aantallen en kentekens in titel/body, nooit bedragen of namen.
  // ADD VALUE-migratie (20261231000901_b62_kostprijs_werkelijk_enums), CC.2 los.
  'BRANDSTOF_REGELS_ONGEKOPPELD', // -> administratie (NOTIFICATIE, dagbundel)
  'BRANDSTOF_VOERTUIG_ZONDER_BERIJDER', // -> intake.team (NOTIFICATIE, dagbundel)
  'BRANDSTOF_VERBRUIK_AFWIJKING', // -> intake.team (NOTIFICATIE, dagbundel)
  'BRANDSTOF_CONSISTENTIE_AFWIJKING', // -> administratie (NOTIFICATIE, nachtcontrole)
  'LOONCOMPONENT_ONBEKEND', // -> loonstrook.verantwoordelijken (NOTIFICATIE, dagbundel)
  'LOONKOST_CONSISTENTIE_AFWIJKING', // -> loonstrook.verantwoordelijken (NOTIFICATIE, nachtcontrole)
  // ── batch-62 telefoonabonnementen (FO-463) ──────────────────────────────
  // Beide GEBUNDELD (NOTIFICATIE + postvak-ITEM-curatie) naar de ADMINISTRATIE-
  // functie-inbox via resolver `abonnementen.beheer`, en via triggerNotificatie-
  // Event dus FO-416-instelbaar. bronEntiteitId = Abonnement.id resp.
  // TelefoonControleItem.id. ADD VALUE-migratie (b62_telefoon_enums), CC.2 los.
  //  - TELEFOON_CONTRACT_SIGNAAL: contract loopt af / opzegtermijn nadert /
  //    stilzwijgende verlenging (nadert of uitgevoerd) -> werkvoorraad
  //    "contracten die aflopen".
  //  - TELEFOON_FACTUUR_CONTROLE: onbekend nummer op een providerfactuur,
  //    abonnement zonder factuurregel, maandbedrag wijkt af (met het verschil).
  'TELEFOON_CONTRACT_SIGNAAL', // -> abonnementen.beheer (NOTIFICATIE, gebundeld)
  'TELEFOON_FACTUUR_CONTROLE', // -> abonnementen.beheer (NOTIFICATIE, gebundeld)
  // ── batch-63 certificaat-mailbox (FO-469) ───────────────────────────────
  // Beide GEBUNDELD (NOTIFICATIE + postvak-ITEM-curatie) naar de HR-functie-
  // inbox via `all-hr`, FO-416-instelbaar via triggerNotificatieEvent.
  // bronEntiteitId = CertificaatAanvraag.id resp. CertificaatIngestItem.id.
  // ADD VALUE-migratie (b63_certificaat_mailbox_enums), CC.2 los.
  'CERTIFICAAT_MAILBOX_BEOORDELEN', // -> all-hr (NOTIFICATIE, gebundeld)
  'CERTIFICAAT_MAILBOX_REVIEW', // -> all-hr (NOTIFICATIE, gebundeld)
  // batch-63 cursusplanning (FO-468) - terugkoppeling op een cursusaanvraag aan
  // de opleider. De eerste vijf gaan naar de aanvrager (resolver
  // `actor.requester`, requesterUserId = CursusAanvraag.aangevraagdDoor;
  // bronEntiteitId = CursusAanvraag.id). De laatste is de gebundelde HR-
  // werkvoorraad "opleidermail niet gekoppeld". ADD VALUE-migratie
  // (b63_cursusplanning_enums), CC.2 los.
  'CURSUS_AANVRAAG_BEVESTIGD', // -> actor.requester
  'CURSUS_AANVRAAG_AFGEWEZEN', // -> actor.requester
  'CURSUS_AANVRAAG_TE_BEOORDELEN', // -> actor.requester
  'CURSUS_AANVRAAG_GEEN_ANTWOORD', // -> actor.requester (herinnering / geen antwoord)
  'CURSUS_AANVRAAG_VOORKEURSDATUM', // -> actor.requester (datum nadert/bereikt, niet bevestigd)
  'CURSUS_AANVRAAG_MAIL_NIET_GEKOPPELD', // -> all-hr (gebundeld per dag)
  // batch-65 inkoop-kosten (FO-483 §7) - doorbelasten van aan klanten
  // toegerekende inkoopkosten. Ouderdom en nagekomen kosten GEBUNDELD per dag
  // naar de administratie (FACTUREN.canView); het signaal op een doorbelasting
  // (teruggekoppeld / bron gewijzigd / gecrediteerd) naar de doorbelaster
  // (`actor.requester`, requesterUserId = InkoopDoorbelasting.createdById;
  // bronEntiteitId = de inkoopfactuur). ADD VALUE-migratie
  // (b65_inkoop_notificatie_keys), CC.2 los.
  'INKOOP_DOORBELASTEN_OUDERDOM', // -> administratie (gebundeld per dag)
  'INKOOP_DOORBELASTING_SIGNAAL', // -> actor.requester (de doorbelaster)
  'INKOOP_NAGEKOMEN_KOSTEN', // -> administratie (gebundeld per dag)
  // ── batch-65 voorraad-besteladvies (FO-481) ─────────────────────────────
  // Alle vier ACTIE (werkvoorraad die zichzelf sluit), FO-416-instelbaar via
  // triggerNotificatieEvent. ADD VALUE-migratie (b65_besteladvies_enums), CC.2 los.
  //  - BESTELADVIES_KLAAR: één melding per leverancier "n regels, EUR totaal"
  //    (bronEntiteitId = BesteladviesRun.id) naar de goedkeurders.
  //  - BESTELLING_MAIL_MISLUKT: het bestelbericht is niet verstuurd (mislukt ->
  //    order TE_VERSTUREN, of automatisch versturen uit / geen mailadres ->
  //    handmatig versturen); bronEntiteitId = InkoopOrder.id.
  //  - LEVERING_TE_LAAT: verwachte leverdatum verstreken zonder volledige
  //    ontvangst; bronEntiteitId = InkoopOrder.id.
  //  - MINIMUM_VOORSTEL: afgekeurde adviesregels met een minimumniveau-voorstel
  //    (gebundeld per advies, bronEntiteitId = BesteladviesRun.id) naar voorraadbeheer.
  'BESTELADVIES_KLAAR', // -> inkoop.goedkeurders
  'BESTELLING_MAIL_MISLUKT', // -> inkoop.behandelaar
  'LEVERING_TE_LAAT', // -> inkoop.behandelaar
  'MINIMUM_VOORSTEL', // -> voorraad.minimumbeheer
  // batch-65 finqle-verzamelbon (FO-484 §7) - klant-akkoord per BUNDEL i.p.v.
  // per bon: één melding per klantbesluit op een verzamelbon (akkoord, of
  // afkeuring van (een deel van) de bonnen; de rest staat dan klaar als "-2").
  // Naar de verzender van de verzamelbon (`actor.requester`, requesterUserId =
  // Verzamelbon.verzondenById). bronEntiteitId = Verzamelbon.id. ADD VALUE-
  // migratie (b65_verzamelbon_enums), CC.2 los.
  'KLANTPORTAAL_VERZAMELBON_GEACCORDEERD', // -> actor.requester
  'KLANTPORTAAL_VERZAMELBON_AFGEKEURD', // -> actor.requester
  // batch-65 uitrusting-flow (FO-480) - lever-/afhaalafspraak na het verwerken
  // van een uitrustingbestelling. bronEntiteitId = UitrustingLevering.id; de link
  // /uitrusting/levering/{ID} bestaat in portal en backoffice. ADD VALUE-
  // migratie (b65_uitrusting_flow_events), CC.2 los.
  'UITRUSTING_LEVERING_VOORSTEL', // -> actor.requester (medewerker: nieuw voorstel)
  'UITRUSTING_LEVERING_GEPLAND', // -> actor.requester (medewerker: moment vastgelegd)
  'UITRUSTING_LEVERING_MOMENT_GEKOZEN', // -> uitrusting.picklijst (magazijn: medewerker koos)
  'UITRUSTING_LEVERING_MEENEMEN', // -> actor.requester (ACTIE voor de meenemer)
  'UITRUSTING_LEVERING_NIET_VERSCHENEN', // -> uitrusting.levering.beoordelaar (na herhaald niet verschenen)
  // batch-65 apk-rapport (FO-478) - ACTIE per voertuig naar wagenparkbeheer
  // (resolver `intake.team`, zelfde doelgroep als de APK-verval-reeks).
  // bronEntiteitId = Voertuig.id; het afgekeurd-item sluit vanzelf zodra een
  // goedgekeurd APK-rapport van dat voertuig is vastgelegd. ADD VALUE-migratie
  // (b65_apk_rapport_enums), CC.2 los.
  'VOERTUIG_APK_AFGEKEURD', // -> intake.team (ACTIE: herkeuring plannen)
  'VOERTUIG_APK_RDW_AFWIJKING', // -> intake.team (ACTIE: RDW-datum wijkt af van het rapport)
  // batch-65 factuur-richting (FO-486 §7) - een factuur uit het verkoop-postvak
  // is als concept-inkoopfactuur klaargezet (handmatig of automatisch); naar de
  // inkoop-beoordelaars met de actuele teller. bronEntiteitId = InkoopFactuur.id.
  // ADD VALUE-migratie (b65_factuur_richting_notificatie_key), CC.2 los.
  'VERKOOP_POSTVAK_NAAR_INKOOP', // -> inkoop.behandelaar (NOTIFICATIE)
  // ── FO-508 (spec 208) telefoon en simkaart als set ─────────────────────
  // Een losse koppeling (telefoon zonder simkaart / simkaart zonder telefoon)
  // vraagt goedkeuring door HR of directie (ADMIN); de uitkomst gaat naar de
  // aanvrager (`actor.requester`, requesterUserId = aangevraagdDoor).
  // bronEntiteitId = MiddelUitzonderingAanvraag.id. ADD VALUE-migratie
  // (fo508_middel_uitzondering_enums), CC.2 los; rijen in fo508_middel_uitzondering.
  'MIDDEL_UITZONDERING_AANGEVRAAGD', // -> middel-uitzondering.beoordelaars (ITEM)
  'MIDDEL_UITZONDERING_BEOORDEELD', // -> actor.requester
  // batch-66 voertuigcontrole (FO-489) - gezondheid van de RDW-sync. Geen bron-
  // entiteit: bronEntiteitId = `rdw-sync-<soort>-<NL-dag>` (hooguit één melding
  // per dag, ook bij twee runs of twee replica's); de link wijst naar de
  // integratiepagina. ADD VALUE-migratie (b66_voertuigcontrole_enums), CC.2 los.
  'VOERTUIG_RDW_SYNC_FOUTEN', // -> all-admins (NOTIFICATIE: >20% fouten of afgebroken run)
  'VOERTUIG_RDW_SYNC_STIL', // -> all-admins (NOTIFICATIE: te lang geen geslaagde run; grens volgt de planning)
  'VOERTUIG_RDW_CRON_HERSTELD', // -> all-admins (NOTIFICATIE: cron-rij ontbrak, opnieuw aangemaakt)
  // batch-66 interne-geldstroom (FO-494) - een interne geldstroom klopt niet
  // (engine- of spiegelbedrag wijkt af, of een gekoppelde factuur is vervallen);
  // bronEntiteitId = InterneGeldstroom.id. En de gebundelde weekmelding over
  // eenzijdige interne boekingen (inkoop zonder verkoop of andersom). ADD
  // VALUE-migratie (b66_interne_geldstroom_notificatie_keys), CC.2 los.
  'INTERNE_GELDSTROOM_VERSCHIL', // -> facturen.approvers (NOTIFICATIE)
  'INTERNE_GELDSTROOM_EENZIJDIG', // -> facturen.approvers (NOTIFICATIE, gebundeld per week)
  // batch-66 inkoop-afwijkingen (FO-490 §2.5) - afwijkingen-register op
  // inkoopfacturen. GEREGISTREERD: nieuw geregistreerde afwijking naar de
  // inkoop-goedkeurders (bronEntiteitId = InkoopFactuur.id; bij een factuur
  // tussen eigen BV's gaat dezelfde melding intern naar de administratie).
  // OUDERDOM: gebundeld per NL-dag, afwijkingen die te lang zonder reactie
  // openstaan (bronEntiteitId = de dag). CREDITNOTA_ONTVANGEN: creditnota geboekt
  // van een leverancier met open afwijkingen, en opnieuw wanneer het gekoppelde
  // creditbedrag afwijkt (bronEntiteitId = de creditnota). REACTIE_ONTVANGEN: de
  // leverancier reageerde op de afwijkingsmail (naar de eigenaar,
  // requesterUserId = InkoopAfwijking.eigenaarId). ADD VALUE-migratie
  // (b66_inkoop_afwijkingen_enums), CC.2 los.
  'INKOOP_AFWIJKING_GEREGISTREERD', // -> inkoop.goedkeurders
  'INKOOP_AFWIJKING_OUDERDOM', // -> inkoop.goedkeurders (gebundeld per dag)
  'INKOOP_AFWIJKING_CREDITNOTA_ONTVANGEN', // -> inkoop.goedkeurders
  'INKOOP_AFWIJKING_REACTIE_ONTVANGEN', // -> actor.requester (de eigenaar)
  // ── batch-66 inkoop-afwijkingen (FO-497) — boetes ───────────────────────
  // TE_BEOORDELEN: een boete wacht op beoordeling (uit een inkoopfactuur,
  // bestuurder onbekend, bezwaar van de medewerker of een verhoging;
  // bronEntiteitId = Boete.id). DOORGEZET: de medewerker hoort vooraf dat een
  // boete met zijn salaris wordt verrekend (bronEntiteitId = Employee.id).
  // RIJGEDRAG_SIGNAAL: de boetes van een medewerker raken een drempel
  // (bronEntiteitId = Employee.id). ADD VALUE-migratie (b66_boetes_enums), CC.2 los.
  'BOETE_TE_BEOORDELEN', // -> hr+manager
  'BOETE_DOORGEZET', // -> employee.self
  'RIJGEDRAG_SIGNAAL', // -> boetes.rijgedrag (HR + teamleider van de medewerker, alleen persoonlijk)
  // ── batch-66 contract-mijlpalen (FO-496) ────────────────────────────────
  // De mijlpaal-workflow rond het arbeidscontract. Een key per mijlpaal M1 t/m
  // M9, zodat de ontvangers per mijlpaal instelbaar zijn (FO-416); de standaard-
  // doelgroep komt uit het sjabloon (resolver `contract-mijlpaal.doelgroep`,
  // bronEntiteitId = ContractMijlpaal.id). Daarnaast: de escalatie bij een
  // verlopen mijlpaal (doelgroep uit het sjabloon), de correctie na een
  // datumwijziging en "teamleider toewijzen" (bronEntiteitId =
  // EmploymentContract.id). ADD VALUE-migratie (b66_contract_mijlpalen_events),
  // CC.2 los.
  'CONTRACT_MIJLPAAL_KENNISMAKING', // M1 -> contract-mijlpaal.doelgroep (ACTIE)
  'CONTRACT_MIJLPAAL_START_NADERT', // M2 -> contract-mijlpaal.doelgroep (NOTIFICATIE)
  'CONTRACT_MIJLPAAL_FACILITIES', // M3 -> contract-mijlpaal.doelgroep (ACTIE)
  'CONTRACT_MIJLPAAL_SUCCES_WENSEN', // M4 -> contract-mijlpaal.doelgroep (ACTIE)
  'CONTRACT_MIJLPAAL_CHECKIN_WEEK1', // M5 -> contract-mijlpaal.doelgroep (ACTIE)
  'CONTRACT_MIJLPAAL_CHECKIN_MAAND1', // M6 -> contract-mijlpaal.doelgroep (ACTIE)
  'CONTRACT_MIJLPAAL_OVERLEG_PROEFTIJD', // M7 -> contract-mijlpaal.doelgroep (ACTIE)
  'CONTRACT_MIJLPAAL_PROEFTIJD_EINDE', // M8 -> contract-mijlpaal.doelgroep (ACTIE)
  'CONTRACT_MIJLPAAL_VERLENGING', // M9 -> contract-mijlpaal.doelgroep (ACTIE)
  'CONTRACT_MIJLPAAL_ESCALATIE', // -> contract-mijlpaal.escalatie (ACTIE)
  'CONTRACT_MIJLPAAL_CORRECTIE', // -> contract-mijlpaal.doelgroep (NOTIFICATIE)
  'CONTRACT_MIJLPAAL_TL_TOEWIJZEN', // -> all-hr (ACTIE)
  // batch-66 kluis-niveaus (FO-495 §2.C.9) - beveiligingsniveaus van de kluis.
  // Geen enkele melding bevat de waarde van een geheim of een bevestigingscode.
  // TOEGANG_VERLOOPT / STRIKT_ONTHULD / VRIJGAVE_BEOORDEELD: bronEntiteitId =
  // KluisItem.id (link /kluis/{ID}); VRIJGAVE_AANGEVRAAGD: bronEntiteitId =
  // KluisVrijgaveAanvraag.id (link naar de werkvoorraad, sluit bij het besluit).
  // ADD VALUE-migratie (b66_kluis_niveaus_notificatie_keys), CC.2 los.
  'KLUIS_TOEGANG_VERLOOPT', // -> kluis.eigenaar (+ de verlener; een dag vooraf)
  'KLUIS_STRIKT_ONTHULD', // -> kluis.eigenaar (bij elke onthulling, ook de eigen)
  'KLUIS_VRIJGAVE_AANGEVRAAGD', // -> kluis.vrijgave.beoordelaars (ACTIE)
  'KLUIS_VRIJGAVE_BEOORDEELD', // -> kluis.vrijgave.betrokkenen (de aanvrager en de eigenaar)
  'KLUIS_BESCHERMING_GEWIJZIGD', // -> kluis.eigenaar (ook de vorige eigenaar, ook bij een eigen handeling)
  // batch-66 uren-loonstroken (FO-492) - 24 uur voor het vrijgavemoment van een
  // periode staan er nog loonstroken die niet goedgekeurd zijn. Eén melding per
  // periode naar de loonstrook-verantwoordelijken (herinnering: bel + mail, geen
  // postvak-item). PRIVACY: alleen aantal + periode, nooit een bedrag of BSN.
  // ADD VALUE-migratie (b66_loonstrook_planning_notificatie_key), CC.2 los.
  'LOONSTROOK_VRIJGAVE_NIET_GOEDGEKEURD', // -> loonstrook.vrijgave-bewakers (NOTIFICATIE)
  // batch-67 taken-engine (FO-498 §2.5) - deadline-bewaking van operationele
  // taken (cron `taak-deadline-herinnering`). Een keer per DEADLINE (de markers
  // `deadlineHerinneringOp` / `deadlineEscalatieOp` op de taak; een verschoven
  // deadline is een nieuwe ronde). De escalatie geldt niet voor stap-taken van
  // een workflow (die escaleert zelf); de herinnering aan de uitvoerder wel.
  // bronEntiteitId = OperationeleTaak.id (link /operationele-taken/{ID}).
  // ADD VALUE-migratie (b67_taken_engine_notificatie_keys), CC.2 los.
  'TAAK_DEADLINE_MORGEN', // -> taak.uitvoerder (een dag voor de deadline)
  'TAAK_DEADLINE_VERSTREKEN_AANMAKER', // -> taak.deadline-escalatie (de dag na de deadline)
  // batch-67 hr-brieven (FO-506) - de HR-besluitworkflows (verlenging, omzetting,
  // opzegging, non-actief, VSO, staande voet, acceptatie opzegging). Nooit een
  // reden, bedrag of medische inhoud in de melding. Losse ADD VALUE-migratie
  // (b67_hr_besluiten_notificatie_keys), CC.2.
  'HR_BESLUIT_CONTROLE_GEVRAAGD', // -> hr-besluit.controleurs (ACTIE: 4-ogen)
  'HR_BESLUIT_GECONTROLEERD', // -> actor.requester (de starter: goedgekeurd/afgewezen)
  'HR_BESLUIT_STAP_OPEN', // -> hr-besluit.stap-verantwoordelijke (ACTIE per rol)
  'HR_BESLUIT_TERMIJN_NADERT', // -> hr-besluit.behandelaars (aanzeg-, bedenk-, herbeoordelings-, bezwaartermijn)
  'HR_BESLUIT_TERMIJN_VERSTREKEN', // -> hr-besluit.behandelaars+directie (escalatie)
  'HR_BESLUIT_BRIEF_VERSTREKT', // -> employee.self (persoonlijk: brief in de portal)
  'HR_BESLUIT_VSO_HERROEPEN', // -> hr-besluit.behandelaars+salaris (offboarding teruggedraaid)
  // batch-67 middelen-inname (FO-499 + FO-505) - middelen-ledger, overdracht,
  // automatische bruikleenovereenkomst, reparatieverzoek en verhaal.
  // ADD VALUE-migratie (b67_middelen_notificatie_keys), CC.2 los.
  'MIDDEL_OVERDRACHT_TE_BEVESTIGEN', // -> middel.overdracht-ontvanger (ACTIE): bronEntiteitId = MiddelOverdrachtVerzoek.id
  'MIDDEL_OVERDRACHT_AFGEHANDELD', // -> employee.self (NOTIFICATIE): bronEntiteitId = Employee.id
  'BRUIKLEEN_ONDERTEKENING_HERINNERING', // -> employee.self (NOTIFICATIE): na n dagen niet ondertekend
  'BRUIKLEEN_ONDERTEKENING_ESCALATIE', // -> all-hr (ACTIE): na m dagen niet ondertekend
  'REPARATIEVERZOEK_INGEDIEND', // -> reparatie.goedkeurders (ACTIE): bronEntiteitId = ReparatieVerzoek.id
  'REPARATIEVERZOEK_BEOORDEELD', // -> actor.requester (de indiener) (NOTIFICATIE)
  'MIDDEL_VERHAAL_BESLUIT', // -> employee.self (NOTIFICATIE, met bezwaarmogelijkheid)
  'MIDDEL_BEZWAAR_INGEDIEND', // -> all-hr (ACTIE)
  // -- batch-67 campagnes (FO-501 bijtelling, FO-502 vakantie, FO-503 evenement) --
  'BIJTELLING_CAMPAGNE_START',          // -> campagne.deelnemer (kaart + push bij start)
  'BIJTELLING_CAMPAGNE_HERINNERING',    // -> campagne.deelnemer (14/7 dagen voor de deadline, dagelijks de laatste 3)
  'BIJTELLING_NIET_INGEDIEND',          // -> campagne.beheerders (HR-signaal per berijder na de deadline; nooit stil een default)
  'BIJTELLING_KEUZE_BEOORDEELD',        // -> campagne.deelnemer (goedgekeurd/afgekeurd met reden)
  'CAMPAGNE_VERLOF_ONDER_VOORBEHOUD',   // -> campagne.beheerders (verlofaanvraag geboekt met saldotekort)
  'CAMPAGNE_BEZETTINGSCONFLICT',        // -> planning.planners (te veel monteurs tegelijk weg bij een klant)
  'EVENEMENT_UITNODIGING',              // -> campagne.deelnemer (bij vrijgave)
  'EVENEMENT_HERINNERING',              // -> campagne.deelnemer (alleen wie nog niet antwoordde)
  'EVENEMENT_BEVESTIGING',              // -> campagne.deelnemer (details + agenda-link)
  'EVENEMENT_ORGANISATIE_OVERZICHT',    // -> evenement.organisatie (dagoverzicht laatste week, wijziging, sluiting)
  'MIDDEL_VERHAAL_TE_VERREKENEN', // -> loonstrook.verantwoordelijken (ACTIE, module reparatie-verrekenen): bronEntiteitId = ReparatieVerzoek.id
  // batch-68 controle (FO-342 §7) - verloningscontrole. bronEntiteitId = de urenmaand 'YYYY-MM'.
  'VERLONINGSCONTROLE_KLAAR',               // -> payroll.approvers (run gereed met n rood)
  'VERLONINGSCONTROLE_GEWIJZIGD_NA_AKKOORD', // -> payroll.approvers (nachtelijke bewaking: bron gewijzigd na akkoord)
  'VERLONINGSCONTROLE_HERINNERING',         // -> payroll.approvers (werkdag N zonder akkoord op de vorige urenmaand)
  // batch-68 werkplaatsfacturen (FO-341) - alle drie naar wagenparkbeheer (ACTIE).
  'VOERTUIG_APK_NIET_BIJ_RDW',               // -> intake.team (ACTIE): APK gefactureerd, na vijf werkdagen geen nieuwere RDW-datum; bronEntiteitId = Voertuig.id
  'VOERTUIG_SERVICE_INDICATOR_NIET_GERESET', // -> intake.team (ACTIE): beurt geregistreerd, telematica blijft achterstallig melden; bronEntiteitId = Voertuig.id
  'VOERTUIG_ONDERHOUD_TERUGDRAAIEN_CONFLICT', // -> intake.team (ACTIE): rollback kon het interval niet herstellen (nieuwere reset); bronEntiteitId = Voertuig.id
  // -- batch-68 handtekening (FO-345) - ondertekenen per rol en volgorde --
  'DOCUMENT_ONDERTEKENEN_AAN_DE_BEURT',     // -> de ondertekenaar die aan de beurt is (ACTIE, module document-ondertekening): bronEntiteitId = DocumentOndertekenaar.id
  'DOCUMENT_HANDTEKENING_VERVALLEN',        // -> wie al tekende: de inhoud is gewijzigd, teken opnieuw (NOTIFICATIE)
  'DOCUMENT_ONDERTEKENDEADLINE_VERSTREKEN', // -> all-hr (ACTIE, module document-ondertekening-deadline): bronEntiteitId = DocumentOndertekenaar.id
  // -- batch-69 bestelflow (FO-512) - beide informatief, bronEntiteitId = UitrustingBestelling.id --
  'UITRUSTING_BESTELLING_INGEDIEND',          // -> uitrusting.beoordelaars (NOTIFICATIE): er staat een aanvraag klaar om te beoordelen
  'UITRUSTING_BESTELLING_STATUS_GOEDKEURDER', // -> uitrusting.goedkeurder (NOTIFICATIE): "extern besteld" en "geleverd" voor wie goedkeurde
  'SKILLMATRIX_INGEVULD', // -> skills.beoordelaars (NOTIFICATIE): medewerker vulde de eigen skillmatrix in; bronEntiteitId = Employee.id
  // -- batch-69 km-balans (FO-515) - kilometerbalans, gebundeld per afgesloten maand --
  'KILOMETERBALANS_WERK_BOVEN_TOTAAL', // -> voertuigen.beheer+kostprijs (wagenparkbeheer met kostprijsinzage, postvak-ITEM): alleen kentekens; bronEntiteitId = periode jjjj-mm
  'KILOMETERBALANS_BOVEN_DREMPEL',     // -> all-hr (postvak-ITEM): alleen aantallen; bronEntiteitId = periode jjjj-mm
  // -- batch-69 planbord-afspraak (FO-523) - afspraak met een klant. bronEntiteitId = PlanningAfspraak.id.
  // Push staat in de event-rijen bewust UIT tot PO-akkoord (batch-guardrail).
  'AFSPRAAK_HERINNERING',         // -> afspraak.deelnemers (herinnering vooraf, standaard 1 uur)
  'AFSPRAAK_VERSLAG_GEVRAAGD',    // -> afspraak.deelnemers (een kwartier na de eindtijd: maak je verslag)
  'AFSPRAAK_VERSLAG_HERINNERING', // -> afspraak.deelnemers (na 4 uur en de volgende ochtend 08:00)
  'AFSPRAAK_VERSLAG_ESCALATIE',   // -> afspraak.escalatie (ACTIE, module crm-afspraak-escalatie: na 48 uur)
  // -- batch-69 samenstellen (FO-509) - de samenstel-opdracht --
  'SAMENSTELLING_KLAAR',  // -> uitrusting.picklijst (ACTIE, module samenstelling): alles gereserveerd, klaar om samen te stellen; bronEntiteitId = UitgifteSamenstelling.id
  'SAMENSTELLING_RISICO', // -> all-hr (NOTIFICATIE, module samenstelling): uitrusting niet compleet voor de startdatum; bronEntiteitId = UitgifteSamenstelling.id
  'SET_SAMENSTELLEN_NODIG', // -> uitrusting.picklijst (ACTIE, module samenstelling-set): te weinig complete sets van een template; bronEntiteitId = GereedschapsPakket.id
  // -- batch-69 samenstellen (FO-516) - de inname-afvinklijst --
  'INNAME_TER_GOEDKEURING_HR', // -> all-hr (ACTIE, module inname-sessie): niet ingeleverde spullen per item te beoordelen; bronEntiteitId = InnameSessie.id
  'INNAME_TE_VERREKENEN', // -> loonstrook.verantwoordelijken (ACTIE, module inname-verrekening): verrekenen via het loon; bronEntiteitId = InnameSessieItem.id
  'INNAME_BEVESTIGD', // -> employee.self (module middelen): de inname-bevestiging; bronEntiteitId = Employee.id
  // -- batch-69 werkbon-pauze (FO-511 §C6) --
  'KLANTPAUZE_ONBEVESTIGD', // -> administratie (NOTIFICATIE, postvak ITEM): verzending zonder bevestigde klantpauze; bronEntiteitId = Werkbon.id
  // -- batch-69 actiepunten (FO-514) - het register "Actiepunten app" --
  'ACTIEPUNTEN_SAMENVATTING', // -> actiepunten.samenvatting (NOTIFICATIE): weekoverzicht (directie) en direct na nieuwe punten (directie + de rollen met nieuwe punten); bronEntiteitId = 'week-<jjjj-Www>' of 'nieuw-<hash van de nieuwe codes>'
  'ACTIEPUNT_HEROPEND',       // -> actiepunt.verantwoordelijke (ACTIE, module actiepunten): bronEntiteitId = SysteemActiepunt.id
  // -- batch-69 tvt-saldi (FO-522 §C9, FO-513 §A2) --
  'VERLOF_SALDO_NEGATIEF_MET_TVT', // -> all-hr (drift-signaal, postvak ITEM): een emmer werd negatief terwijl er TVT was; bronEntiteitId = Employee.id
  'VERLOF_SALDO_VERPLAATST',       // -> employee.self (persoonlijk): "saldo gecorrigeerd: n uur TVT ingezet voor verlof"; bronEntiteitId = Employee.id
  // -- batch-69 tvt-saldi (FO-513 §C8) --
  'TVT_OPBOUW_GEDAALD',            // -> all-hr (postvak ITEM): opbouw van een week/dag daalde en het beleid stelt niet bij, of een bijstelling zette de TVT in de min; bronEntiteitId = Employee.id
  // -- batch-69 magazijn-cijfers (FO-521) - besteladvies in de goedkeuringenlijst --
  // ADD VALUE-migratie b69_voorraad_enumwaarden (CC.2 los). FO-416-instelbaar.
  'BESTELADVIES_HERINNERING',         // -> inkoop.goedkeurders (NOTIFICATIE + postvak-config ITEM): adviesregels wachten N werkdagen; bronEntiteitId = BesteladviesRun.id
  'BESTELADVIES_ESCALATIE',           // -> directie = doelgroep Inkoop-rapportage (NOTIFICATIE + postvak-config ITEM): M werkdagen onbeoordeeld; bronEntiteitId = BesteladviesRun.id
  'BESTELADVIES_AUTOMATISCH_BESTELD', // -> directie = doelgroep Inkoop-rapportage (NOTIFICATIE + postvak-config ITEM): dagelijkse samenvatting bij VOLLEDIG_AUTOMATISCH
  'VOORRAAD_PRIJS_VOORSTEL',          // -> voorraad.minimumbeheer (ACTIE, module voorraad-prijsvoorstel): levering met afwijkende prijs; bronEntiteitId = VoorraadArtikel.id
  // -- batch-69 magazijn-cijfers (FO-520) - telsessie --
  'TELLING_GOEDKEURING_NODIG',        // -> voorraad.goedkeurders = doelgroep Voorraad-goedkeurders (ACTIE, module voorraad-telling-goedkeuring): correcties boven de drempel; bronEntiteitId = VoorraadTelling.id
  'TELLING_AFGEROND',                 // -> voorraad.goedkeurders = doelgroep Voorraad-rapportage (NOTIFICATIE + postvak-config ITEM): telling afgerond; bronEntiteitId = VoorraadTelling.id
  'TELLING_OPEN_HERINNERING',         // -> actor.requester = de aanmaker (ACTIE, module voorraad-telling-open): telling N dagen open; bronEntiteitId = VoorraadTelling.id
  // -- batch-70 vakantiecampagne (FO-525 §7) --
  'CAMPAGNE_SALDO_GEWIJZIGD',         // -> employee.self (persoonlijk): saldo of advies gewijzigd na indienen; bronEntiteitId = Employee.id, link naar stap 2 van de wizard
  'VERLOF_SALDO_RECONCILIATIE',       // -> all-hr (postvak ITEM): het jaaroverzicht sluit niet aan op het saldo-ledger; bronEntiteitId = Employee.id
  'CAMPAGNE_IN_OVERLEG',              // -> campagne.beheerders (teamleider/planning met Verlof goedkeuren): een wens staat op "in overleg"; bronEntiteitId = Campagne.id
  'CAMPAGNE_WIJZIGING_INGEDIEND',     // -> campagne.beheerders: na indienen gewijzigd of toegevoegd (een nieuwe aanvraag); bronEntiteitId = Campagne.id
  'CAMPAGNE_TVT_DECEMBER_LAAT',       // -> all-hr: TVT-ingang per 1 december gekozen na 20 november; vóór de december-payroll verwerken (FO-525 A9)
  'CAMPAGNE_UITKOMST',                // -> employee.self (persoonlijk): de collectieve uitkomst, een melding per medewerker; bronEntiteitId = Employee.id
  // -- batch-70 uitrusting-controle (FO-531 §7) --
  'CAMPAGNE_UITRUSTING_START',           // -> campagne.deelnemer (direct, notify userId): de uitrustingscontrole is open; bronEntiteitId = Campagne.id
  'CAMPAGNE_UITRUSTING_HERINNERING',     // -> campagne.deelnemer (direct): herinnering, alleen wie nog niet indiende; bronEntiteitId = Campagne.id
  'UITRUSTING_AANGEVULD_TEKENEN',        // -> employee.self (direct): magazijn vulde aan of gaf uit, de gereedschapsovereenkomst staat klaar; bronEntiteitId = Campagne.id
  'UITRUSTING_ONTBREKEND_NIEUW',         // -> voorraad.minimumbeheer = doelgroep Magazijn (NOTIFICATIE + postvak ITEM, module uitrustingscontrole): dagelijkse bundel nieuwe meldingen + keuringen; bronEntiteitId = Campagne.id
  'UITRUSTING_KOFFER_OP_VERKEERDE_NAAM', // -> voorraad.minimumbeheer = doelgroep Magazijn (ACTIE, module uitrustingscontrole-wissel): andere koffer gescand dan op naam; bronEntiteitId = Campagne.id (postvak per set)
  'UITRUSTING_TEAM_WEEKLIJST',           // -> de teamleider zelf (direct, notify userId): wekelijks wie van zijn team nog niet indiende; bronEntiteitId = Campagne.id
  // -- batch-70 voertuig-meldingen (FO-527 §7) - bronEntiteitId = VoertuigMeldingOpdracht.id --
  'VOERTUIG_MELDING_MISLUKT',               // -> voertuig-melding.doelgroep (wagenpark, of salaris bij NAW; postvak-ITEM): melding kan niet automatisch de deur uit
  'VOERTUIG_MELDING_NIET_BEVESTIGD',        // -> voertuig-melding.doelgroep (postvak-ITEM): geen bevestiging na de termijn, herinnering verstuurd
  'VOERTUIG_CARTRACKER_CORRECTIE_MISLUKT',  // -> voertuigen.beheer (postvak-ITEM): automatische Cartracker-correctie mislukt
  'VOERTUIG_CARTRACKER_HANDMATIG_NODIG',    // -> voertuigen.beheer (postvak-ITEM): handmatige modus, taak staat klaar
  'LOONSTROOK_BIJTELLING_AFWIJKING',        // -> loonstrook.verantwoordelijken (postvak-ITEM): bijtelling op de strook strookt niet met de verstuurde loonstrookcorrectie
  // batch-70 wagenpark-week (FO-528 + FO-529) — gebundeld per ISO-week (bronEntiteitId =
  // de week jjjj-Www of de medewerker), alleen kentekens en aantallen; de vier
  // BRANDSTOF_*-keys hierboven worden hergebruikt (geen tweede key met dezelfde betekenis).
  'VOERTUIG_KM_ONBETROUWBAAR',        // -> voertuigen.beheer (ITEM): verdachte km-standen en voertuigen zonder weekstand
  'VOERTUIG_CONTRACT_KM_OVERSCHRIJDING_VERWACHT', // -> voertuigen.beheer+kostprijs (ITEM): contract-km naar verwachting overschreden
  'VOERTUIG_PRIVE_KM_HOOG',           // -> all-hr (ITEM): privé-km boven de drempel, of privégebruik bij geen bijtelling
  'VOERTUIG_TOELICHTING_GEVRAAGD',    // -> employee.self (persoonlijk, ITEM): afwijkingen ritten/uren en tankpas-signalen om toe te lichten
  'TANKPAS_HOUDER_GEWIJZIGD',         // -> employee.self (persoonlijk, STIL): de tankpas staat nu op jouw naam, of is overgedragen
  'TANKPAS_REGIME_ONBEKEND',          // -> all-hr (ITEM): houders zonder bijtellingskeuze of verklaring
  'TANKPAS_500KM_GRENS',              // -> all-hr (ITEM): bevestigde privé-km bij geen bijtelling op 60/90/100% van de 500 km
  'WAGENPARK_WEEKRAPPORT',            // -> voertuigen.beheer+kostprijs (STIL; doelgroep "Wagenpark-weekrapport" via de ontvangers-instelling)
  // -- batch-70 app-dashboard (FO-526) --
  'APP_DEPLOY_LIVE',                  // -> app-dashboard.doelgroep (NOTIFICATIE, postvak STIL): een nieuwe versie is live; bronEntiteitId = AppDeploy.id
  'APP_DEPLOY_NIET_GEREGISTREERD',    // -> all-admins (ACTIE, postvak ITEM): een deploy is niet door de registratiestap vastgelegd; bronEntiteitId = sha
  'APP_DASHBOARD_WEEKSAMENVATTING',   // -> app-dashboard.doelgroep (NOTIFICATIE, postvak STIL): maandag 08:00; bronEntiteitId = 'week-<jjjj-Www>'
  // batch-71 kiosk-magazijn (FO-532 §7)
  'KIOSK_PIN_GEBLOKKEERD',    // -> kiosk.beheerders (ADMIN+HR, postvak-ITEM): te vaak een foute PIN, operator geblokkeerd (ook de badge)
  'KIOSK_SESSIE_GEREVOCEERD', // -> kiosk.beheerders (ADMIN+HR, postvak-STIL): een kioskaccount is op afstand uitgelogd
  // -- batch-71 workflow-sjablonen (FO-533 §15) - bronEntiteitId = WorkflowRun.id (de dagbundel: doelgroep + dag) --
  'WORKFLOW_GESTART',                  // -> workflow.doelgroep (eigenaar, postvak STIL)
  'WORKFLOW_STAP_OPEN',                // -> workflow.doelgroep (doelgroep van de stap, gebundeld per dag, postvak STIL)
  'WORKFLOW_STAP_VERLOPEN',            // -> workflow.doelgroep (escalatie-doelgroep, postvak-ITEM)
  'WORKFLOW_STAP_MISLUKT',             // -> workflow.doelgroep (eigenaar, plus beheer via de terugval; postvak-ITEM)
  'WORKFLOW_AFGEROND',                 // -> workflow.doelgroep (eigenaar, samenvatting, postvak STIL)
  'WORKFLOW_WACHT_EXTERN_HERINNERING', // -> workflow.doelgroep (doelgroep van de stap, postvak-ITEM)
  // -- batch-71 offboarding-inname (FO-535 §7) --
  'OFFBOARDING_SALARIS_AFMELDING_VERSTUURD',      // -> all-hr (NOTIFICATIE, postvak STIL): afmeldmail naar de salarisadministratie; bronEntiteitId = Employee.id
  'OFFBOARDING_SALARIS_AFMELDING_NIET_BEVESTIGD', // -> all-hr (ACTIE, postvak ITEM): na 5 werkdagen geen bevestiging; bronEntiteitId = Employee.id
  'OFFBOARDING_EINDAFREKENING_VERSTUURD_NAW',     // -> all-hr (NOTIFICATIE, postvak STIL): eindafrekening naar de salarisadministratie; bronEntiteitId = Employee.id
  'OFFBOARDING_INNAME_CONFLICT',                  // -> all-hr (ACTIE, postvak ITEM): innamelijst en cockpit zijn het oneens; bronEntiteitId = Employee.id
  // -- batch-71 gesprek-secties (FO-536 §7, FO-537 §7) --
  'ONTWIKKELDOEL_STREEFDATUM_NADERT', // -> ontwikkeldoel.eigenaar (NOTIFICATIE): 30 dagen voor de streefdatum; bronEntiteitId = doel-id
  'ONTWIKKELING_NIET_BESPROKEN',      // -> all-hr (postvak-ITEM): twee gesprekken op rij geen ontwikkeling; bronEntiteitId = Employee.id
  'CONTROLE_AFWIJKING',               // -> gesprekcontrole.houders (postvak-ITEM): afwijking in een gesprekscontrole; bronEntiteitId = invulling-id
  'CONTROLE_NIET_UITGEVOERD',         // -> all-hr (postvak-ITEM): lijst bij een gesprek niet uitgevoerd; bronEntiteitId = Employee.id
  'TANKPAS_NIET_AANWEZIG',            // -> gesprekcontrole.houders (postvak-ITEM): tankpas niet aanwezig bij de controle; bronEntiteitId = voertuig-id
  // -- batch-71 kennismaking (FO-534 §7) --
  'KENNISMAKING_GEPLAND',             // -> kennismaking.verantwoordelijke+hr (ACTIE, ITEM): gesprek, taak, blok en uitnodiging staan klaar; bronEntiteitId = ContractMijlpaal.id
  'KENNISMAKING_HERINNERING',         // -> kennismaking.verantwoordelijke (NOTIFICATIE, STIL): de dag voor het gesprek; bronEntiteitId = KennismakingChecklist.id
  'KENNISMAKING_DEADLINE_VERLOPEN',   // -> kennismaking.escalatie (ACTIE, ITEM): geen verslag voor de deadline (bronEntiteitId = ContractMijlpaal.id), of de weeklijst (bronEntiteitId = 'week-<jjjj-Www>')
  'KENNISMAKING_ACTIEPUNT',           // -> direct via notify aan de houder (ACTIE, ITEM): een open onderwerp is een actiepunt geworden; bronEntiteitId = GespreksverslagAfspraak.id
  'KENNISMAKING_SAMENVATTING',        // -> direct via notify aan de medewerker (persoonlijk, STIL): wat we hebben besproken; bronEntiteitId = Employee.id
  'KENNISMAKING_AGENDA_MISLUKT',      // -> kennismaking.verantwoordelijke (ACTIE, ITEM): agenda-item niet aangemaakt, taak en blok staan er wel; bronEntiteitId = KennismakingChecklist.id
  // -- batch-71 verplichte-certificaten (FO-541). bronEntiteitId = Employee.id --
  'VERPLICHT_CERTIFICAAT_UPLOAD_GEVRAAGD',    // -> employee.self (persoonsgebonden, postvak STIL; de kaart staat in de portal)
  'VERPLICHT_CERTIFICAAT_UPLOAD_HERINNERING', // -> employee.self (persoonsgebonden, postvak STIL): elke 14 dagen, maximaal 3 keer
  'VERPLICHT_CERTIFICAAT_NIET_AANGELEVERD',   // -> verplichte-certificaten.hr+tl (postvak ITEM, gebundeld): herinneringen op, of geen portal-toegang
  'VERPLICHTE_CURSUS_NIET_GEPLAND',           // -> all-hr (postvak STIL) plus de eigen teamleiders: de weekbundel van maandag 08:00; bronEntiteitId = 'week-<jjjj-Www>'
  'VERPLICHT_CERTIFICAAT_NOG_NIET_GEVOLGD',   // -> verplichte-certificaten.hr+tl (postvak ITEM, gebundeld): de medewerker heeft de cursus nog niet gevolgd
  // -- batch-71 ouderschap-verlof (FO-539 §8) --
  'OUDERSCHAP_VERVAL_GECORRIGEERD',       // -> all-hr (postvak ITEM): bundel van een correctierun; bronEntiteitId = runId
  'OUDERSCHAP_VERLOPEN',                  // -> employee.self (postvak STIL, alleen in de app): termijn verstreken, < 12 maanden geleden; bronEntiteitId = Employee.id
  'OUDERSCHAP_BESCHERMD_VOORBIJ_TERMIJN', // -> all-hr (postvak STIL): dagelijkse bundel van de HR-lijst; bronEntiteitId = 'ouderschap-verval-<jjjj-mm-dd>'
] as const
export type NotificatieEventTypeKey = (typeof NOTIFICATIE_EVENT_TYPE_KEY)[number]

export const NOTIFICATIE_EVENT_LABELS: Record<NotificatieEventTypeKey, string> = {
  WERKBON_TOEGEWEZEN:                 'Werkbon aan jou toegewezen',
  WERKBON_DAG_VERPLAATST:             'Werkbon-dag aan jou toegewezen',
  WERKBON_TIJDEN_GEWIJZIGD:           'Werkbon-tijden gewijzigd',
  WERKBON_DAG_VERWIJDERD:             'Werkbon-dag uit jouw planning gehaald',
  VERLOF_INGEDIEND:                   'Verlof ingediend (te beoordelen)',
  VERLOF_GOEDGEKEURD:                 'Verlof goedgekeurd',
  VERLOF_AFGEKEURD:                   'Verlof afgekeurd',
  // Spec 34 — verlof-intrekking 2-staps-keten
  VERLOF_INTREKKING_AANGEVRAAGD:      'Verlof-intrekking aangevraagd (te beoordelen)',
  VERLOF_INTREKKING_TL_GOEDGEKEURD:   'Verlof-intrekking - TL akkoord (HR saldo-controle)',
  VERLOF_INGETROKKEN:                 'Verlof ingetrokken',
  VERLOF_INTREKKING_AFGEWEZEN:        'Verlof-intrekking afgewezen',
  VERZUIM_GEMELD:                     'Ziekmelding ontvangen',
  VERZUIM_HERSTELD:                   'Betermelding ontvangen',
  VERZUIM_LANGDURIG:                  'Ziekmelding ≥7 dagen - actie vereist',
  DECLARATIE_INGEDIEND:               'Declaratie ingediend (te beoordelen)',
  DECLARATIE_GOEDGEKEURD:             'Declaratie goedgekeurd',
  DECLARATIE_AFGEKEURD:               'Declaratie afgekeurd',
  DECLARATIE_UITBETAALD:              'Declaratie uitbetaald',
  MUTATIEVERZOEK_INGEDIEND:           'Mutatieverzoek ingediend (te beoordelen)',
  MUTATIEVERZOEK_BESLIST:             'Mutatieverzoek beslist',
  KLANTPORTAAL_AANVRAAG_BINNENGEKOMEN:'Nieuwe klant-aanvraag binnen',
  UREN_INVULLEN_REMINDER:             'Vergeet je uren niet in te vullen',
  COMMUNICATIE_BERICHT_NIEUW:         'Nieuw bericht in communicatie',
  ONBOARDING_HERINNERING:             'Onboarding-herinnering - vul je open stappen in',
  TAAK_URGENT:                        'Urgente taak voor jou',
  TARGETING_LEAD_STATUS_CHANGED:      'Status van lead is gewijzigd',
  MATERIEEL_APK_BINNENKORT:           'APK voertuig verloopt binnenkort',
  MATERIEEL_TERUGROEPACTIE:           'Openstaande terugroepactie (recall) voertuig',
  // batch-59 voertuig-afspraken (FO-435 + FO-436)
  VOERTUIG_AFSPRAAK_TE_PLANNEN:       'Voertuig-afspraak nog te plannen',
  VOERTUIG_AFSPRAAK_MORGEN:           'Voertuig-afspraak morgen',
  VOERTUIG_AFSPRAAK_NIET_AFGEROND:    'Voertuig-afspraak verstreken - nagekomen?',
  VOERTUIG_TERUGROEP_HEROPEND:        'Terugroepactie weer openstaand bij RDW',
  VOERTUIG_TERUGROEP_VOORSTEL_OPEN:   'Terugroep-melding van berijder wacht op bevestiging',
  FORMULIER_INGEDIEND:                'Nieuw formulier ingediend',
  REISUREN_AFWIJKING_INGEDIEND:       'Reisuren-afwijking ingediend (te beoordelen)',
  // PR-B (spec 18)
  CERTIFICAAT_VERLOOPT_BINNENKORT:    'Certificaat verloopt binnenkort',
  CERTIFICAAT_VERLOPEN:               'Certificaat is verlopen',
  CONTRACT_VERLOOPT_BINNENKORT:       'Contract verloopt binnenkort',
  CONTRACT_VERLOPEN:                  'Contract is verlopen',
  VERJAARDAG_VANDAAG:                 'Verjaardag vandaag',
  PLANNING_GEWIJZIGD:                 'Planning is gewijzigd',
  WERKBON_INGEDIEND:                  'Werkbon ingediend (te beoordelen)',
  WERKBON_GOEDGEKEURD:                'Werkbon goedgekeurd',
  WERKBON_AFGEKEURD:                  'Werkbon afgekeurd',
  PROEFTIJD_VERLOOPT:                 'Proeftijd verloopt binnenkort',
  VOERTUIG_KEURING_VERLOPEN:          'Voertuigkeuring verlopen',
  RIJBEWIJS_VERLOOPT:                 'Rijbewijs verloopt binnenkort',
  // PR-B follow-up (spec 18) — events voor PR-J/L/M/K
  REISUREN_AFWIJKING_GOEDGEKEURD:               'Reisuren-afwijking goedgekeurd',
  REISUREN_AFWIJKING_AFGEKEURD:                 'Reisuren-afwijking afgekeurd',
  // PR-L gebruikt uitgebreidere labels (spec-correct, gebruikersvriendelijker).
  KEUZEBUDGET_AANVRAAG_INGEDIEND:               'Keuzebudget-aanvraag ingediend (te beoordelen)',
  KEUZEBUDGET_AANVRAAG_GOEDGEKEURD_TEAMLEIDER:  'Keuzebudget-aanvraag akkoord door teamleider',
  KEUZEBUDGET_AANVRAAG_GOEDGEKEURD_HR:          'Keuzebudget-aanvraag definitief goedgekeurd',
  KEUZEBUDGET_AANVRAAG_AFGEKEURD:               'Keuzebudget-aanvraag afgekeurd',
  GESPREK_AANGEVRAAGD:                          'Gesprek aangevraagd',
  GESPREK_INGEPLAND:                            'Gesprek ingepland',
  GESPREK_AFGEROND:                             'Gesprek afgerond',
  GESPREK_GEANNULEERD_DOOR_MEDEWERKER:          'Gesprek geannuleerd door medewerker',
  GESPREK_GEANNULEERD_DOOR_ADMIN:               'Gesprek geannuleerd door admin',
  FACTUUR_INGEDIEND:                            'Factuur ingediend',
  // PR-B1 (spec verzuim-workflow-accordering §6.2)
  VERZUIM_WACHT_OP_ACCORDERING:    'Ziekmelding wacht op accordering',
  VERZUIM_GEACCORDEERD:            'Ziekmelding geaccordeerd',
  VERZUIM_GEACCORDEERD_MEDEWERKER: 'Je ziekmelding is bevestigd',
  VERZUIM_AFGEKEURD:               'Ziekmelding afgekeurd',
  VERZUIM_AFGEKEURD_HR:            'Ziekmelding afgekeurd (ter info)',
  VOORRAAD_LAAG:                                'Voorraad laag',
  KARREN_CONTROLE_VERVALDATUM:                  'Kar/koffer controle vervalt',
  KARREN_MELDING_ONTBREEKT:                     'Onderdeel gemeld als ontbrekend',
  KARREN_MELDING_KAPOT:                         'Onderdeel gemeld als kapot',
  KARREN_BESTELLING_ONTVANGEN:                  'Bestelling ontvangen - koppel terug naar kar',
  INTEGRATIE_EXPIRY_NABIJ:                      'Integratie-API-key verloopt binnenkort',
  // Track-I (klantenportaal-audit)
  KLANTPORTAAL_AANVRAAG_AFGEWEZEN:              'Klant-aanvraag afgewezen',
  KLANTPORTAAL_AANVRAAG_AFGEROND:               'Klant-aanvraag afgerond',
  KLANTPORTAAL_WERKBON_GEACCORDEERD:            'Klant heeft werkbon geaccordeerd',
  KLANTPORTAAL_WERKBON_GEWEIGERD:               'Klant heeft werkbon geweigerd',
  KLANTPORTAAL_BEOORDELING_KLAARGEZET:          'Klant-beoordeling klaargezet',
  KLANTPORTAAL_BEOORDELING_LAGE_SCORE:          'Lage score in klant-beoordeling',
  // Verjaardag-keuze (januari-campagne)
  VERJAARDAG_KEUZE_START:                       'Verjaardag - maak je keuze',
  VERJAARDAG_KEUZE_HERINNERING:                 'Verjaardag-keuze - herinnering',
  VERJAARDAG_KEUZE_DEADLINE:                    'Verjaardag-keuze - laatste kans',
  VERJAARDAG_KEUZE_BEVESTIGD:                   'Verjaardag-keuze bevestigd',
  VERJAARDAG_VRIJ_OMPLANNEN:                    'Medewerker vrij op verjaardag - controleer planning',
  // Vakantie-campagne
  CAMPAGNE_EERSTE_MELDING:                      'Vakantie-campagne - eerste melding',
  CAMPAGNE_HERINNERING:                         'Vakantie-campagne - herinnering',
  CAMPAGNE_DEADLINE:                            'Vakantie-campagne - deadline',
  CAMPAGNE_BEOORDELING_OPEN:                    'Vakantie-campagne - beoordeling open',
  CAMPAGNE_GOEDGEKEURD:                         'Vakantie-aanvraag goedgekeurd',
  CAMPAGNE_AFGEKEURD:                           'Vakantie-aanvraag afgekeurd',
  CAMPAGNE_AFGEWEZEN:                           'Vakantie-aanvraag afgewezen',
  CAMPAGNE_WACHTLIJST:                          'Vakantie-aanvraag op wachtlijst',
  // Medewerker-app (PR #174)
  WEEKSTAAT_INGEDIEND:                          'Weekstaat ingediend ter accordering',
  WEEKSTAAT_GOEDGEKEURD:                        'Weekstaat goedgekeurd',
  WEEKSTAAT_AFGEKEURD:                          'Weekstaat afgekeurd',
  DAG_UREN_HERINNERING:                         'Herinnering: vul je uren in',
  DOCUMENT_ONDERTEKEND:                         'Document ondertekend',
  DOCUMENT_WACHT_OP_HANDTEKENING:               'Document wacht op handtekening',
  DOCUMENT_DEADLINE_NADERT:                     'Document-deadline nadert',
  DOCUMENT_ZICHTBAAR_VOOR_MEDEWERKER:           'Nieuw document in je portal',
  DOCUMENT_LEESBEVESTIGING_VEREIST:             'Document vereist je leesbevestiging',
  PLANNING_WIJZIGING_INGEDIEND:                 'Planningswijziging ingediend',
  PLANNING_WIJZIGING_GOEDGEKEURD:               'Planningswijziging goedgekeurd',
  PLANNING_WIJZIGING_AFGEKEURD:                 'Planningswijziging afgekeurd',
  // Spec A §SS18 — werkbon-modificatie + reistijd-override
  WERKBON_MEDEWERKER_VERVANGEN:                 'Je bent vervangen op een werkbon',
  WERKBON_EXTRA_INZET:                          'Extra inzet bij klant',
  REISTIJD_OVERRIDE:                            'Reistijd handmatig aangepast',
  WERKBON_SCOPE_WIJZIGING:                      'Werkbon-planning gewijzigd (bulk)',
  // Spec C §4.4 — contactverzoeken
  CONTACT_VERZOEK_NIEUW:                        'Nieuw contactverzoek',
  CONTACT_VERZOEK_GELEZEN:                      'Je contactverzoek is gezien',
  CONTACT_VERZOEK_INGEPLAND:                    'Contactverzoek ingepland',
  CONTACT_VERZOEK_AFGEHANDELD:                  'Contactverzoek afgerond',
  CONTACT_VERZOEK_GEANNULEERD:                  'Contactverzoek geannuleerd',
  CONTACT_VERZOEK_URGENT:                       'URGENT contactverzoek',
  // Spec D §14 — opleidings-events
  OPLEIDING_AANGEVRAAGD:                        'Opleidingsaanvraag ingediend',
  OPLEIDING_AANVRAAG_GOEDGEKEURD:               'Opleidingsaanvraag goedgekeurd',
  OPLEIDING_AANVRAAG_AFGEWEZEN:                 'Opleidingsaanvraag afgewezen',
  OPLEIDING_INGEPLAND:                          'Opleiding ingepland',
  OPLEIDING_HERINNERING:                        'Herinnering: opleiding morgen',
  OPLEIDING_AFGEROND:                           'Opleiding afgerond',
  OPLEIDING_GEANNULEERD:                        'Opleiding geannuleerd',
  OPLEIDING_HERHALING_NODIG:                    'Herhalingsopleiding nodig',
  OPLEIDING_SESSIE_AFRONDEN:                    'Opleidingssessie afronden of herplannen',
  // Ronde 4 W4 — verplichte werkbon-notificaties (user-voorkeuren bypass)
  WERKBON_HEROPEND_VERPLICHT:                   'Werkbon heropend door teamleider (verplicht)',
  WERKBON_GECORRIGEERD_VERPLICHT:               'Werkbon-uren gecorrigeerd door teamleider (verplicht)',
  // Ronde 4 W7 — signalering-engine event-types (spec ronde-4/07 §1.8)
  PLANNING_OVERLAP:                             'Planningconflict: werktijden botsen',
  PLANNING_REISTIJD_CONFLICT:                   'Planningconflict: reistijd te krap',
  PLANNING_MAX_UREN_OVERSCHREDEN:               'Arbo: >12 uur ingepland',
  PLANNING_RUSTTIJD_ONVOLDOENDE:                'Arbo: <11 uur rusttijd',
  PLANNING_REISTIJD_LANG:                       'Lange reistijd (>1,5 uur)',
  PLANNING_MEDEWERKER_NIEUW_BIJ_KLANT:          'Medewerker nieuw bij klant',
  PLANNING_PROFIEL_MISMATCH:                    'Profiel-mismatch met klant-eis',
  PLANNING_BV_MISMATCH:                         'BV-mismatch medewerker ↔ klant',
  VERZUIM_EINDE_DAG_CHECK:                      'Verzuim: dagelijkse beter-meld check',
  VERZUIM_GEEN_TL_CONTACT:                      'Verzuim: geen contact met teamleider',
  VERZUIM_5_WERKDAGEN:                          'Verzuim: >5 werkdagen ziek',
  VERZUIM_FREQUENT:                             'Verzuim: frequent verzuim',
  VERZUIM_OPENSTAANDE_DIENST:                   'Verzuim: geplande dienst open',
  VERZUIM_VERVANGING_NODIG:                     'Verzuim: vervanging nodig',
  // batch-40 uren-toeslag
  ROOSTER_CLASSIFICATIE_VERDACHT:               'Rooster: toeslagclassificatie verdacht',
  SAMENLOOP_ZIEKTE_VERLOF:                      'Verzuim: samenloop ziekte en verlof',
  VERLOF_HOOG_PERCENTAGE_VRIJ:                  'Verlof: hoog percentage tegelijk vrij',
  VERLOF_KORT_VOORAF:                           'Verlof: kort vooraf aangevraagd',
  VERLOF_LANG_AANEENGESLOTEN:                   'Verlof: lange aaneengesloten periode',
  VERLOF_SALDO_DREIGT_NEGATIEF:                 'Verlof: saldo dreigt negatief',
  VERLOF_ATV_VERVALT_BINNENKORT:                'Verlof: ATV vervalt binnenkort',
  VERLOF_BOVENWETTELIJK_VERVALT:                'Verlof: bovenwettelijk verlof vervalt',
  UREN_NIET_INGEVULD_HERINNERING_2:             'Uren: tweede herinnering',
  UREN_TL_MELDING_NIET_INGEVULD:                'Uren: medewerker vult structureel niet in',
  WERKBON_WEEK_NIET_GEREED:                     'Werkbon niet gereed na week',
  WERKBON_NIET_GEREEDGEMELD:                    'Werkbon nog niet gereedgemeld',
  WERKBON_NIET_FACTURABEL_ZONDER_REDEN:         'Werkbonnen niet te factureren zonder reden (dagcontrole)',
  SYSTEEM_API_FAILURE:                          'Systeem: API-fout',
  SYSTEEM_SYNC_PROBLEEM:                        'Systeem: synchronisatie-probleem',
  SYSTEEM_VALIDATIE_FOUT:                        'Beheer-signaal: meldingsconfiguratie',
  // Ronde 4 W8 — capaciteits-signalen (spec ronde-4/08 §2.2)
  CAPACITEIT_OVERBELAST:                         'Capaciteit: medewerker overbelast',
  CAPACITEIT_BIJNA_VOL:                          'Capaciteit: medewerker bijna vol',
  CAPACITEIT_ONDERBENUT:                         'Capaciteit: medewerker onderbenut',
  CAPACITEIT_STRUCTUREEL_OVERWERK:               'Capaciteit: structureel overwerk',
  CAPACITEIT_KOSTEN_LEEGLOOP:                    'Capaciteit: hoge leegloopkosten',
  TAAK_DAILY_DIGEST:    'Dagelijkse taken-samenvatting',
  BULK_WIJZIGING_DIGEST:'Meerdere wijzigingen samengevat',
  DAGTAAK_VAAK_DOORGESCHOVEN: 'Dagtaak vaak doorgeschoven',
  VEILIGHEID_ONVEILIGE_SITUATIE_GEMELD: 'Onveilige situatie gemeld',
  WERKBON_WACHT_OP_INTERN: 'Werkbon wacht op goedkeuring',
  // Felicitaties + jubilea (B6 / C8 / C9)
  VERJAARDAG_FELICITATIE:                       'Gefeliciteerd met je verjaardag',
  DIENSTJAAR_FELICITATIE:                       'Gefeliciteerd met je dienstverband',
  DIENSTJUBILEUM_AANKOMEND:                     'Aankomend dienstjubileum',
  DIENSTJUBILEUM_VANDAAG:                       'Dienstjubileum vandaag',
  VERJAARDAG_VOORAF_HRTL:                       'Medewerker morgen jarig',
  // Vervanging bij verlof (B6)
  VERVANGING_INTERN_NODIG:                      'Vervanging nodig (intern)',
  VERVANGING_KLANT_REACTIE:                     'Klantreactie op vervanging',
  KLANT_AFWEZIGHEID_REACTIE:                    'Klantreactie op afwezigheid',
  KLANT_MONTEUR_FEEDBACK:                       'Klantfeedback op monteur',
  KLANT_DOCUMENT_REACTIE:                       'Klantreactie op werkbon/factuur',
  KLANT_OFFERTE_BESLUIT:                        'Klantbesluit op offerte',
  KLANT_STORING_MELDING:                        'Storingsmelding van klant',
  // Spec 30 Fase 8A — voorraad uniek-item cron-checks
  VOORRAAD_KEURING_VERLOOPT:                    'Voorraad: keuring verloopt binnenkort of verlopen',
  VOORRAAD_PBM_VERVANG:                         'Voorraad: PBM houdbaarheid of vervangdatum nadert',
  VOORRAAD_NIET_RETOUR:                         'Voorraad: item niet tijdig teruggebracht',
  VOORRAAD_SLEUTEL_TE_LANG_UIT:                 'Voorraad: autosleutel te lang uitgegeven',
  VOORRAAD_SIM_ZONDER_MEDEWERKER:               'Voorraad: simkaart zonder gekoppelde medewerker',
  VOORRAAD_ITEM_VERMIST:                        'Voorraad: item vermist',
  // Ongevallen-/incidentenregistratie (Ongevallen-module §7)
  ONGEVAL_GEREGISTREERD:                        'Ongeval geregistreerd',
  ONGEVAL_ARBEIDSINSPECTIE_VEREIST:            'Ongeval meldingsplichtig - Arbeidsinspectie',
  // Spec 33 fase 3 — min-uren verzoek ingediend (te beoordelen)
  WEEKSTAAT_MINUREN_INGEDIEND:                 'Weekstaat: min-uren ingediend (te beoordelen)',
  // Loonstroken-module (spec B, stap 4a)
  LOONSTROOK_KLAARGEZET:                        'Je loonstrook staat klaar',
  LOONSTROOK_UPLOAD_HERINNERING:                'Loonstroken moeten geüpload worden',
  // #28 Deel 4 — correctieverzoek/vraag bij een loonstrook
  LOONSTROOK_VRAAG:                             'Vraag of correctieverzoek loonstrook',
  // ── NHG werkgeversverklaring (lane O1) ─────────────────────────────────────
  WERKGEVERSVERKLARING_AANGEVRAAGD:            'Nieuwe aanvraag werkgeversverklaring',
  WERKGEVERSVERKLARING_VERSTREKT:              'Je werkgeversverklaring staat klaar',
  // ── Operationele takenmodule (spec 34 §4 + §11) ───────────────────────────
  TAAK_TOEGEWEZEN:                             'Taak aan jou toegewezen',
  TAAK_HOOG_GEPLAND:                           'Taak met hoge prioriteit gepland',
  TAAK_NIET_UITGEVOERD_HOOG:                   'Taak niet uitgevoerd - hoge prioriteit',
  TAAK_2X_NIET_UITGEVOERD:                     'Taak 2x niet uitgevoerd',
  TAAK_PRIO_VERHOOGD:                          'Taak-prioriteit verhoogd',
  TAAK_DEADLINE_VANDAAG:                       'Taak-deadline vandaag',
  TAAK_DEADLINE_VERLOPEN:                      'Taak-deadline verlopen',
  TAAK_HERINNERING_EINDE_DAG:                  'Herinnering: openstaande taak',
  TAAK_UITVOERDER_UIT_DIENST:                  'Taak terug naar open: uitvoerder uit dienst',
  TAAK_GEREED_GEMELD:                          'Taak gereedgemeld',
  // Batch-6 hrm — HRM gespreksagenda
  HR_GESPREK_GEPLAND:                          'Gesprek ingepland',
  HR_GESPREK_VERZET:                           'Gesprek verzet',
  HR_GESPREK_GEANNULEERD:                      'Gesprek geannuleerd',
  HR_GESPREK_HERINNERING:                      'Herinnering: gesprek morgen',
  // Spec 35 — Vertrouwenspersoon-module
  VP_VERVOLGSTAP_HERINNERING:                  'Herinnering: openstaande vervolgstap',
  VP_ROL_GEWIJZIGD:                            'Je VP-rol is gewijzigd',
  // Batch-6 verlof — ouderschapsverlof + seniorendagen
  KIND_TOEGEVOEGD:                             'Kind toegevoegd (gezin) - controleren',
  OUDERSCHAPSVERLOF_RECHT_BESCHIKBAAR:         'Ouderschapsverlof-recht beschikbaar',
  OUDERSCHAPSVERLOF_BETAALD_VERLOOPT:          'Betaald ouderschapsverlof verloopt binnenkort',
  OUDERSCHAPSVERLOF_UWV_AANVRAAG_NODIG:        'UWV-aanvraag nodig voor betaald ouderschapsverlof',
  OUDERSCHAPSVERLOF_SALDO_BIJNA_OP:            'Ouderschapsverlof-saldo bijna op',
  SENIORENDAGEN_CONCEPT_KLAAR:                 'Seniorendagen-conceptmutaties te beoordelen',
  SENIORENDAGEN_KEUZE_NODIG:                   'Keuze nodig: seniorendagen opnemen of uitbetalen',
  SENIORENVERLOF_TOEGEKEND:                    'Seniorenverlof toegekend',
  SENIORENDAGEN_BIJNA_GERECHTIGD:              'Medewerker bereikt binnenkort de seniorenregeling',
  // Spec 42 — verlofverval 640a.
  VERLOF_VERVAL_WAARSCHUWING:                  'Verlof vervalt binnenkort',
  VERLOF_VERVAL_GEBLOKKEERD:                   'Verlofverval geblokkeerd: handmatig afhandelen',
  // Batch-6 cert — certificaten-opvolgflow
  CERTIFICAAT_WACHT_OP_UPLOAD:                 'Certificaat wacht op upload',
  CERTIFICAAT_NIET_OPGEPAKT:                   'Verlopen certificaat niet opgepakt',
  CERTIFICAAT_TOEGEVOEGD:                      'Certificaat toegevoegd',
  GEPLAND_ZONDER_GELDIG_CERTIFICAAT:           'Gepland zonder geldig certificaat',
  // FO-309 P4 (batch-34) — cursist-gerichte certificaat-upload
  CERTIFICAAT_UPLOAD_GEVRAAGD:                 'Upload je certificaat',
  CERTIFICAAT_UPLOAD_HERINNERING:              'Herinnering: upload je certificaat',
  // batch-54 certificaten (FO-408)
  CERTIFICAAT_CONTROLE_GEVRAAGD:               'Certificaten wachten op controle',
  CERTIFICAAT_CONCEPT_BEVESTIGD:               'Je certificaat is bevestigd',
  CERTIFICAAT_CONCEPT_AFGEWEZEN:               'Je certificaat is afgewezen',
  // Batch-7 recruitment
  ACQUISITIE_CHECKIN_VANDAAG:                  'Check-in met lead vandaag',
  RECRUITMENT_NIEUW_ONOPGEVOLGD:               'Nieuwe kandidaat niet opgevolgd',
  RECRUITMENT_NABELLEN_VANDAAG:                'Kandidaat vandaag nabellen',
  RECRUITMENT_GESPREK_MORGEN:                  'Kandidaat-gesprek morgen',
  RECRUITMENT_AANBOD_VERLOOPT:                 'Aanbieding verloopt morgen',
  RECRUITMENT_AANBOD_GEEN_REACTIE:             'Geen reactie op aanbieding',
  RECRUITMENT_TALENTPOOL_HERBEOORDELING:       'Talentpool-kandidaat herbeoordelen',
  RECRUITMENT_AFWIJZING_NIET_AFGEROND:         'Afwijzing nog niet afgerond',
  RECRUITMENT_BEOORDELING_HERINNERING: 'Recruiterbeoordeling nog niet afgerond',
  RECRUITMENT_BEOORDELING_ESCALATIE: 'Recruiterbeoordeling over de deadline',
  RECRUITMENT_SECOND_REVIEW_GEVRAAGD: 'Second review gevraagd',
  RECRUITMENT_ADVIES_AFWIJKING: 'Beoordelaar wijkt af van het recruiteradvies',
  RECRUITMENT_INFO_VERZOEK: 'Aanvullende informatie gevraagd over een kandidaat',
  RECRUITMENT_BESLUIT_GEVRAAGD: 'Besluit over een kandidaat gevraagd',
  REISTIJD_AFWIJKING_SIGNAAL:                  'Reistijd wijkt structureel af van de standaard',
  IDEE_INGEDIEND:                              'Idee ingediend',
  ONBOARDING_CERT_DEADLINE_NADERT:             'Verplicht certificaat nadert de deadline',
  ONBOARDING_CERT_DEADLINE_VERLOPEN:           'Verplicht certificaat over de deadline',
  // FO-016 (batch-14) — ideeenbox
  IDEE_STATUS_GEWIJZIGD:                       'Je inzending heeft een nieuwe status',
  IDEE_REACTIE_TER_MODERATIE:                  'Reactie op een voorstel wacht op je akkoord',
  IDEE_VOORSTEL_GEPUBLICEERD:                  'Nieuw openbaar voorstel',
  INSPECTIE_VERVALDATUM_NADERT:                'Inspectie verloopt binnenkort',
  INSPECTIE_VERVALDATUM_VERLOPEN:              'Inspectie-vervaldatum verstreken',
  GESPREKSVERSLAG_TE_MAKEN:                    'Gespreksverslag maken',
  GESPREKSVERSLAG_KLAARGEZET:                  'Gespreksverslag klaargezet',
  GESPREKSVERSLAG_REACTIE:                     'Reactie op gespreksverslag',
  // FO-309 P3 (batch-34) — verslag-termijn + afspraak-deadline
  GESPREKSVERSLAG_TERMIJN_HERINNERING:         'Gespreksverslag: termijn nadert',
  GESPREKSVERSLAG_TERMIJN_ESCALATIE:           'Gespreksverslag: termijn overschreden',
  GESPREKSVERSLAG_MEDEWERKER_HERINNERING:      'Herinnering: teken of reageer op je verslag',
  GESPREKSVERSLAG_AFSPRAAK_DEADLINE:           'Afspraak uit gespreksverslag nadert deadline',
  // batch-39 gesprekken (FO-332)
  GESPREKSVERSLAG_ACTIEPUNT_TOEGEWEZEN:        'Nieuw actiepunt uit een gespreksverslag',
  GESPREKSVERSLAG_VERVOLG_PLANNEN:             'Vervolggesprek in te plannen',
  GESPREKSVERSLAG_ACTIEPUNT_ESCALATIE:         'Actiepunt-houder uit dienst - actie nodig',
  GESPREKSVERSLAG_STILZWIJGEND_AANGEKONDIGD:   'Gespreksverslag: stilzwijgend akkoord nadert',
  GESPREKSVERSLAG_STILZWIJGEND_VASTGESTELD:    'Gespreksverslag: stilzwijgend akkoord vastgesteld',
  // batch-47 gespreksverslagen (spec 90 §10)
  GESPREKSVERSLAG_TER_CONTROLE:                'Gespreksverslag ter controle',
  GESPREKSVERSLAG_TER_CONTROLE_TERUG:          'Gespreksverslag teruggestuurd ter aanpassing',
  GESPREKSVERSLAG_ONDERTEKENING_VERZOEK:       'Gespreksverslag: jouw handtekening gevraagd',
  GESPREKSVERSLAG_ONDERTEKENING_GEWEIGERD:     'Gespreksverslag: ondertekening geweigerd',
  GESPREKSVERSLAG_VOLLEDIG_ONDERTEKEND:        'Gespreksverslag volledig ondertekend',
  GESPREKSVERSLAG_GELEZEN:                     'Gespreksverslag gelezen door de medewerker',
  GESPREKSVERSLAG_TERMIJN_TL_ESCALATIE:        'Gespreksverslag: termijn loopt, teamleider inschakelen',
  GESPREKSVERSLAG_VERLOPEN:                    'Gespreksverslag: termijn verstreken',
  GESPREKSVERSLAG_ADDENDUM_TOEGEVOEGD:         'Correctieblad bij je gespreksverslag',
  // FO-058 (batch-18) — onderhoud-controle + mail-automatisering.
  ONDERHOUD_CONTROLE_AFWIJKING:                'Onderhoudsplanning wijkt af',
  MAIL_AUTOMATISERING_CONCEPT:                 'Automatische mail klaar ter controle',
  MAIL_AUTOMATISERING_GEBLOKKEERD:             'Automatische mail geblokkeerd (data ontbreekt)',
  MAIL_AUTOMATISERING_VERSTUURD:               'Automatische mail bezorgd',
  MIDDELEN_RETOURCHECK:                        'Retourcheck middelen bij uitdienst',
  CONTRACT_VERLOOP:                            'Contract verloopt (bijna)',
  IMPORT_AFGEROND:                             'Achtergrond-import afgerond',
  ABONNEMENT_VERLOOP:                          'Abonnement nadert opzegtermijn / verloopt',
  // FO-144 (batch-28) RI&E
  RIE_MAATREGEL_DEADLINE_NADERT:               'RI&E-maatregel: deadline nadert',
  RIE_MAATREGEL_DEADLINE_VERLOPEN:             'RI&E-maatregel: deadline verstreken',
  RIE_HERZIENING_NADERT:                       'RI&E-herziening nadert',
  // FO-146 (batch-28) — proactieve melding bij een niet-bezorgde mail.
  MAIL_NIET_BEZORGD:                           'Mail niet bezorgd',
  ABONNEMENT_INDEXERING_SAMENVATTING:          'Abonnementen geïndexeerd (jaarlijkse samenvatting)',
  ABONNEMENT_FACTUUR_AFWIJKING:                'Abonnementsfactuur wijkt af van het afgesproken bedrag',
  CONSIGNATIE_RUIL_AANGEVRAAGD:                'Ruilverzoek consignatie ontvangen',
  CONSIGNATIE_RUIL_COLLEGA_GEREAGEERD:         'Collega reageerde op ruilverzoek',
  CONSIGNATIE_RUIL_BESLIST:                    'Beslissing op je ruilverzoek',
  CATALOGUS_SCHEEFSTAND:                       'Gereedschap-catalogus scheefstand',
  // FO-322 (batch-36) — AVG-toestemmingen
  TOESTEMMING_OPNIEUW_BEANTWOORDEN:            'Privacy-toestemming opnieuw te beantwoorden',
  TOESTEMMING_HERINNERING:                     'Herinnering: privacy-voorkeuren invullen',
  // FO-323 (batch-36) — NEN 3140-aanwijzingen
  NEN3140_AANWIJZING_VERLOOPT:                 'NEN 3140-aanwijzing verloopt binnenkort',
  NEN3140_AANWIJZING_VERLOPEN:                 'NEN 3140-aanwijzing verlopen',
  NEN3140_CERTIFICAAT_STEUNT_VERLOOPT:         'Aanwijzing steunt op verlopend certificaat',
  NEN3140_SKILL_VOORSTEL:                      'Aanwijzing actief - skill bijwerken?',
  NEN3140_TEKENEN_VERZOCHT:                    'Je NEN 3140-aanwijzing wacht op ondertekening',
  NEN3140_KLANTAUTORISATIE_VERLOOPT:           'NEN 3140-klantautorisatie verloopt',
  NEN3140_KLANTAUTORISATIE_VERLOPEN:           'NEN 3140-klantautorisatie verlopen',
  NEN3140_KLANTAUTORISATIE_INGETROKKEN:        'NEN 3140-klantautorisatie ingetrokken',
  NEN3140_BEOORDEEL_VOOR_AANWIJZING:           'Beoordeel voor een NEN 3140-aanwijzing',
  NEN3140_PLANNING_OVERRULE:                   'NEN 3140-vereiste overruled bij plannen',
  // batch-37 onderhoudsrapport (FO-327)
  VOERTUIG_ONDERHOUD_BINNENKORT:               'Voertuig-onderhoud binnenkort nodig',
  VOERTUIG_ONDERHOUD_ACHTERSTALLIG:            'Voertuig-onderhoud achterstallig',
  VOERTUIG_ONDERHOUD_RAPPORT_STILTE:           'Geen onderhoudsrapport ontvangen',
  VOERTUIG_ONDERHOUD_INGEST_FOUT:              'Onderhoudsrapport niet verwerkt (fout)',
  // FO-326 (batch-37) — schadeformulier
  SCHADEMELDING_NIEUW:                         'Nieuwe schademelding (aanrijdingsformulier)',
  SCHADEMELDING_BLAD2_HERINNERING:             'Herinnering: blad 2 van je schademelding invullen',
  SCHADEMELDING_STATUS_UPDATE:                 'Status van je schademelding gewijzigd',
  SCHADEMELDING_GEGEVENS_ONTBRAKEN:            'Gegevens ontbraken bij schademelding',
  // batch-38 garagefacturen (FO-329)
  GARAGE_ONDERHOUD_WERKVOORRAAD:               'Onderhoudsfactuur te koppelen aan voertuig',
  // batch-39 onboarding (FO-337)
  ONBOARDING_CONFIGURATIEFOUT:                 'Onboarding-stap niet invulbaar (configuratie)',
  // batch-40 status (FO-340 §13.4) — medewerkerstatus in twee lagen.
  MEDEWERKER_NON_ACTIEF_GEZET:                 'Medewerker op non-actief gezet',
  MEDEWERKER_NON_ACTIEF_VERLOPEN:              'Non-actief-periode verlopen (weer inzetbaar)',
  MEDEWERKER_STATUS_DRIFT:                     'Statusafwijking gecorrigeerd',
  DIENSTVERBAND_VENSTER_AFWIJKING:             'Dienstverband en datums spreken elkaar tegen',
  // batch-40 meldingen (FO-345 + FO-354).
  VERZUIM_DOELGROEP_LEEG:                      'Geen ontvangers voor verzuimmeldingen',
  LOONSTROKEN_TE_BEOORDELEN:                   'Loonstroken te beoordelen',
  LOONSTROKEN_DOELGROEP_LEEG:                  'Geen ontvangers voor loonstrookmeldingen',
  MALWARE_BESTAND_GEVONDEN:                    'Malware-bestand in mail-ingest gevonden',
  // batch-41 dossier (FO-355 + FO-358).
  DOSSIER_ONVOLLEDIG:                          'Dossier onvolledig',
  DOSSIER_ONVOLLEDIG_HERINNERING:              'Herinnering: dossier onvolledig',
  DOSSIER_NIET_AANGEVULD:                      'Dossier langdurig niet aangevuld (escalatie)',
  INFORMATIE_OPGEVRAAGD:                       'Informatie opgevraagd voor je dossier',
  INFORMATIE_VERZOEK_HERINNERING:              'Herinnering: informatie-verzoek open',
  INFORMATIE_VERZOEK_ONBEANTWOORD:             'Informatie-verzoek onbeantwoord',
  INFORMATIE_VERZOEK_AFGEROND:                 'Informatie-verzoek afgerond',
  // batch-42 onboarding (FO-369)
  ONBOARDING_GEBLOKKEERD:                      'Onboarding geblokkeerd (actie nodig)',
  ONBOARDING_VOORBIJ_START:                    'Onboarding voorbij startdatum',
  UITRUSTING_PICKLIJST:                        'Picklijst: uit voorraad klaarleggen',
  UITRUSTING_BESTELLING_TE_VERWERKEN:          'Bestelling verwerken (samenstellen)',
  UITRUSTING_BESTELLING_GOEDGEKEURD:           'Je bestelling is goedgekeurd',
  UITRUSTING_BESTELLING_AFGEKEURD:             'Je bestelling is afgekeurd',
  UITRUSTING_BESTELLING_VERWERKT:              'Je bestelling is verwerkt',
  UITRUSTING_BESTELLING_GELEVERD:              'Je bestelling is geleverd',
  UITRUSTING_BESTELLING_GEANNULEERD:           'Je bestelling is geannuleerd',
  POORTWACHTER_STAP_DEADLINE:                  'Poortwachter-termijn nadert',
  FREQUENT_VERZUIM_GESIGNALEERD:               'Frequent verzuim - verzuimgesprek voorstellen',
  INDEXATIE_HERINNERING_NODIG:                 'Jaarlijkse tarief-indexatie plannen',
  INKOOP_BUDGET_80:                            'Inkoopbudget 80% bereikt',
  INKOOP_BUDGET_OVERSCHREDEN:                  'Inkoopbudget overschreden',
  INKOOP_BUDGET_DRIFT:                         'Inkoopbudget-teller gecorrigeerd',
  INKOOP_MATCH_AFWIJKING:                      'Inkoopfactuur: match-afwijking',
  INKOOP_ONTVANGST_ZONDER_FACTUUR:             'Ontvangst zonder factuur',
  // batch-59 interne-facturen (FO-432).
  INTERNE_FACTUUR_PATROON_VOORSTEL:            'Interne facturen: vast patroon herkend',
  INTERNE_FACTUUR_HERHAALREGEL_GEPAUZEERD:     'Interne facturen: herhaal-regel gepauzeerd',
  // batch-63 facturen-bronregel (FO-471)
  FACTUURREEKS_AFWIJKING:                      'Factuurreeks: afwijking gesignaleerd',
  CONTRACT_AANZEGTERMIJN_KRITIEK:              'Aanzegtermijn verstrijkt - besluit + aanzegging nodig',
  CONTRACT_EINDE_OFFBOARDING:                  'Contract eindigt - start de uitdienst-flow',
  // FO-396 (batch-46) — offboarding-workflow (uitdienst).
  OFFBOARDING_STAP_DEADLINE_NADERT:            'Uitdienst: einddatum nadert, stappen open',
  OFFBOARDING_STAP_DEADLINE_VERLOPEN:          'Uitdienst: einddatum verstreken, stappen open',
  OFFBOARDING_VERLOF_NA_EINDDATUM:             'Uitdienst: goedgekeurd verlof na einddatum',
  OFFBOARDING_PLANNING_NA_EINDDATUM:           'Uitdienst: planning na einddatum',
  OFFBOARDING_ZIEKTE_BIJ_UITDIENST:            'Uitdienst: verzuim loopt door',
  OFFBOARDING_GRAPH_BLOKKADE_MISLUKT:          'Uitdienst: automatische accountblokkade mislukt',
  OFFBOARDING_DOCUMENT_VERSTREKT:              'Je uitdienst-document is beschikbaar',
  OFFBOARDING_OPEN_STAPPEN_OVERZICHT:          'Offboarding: stand van zaken (open stappen)',
  // batch-71 offboarding-inname (FO-535 §7)
  OFFBOARDING_SALARIS_AFMELDING_VERSTUURD:      'Offboarding: afmelding verstuurd naar de salarisadministratie',
  OFFBOARDING_SALARIS_AFMELDING_NIET_BEVESTIGD: 'Offboarding: afmelding niet bevestigd door de salarisadministratie',
  OFFBOARDING_EINDAFREKENING_VERSTUURD_NAW:     'Offboarding: eindafrekening verstuurd naar de salarisadministratie',
  OFFBOARDING_INNAME_CONFLICT:                  'Offboarding: inname-conflict tussen cockpit en innamelijst',
  VOERTUIG_APK_VERVAL:                         'APK voertuig verloopt of is verlopen',
  VOERTUIG_VERZEKERING_VERVAL:                 'Verzekering voertuig verloopt of is verlopen',
  VOERTUIG_LEASE_EINDE:                        'Leasecontract voertuig loopt af',
  VOERTUIG_RDW_UITGESCHREVEN:                  'Voertuig niet meer geregistreerd bij RDW',
  RELATIE_KVK_STATUS:                          'KVK meldt faillissement/opheffing van een relatie',
  // batch-59 onboarding-review (FO-437)
  RELATIE_ONBOARDING_TER_BEOORDELING:          'Relatie-onboarding ter beoordeling',
  RELATIE_ONBOARDING_REVIEW_HERINNERING:       'Relatie-onboarding wacht op beoordeling (herinnering)',
  RELATIE_ONBOARDING_KLANT_HERINNERING:        'Relatie-onboarding wacht op de klant (herinnering)',
  // FO-438 B2 (batch-60)
  KLANTPORTAAL_ERVARING_CACHE_GAT:             'Klantportaal: monteurs zonder urenberekening',
  // batch-60 klantportaal-zichtbaar-verzoeken (FO-439b K4)
  KLANTVERZOEK_STORING:                        'Klantverzoek: storing gemeld',
  KLANTVERZOEK_EXTRA_INZET:                    'Klantverzoek: extra inzet aangevraagd',
  KLANTVERZOEK_KLACHT:                         'Klantverzoek: klacht ingediend',
  KLANTVERZOEK_VRAAG:                          'Klantverzoek: vraag gesteld',
  KLANTVERZOEK_OFFERTE:                        'Klantverzoek: offerte aangevraagd',
  KLANTVERZOEK_PLANNING_WIJZIGING:             'Klantverzoek: planningswijziging voorgesteld',
  KLANTVERZOEK_GEGEVENS_WIJZIGING:             'Klantverzoek: wijziging klantgegevens voorgesteld',
  KLANTVERZOEK_CONTACTGEGEVENS:                'Klantverzoek: contactgegevens monteur opgevraagd',
  KLANTVERZOEK_MONTEUR_PROFIEL:                'Klantverzoek: monteursprofiel ingediend',
  KLANTVERZOEK_GEREEDSCHAP:                    'Klantverzoek: gereedschap of materieel gevraagd',
  KLANTVERZOEK_MONTEURKEUZE:                   'Klantverzoek: reactie op een voorgestelde monteur',
  KLANTVERZOEK_SLUITINGSDAG:                   'Klantverzoek: sluitingsdag doorgegeven',
  KLANTVERZOEK_BEOORDELING:                    'Klantverzoek: beoordeling ingevuld',
  KLANTVERZOEK_ONBEANTWOORD:                   'Klantverzoeken te lang onbeantwoord (escalatie)',
  // batch-61 uitrusting-credits-maten (FO-445)
  VANYSSELT_SALDO_AFWIJKING:                   'Van Ysselt meldt een afwijkend creditsaldo',
  // batch-46 adres-reis (FO-390)
  ADRES_REISIMPACT_INFO:                       'Adreswijziging: reisimpact (ter kennisname)',
  ADRES_REISCORRECTIE_HERBEOORDELEN:           'Herbeoordeel structurele reiscorrecties',
  // batch-49 in-te-plannen (FO-398)
  DIENST_IN_TE_PLANNEN:                        'Diensten zonder uitvoerder (in te plannen)',
  DIENST_IN_TE_PLANNEN_URGENT:                 'Dienst zonder uitvoerder binnen 48 uur',
  // batch-60 vaste-monteur (FO-440)
  VASTE_MONTEUR_VERVANGING_ESCALATIE:          'Vervanging vaste monteur nog niet geregeld',
  // batch-50 ai-tegoed (FO-402).
  AI_TEGOED_OP:                                'AI-tegoed op - verwerking gepauzeerd',
  AI_TEGOED_HERSTELD:                          'AI-verwerking hervat na tegoed-aanvulling',

  REISTIJD_CACHE_GESANEERD:                    'Route-cache gesaneerd + open dagen herrekend',

  // batch-51 finqle-api (FO-406).
  FINQLE_VERZOEK_WACHT_OP_AKKOORD:             'Facturatieverzoeken wachten op akkoord van de klant',
  FINQLE_VERZOEK_AFGEKEURD:                    'Facturatieverzoek afgekeurd door de klant',
  FINQLE_INTEGRATIE_STORING:                   'Finqle-koppeling werkt niet',
  FINQLE_INTEGRATIE_HERSTELD:                  'Finqle-koppeling werkt weer',

  // batch-53 acquisitie-vervolg (FO-407)
  ACQUISITIE_VERVOLGCONTACTEN:                 'Vervolgcontacten van vandaag (dagbundel)',

  // batch-54 klant-akkoord-mail (FO-412)
  KLANT_AKKOORD_MAIL_TE_BEOORDELEN:            'Mail-reacties van klanten te beoordelen',
  KLANT_AKKOORD_MAIL_NIET_GEKOPPELD:           'Niet-gekoppelde mail-reacties van klanten',
  LOONSTROOK_VERZOEK_TE_BEOORDELEN:            'Loonstrook-verzoek te beoordelen',
  LOONSTROOK_VERZOEK_BESLIST:                  'Besluit op je loonstrook-verzoek',
  LOONSTROOK_VERZOEK_AFGEROND:                 'Je loonstrook-verzoek is afgerond',
  BRUIKLEEN_OVEREENKOMST_NODIG:                'Bruikleenovereenkomst opstellen',
  BRUIKLEEN_OVEREENKOMST_OPEN:                 'Bruikleenovereenkomsten blijven openstaan',

  // batch-59 loonstrook-controles (FO-433)
  LOONSTROOK_CONTROLE_AFWIJKINGEN:             'Afwijking voertuig/bijtelling op de loonstroken',
  LOONSTROOK_BOETE_NIET_VERREKEND:             'Boete nog niet verrekend',

  // batch-61 portal-collega (FO-446)
  PLANNING_COLLEGA_BEZETTING_GEWIJZIGD:        'Bezetting van je dienst gewijzigd (collega erbij of eraf)',

  // batch-62 garage-voertuig (FO-465)
  VOERTUIG_ONDERHOUD_REMINDER:                 'Volgende onderhoudsactie plannen (beurt, APK of banden)',
  GARAGE_FACTUUR_CONTROLE:                     'Garagefactuur controleren (km, datum of schade)',
  // batch-62 kostprijs-werkelijk (FO-460 + FO-461)
  BRANDSTOF_REGELS_ONGEKOPPELD:                'Brandstofregels wachten op koppeling',
  BRANDSTOF_VOERTUIG_ZONDER_BERIJDER:          'Tankbeurten op een voertuig zonder berijder',
  BRANDSTOF_VERBRUIK_AFWIJKING:                'Afwijkend brandstofverbruik',
  BRANDSTOF_CONSISTENTIE_AFWIJKING:            'Nachtcontrole brandstofkosten: afwijkingen',
  LOONCOMPONENT_ONBEKEND:                      'Nieuwe looncomponenten te classificeren',
  LOONKOST_CONSISTENTIE_AFWIJKING:             'Nachtcontrole loonkosten: afwijkingen',
  // batch-62 telefoonabonnementen (FO-463)
  TELEFOON_CONTRACT_SIGNAAL:                   'Telefooncontract loopt af, opzegtermijn nadert of wordt stilzwijgend verlengd',
  TELEFOON_FACTUUR_CONTROLE:                   'Controle telefoonfactuur (onbekend nummer, ontbrekende regel of afwijkend bedrag)',
  // batch-63 certificaat-mailbox (FO-469)
  CERTIFICAAT_MAILBOX_BEOORDELEN:              'Certificaten uit de mailbox wachten op beoordeling',
  CERTIFICAAT_MAILBOX_REVIEW:                  'Certificaat-mails vragen handmatige controle',
  // batch-63 cursusplanning (FO-468)
  CURSUS_AANVRAAG_BEVESTIGD:                   'Cursusaanvraag bevestigd door de opleider',
  CURSUS_AANVRAAG_AFGEWEZEN:                   'Cursusaanvraag afgewezen door de opleider',
  CURSUS_AANVRAAG_TE_BEOORDELEN:               'Antwoord van de opleider beoordelen',
  CURSUS_AANVRAAG_GEEN_ANTWOORD:               'Cursusaanvraag: nog geen antwoord van de opleider',
  CURSUS_AANVRAAG_VOORKEURSDATUM:              'Cursusdatum nadert zonder bevestiging',
  CURSUS_AANVRAAG_MAIL_NIET_GEKOPPELD:         'Opleidermails zonder gekoppelde cursusaanvraag',
  // batch-65 inkoop-kosten (FO-483)
  INKOOP_DOORBELASTEN_OUDERDOM:                'Inkoopkosten wachten te lang op doorbelasten',
  INKOOP_DOORBELASTING_SIGNAAL:                'Doorbelaste inkoopkosten: controle nodig',
  INKOOP_NAGEKOMEN_KOSTEN:                     'Nagekomen inkoopkosten op een gefactureerde werkbon',
  // batch-65 voorraad-besteladvies (FO-481)
  BESTELADVIES_KLAAR:                          'Besteladvies klaar om te beoordelen',
  BESTELLING_MAIL_MISLUKT:                     'Bestelbericht niet verstuurd',
  LEVERING_TE_LAAT:                            'Levering te laat',
  MINIMUM_VOORSTEL:                            'Voorstel minimumvoorraad',
  // batch-65 finqle-verzamelbon (FO-484)
  KLANTPORTAAL_VERZAMELBON_GEACCORDEERD:       'Klant heeft verzamelbon geaccordeerd',
  KLANTPORTAAL_VERZAMELBON_AFGEKEURD:          'Klant heeft (een deel van) een verzamelbon afgekeurd',
  // batch-65 uitrusting-flow (FO-480)
  UITRUSTING_LEVERING_VOORSTEL:                'Uitrusting: voorstel om af te halen',
  UITRUSTING_LEVERING_GEPLAND:                 'Uitrusting: afhaalmoment vastgelegd',
  UITRUSTING_LEVERING_MOMENT_GEKOZEN:          'Uitrusting: medewerker koos een afhaalmoment',
  UITRUSTING_LEVERING_MEENEMEN:                'Uitrusting meenemen voor een collega',
  UITRUSTING_LEVERING_NIET_VERSCHENEN:         'Uitrusting: herhaald niet verschenen bij de overdracht',
  // batch-65 apk-rapport (FO-478)
  VOERTUIG_APK_AFGEKEURD:                      'APK afgekeurd, herkeuring nodig',
  VOERTUIG_APK_RDW_AFWIJKING:                  'RDW noemt een andere APK-vervaldatum dan het rapport',
  // batch-65 factuur-richting (FO-486)
  VERKOOP_POSTVAK_NAAR_INKOOP:                 'Inkoopfactuur vanuit het verkoop-postvak klaargezet',
  // FO-508 (spec 208)
  MIDDEL_UITZONDERING_AANGEVRAAGD:             'Losse koppeling telefoon/simkaart wacht op goedkeuring',
  MIDDEL_UITZONDERING_BEOORDEELD:              'Losse koppeling telefoon/simkaart beoordeeld',
  // batch-66 voertuigcontrole (FO-489)
  VOERTUIG_RDW_SYNC_FOUTEN:                    'RDW-sync: te veel fouten of afgebroken',
  VOERTUIG_RDW_SYNC_STIL:                      'RDW-sync draait niet meer',
  VOERTUIG_RDW_CRON_HERSTELD:                  'RDW-sync: planning ontbrak en is hersteld',
  // batch-66 interne-geldstroom (FO-494)
  INTERNE_GELDSTROOM_VERSCHIL:                 'Interne geldstroom: verschil',
  INTERNE_GELDSTROOM_EENZIJDIG:                'Interne geldstroom: eenzijdige boekingen',
  // batch-66 inkoop-afwijkingen (FO-490)
  INKOOP_AFWIJKING_GEREGISTREERD:              'Inkoopfactuur: afwijking geregistreerd',
  INKOOP_AFWIJKING_OUDERDOM:                   'Inkoopafwijkingen wachten te lang op reactie',
  INKOOP_AFWIJKING_CREDITNOTA_ONTVANGEN:       'Creditnota ontvangen bij open afwijking',
  INKOOP_AFWIJKING_REACTIE_ONTVANGEN:          'Leverancier reageerde op een afwijking',
  BOETE_TE_BEOORDELEN:                         'Boete te beoordelen',
  BOETE_DOORGEZET:                             'Boete wordt met je salaris verrekend',
  RIJGEDRAG_SIGNAAL:                           'Rijgedrag: meerdere of ernstige boetes',
  // batch-66 contract-mijlpalen (FO-496)
  CONTRACT_MIJLPAAL_KENNISMAKING:              'Contract getekend: kennismaking en startgesprek plannen',
  CONTRACT_MIJLPAAL_START_NADERT:              'Nieuwe medewerker start over twee weken',
  CONTRACT_MIJLPAAL_FACILITIES:                'Nieuwe medewerker: middelen klaarzetten',
  CONTRACT_MIJLPAAL_SUCCES_WENSEN:             'Nieuwe medewerker start vandaag: succes wensen',
  CONTRACT_MIJLPAAL_CHECKIN_WEEK1:             'Check-in na de eerste week',
  CONTRACT_MIJLPAAL_CHECKIN_MAAND1:            'Check-in na de eerste maand',
  CONTRACT_MIJLPAAL_OVERLEG_PROEFTIJD:         'Overleg proeftijd',
  CONTRACT_MIJLPAAL_PROEFTIJD_EINDE:           'Einde proeftijd: gesprek en verslag',
  CONTRACT_MIJLPAAL_VERLENGING:                'Contract loopt af: besluit verlenging',
  CONTRACT_MIJLPAAL_ESCALATIE:                 'Contractmijlpaal niet op tijd afgerond',
  CONTRACT_MIJLPAAL_CORRECTIE:                 'Contractmijlpaal: datum gewijzigd',
  CONTRACT_MIJLPAAL_TL_TOEWIJZEN:              'Contract zonder teamleider: teamleider toewijzen',
  // batch-66 kluis-niveaus (FO-495)
  KLUIS_TOEGANG_VERLOOPT:                      'Tijdelijke kluistoegang verloopt binnen 24 uur',
  KLUIS_STRIKT_ONTHULD:                        'Strikt beveiligd geheim onthuld',
  KLUIS_VRIJGAVE_AANGEVRAAGD:                  '4-ogen-vrijgave voor een geheim aangevraagd',
  KLUIS_VRIJGAVE_BEOORDEELD:                   '4-ogen-vrijgave beoordeeld',
  KLUIS_BESCHERMING_GEWIJZIGD:                 'Bescherming van strikt geheim gewijzigd',
  // batch-66 uren-loonstroken (FO-492)
  LOONSTROOK_VRIJGAVE_NIET_GOEDGEKEURD:        'Loonstroken nog niet goedgekeurd voor de vrijgave',
  // batch-67 taken-engine (FO-498)
  TAAK_DEADLINE_MORGEN:                        'Taak-deadline morgen',
  TAAK_DEADLINE_VERSTREKEN_AANMAKER:           'Deadline verstreken: taak nog niet gereed',
  // batch-67 hr-brieven (FO-506)
  HR_BESLUIT_CONTROLE_GEVRAAGD:                'HR-besluit wacht op controle (4-ogen)',
  HR_BESLUIT_GECONTROLEERD:                    'HR-besluit gecontroleerd',
  HR_BESLUIT_STAP_OPEN:                        'Stap in een HR-besluit staat open voor jouw rol',
  HR_BESLUIT_TERMIJN_NADERT:                   'Termijn van een HR-besluit nadert',
  HR_BESLUIT_TERMIJN_VERSTREKEN:               'Termijn van een HR-besluit verstreken',
  HR_BESLUIT_BRIEF_VERSTREKT:                  'Brief van HR beschikbaar in de portal',
  HR_BESLUIT_VSO_HERROEPEN:                    'Overeenkomst herroepen binnen de bedenktermijn',
  // batch-67 middelen-inname (FO-499 + FO-505)
  MIDDEL_OVERDRACHT_TE_BEVESTIGEN:             'Overdracht van een bedrijfsmiddel te bevestigen',
  MIDDEL_OVERDRACHT_AFGEHANDELD:               'Overdracht van een bedrijfsmiddel afgehandeld',
  BRUIKLEEN_ONDERTEKENING_HERINNERING:         'Bruikleenovereenkomst wacht op je handtekening',
  BRUIKLEEN_ONDERTEKENING_ESCALATIE:           'Bruikleenovereenkomst te lang niet ondertekend',
  REPARATIEVERZOEK_INGEDIEND:                  'Reparatieverzoek te beoordelen',
  REPARATIEVERZOEK_BEOORDEELD:                 'Reparatieverzoek beoordeeld',
  MIDDEL_VERHAAL_BESLUIT:                      'Kosten van een bedrijfsmiddel op je verhaald',
  MIDDEL_BEZWAAR_INGEDIEND:                    'Bezwaar tegen een verrekening ingediend',
  // batch-67 campagnes
  BIJTELLING_CAMPAGNE_START:                   'Bijtellingskeuze - campagne gestart',
  BIJTELLING_CAMPAGNE_HERINNERING:             'Bijtellingskeuze - herinnering',
  BIJTELLING_NIET_INGEDIEND:                   'Bijtellingskeuze niet ingediend na de deadline',
  BIJTELLING_KEUZE_BEOORDEELD:                 'Bijtellingskeuze beoordeeld',
  CAMPAGNE_VERLOF_ONDER_VOORBEHOUD:            'Vakantie onder voorbehoud (saldotekort)',
  CAMPAGNE_BEZETTINGSCONFLICT:                 'Bezettingsconflict vakantieplanning',
  EVENEMENT_UITNODIGING:                       'Evenement - uitnodiging',
  EVENEMENT_HERINNERING:                       'Evenement - herinnering',
  EVENEMENT_BEVESTIGING:                       'Evenement - aanmelding bevestigd',
  EVENEMENT_ORGANISATIE_OVERZICHT:             'Evenement - overzicht voor de organisatie',
  MIDDEL_VERHAAL_TE_VERREKENEN:                'Verhaald bedrag te verrekenen via het loon',
  // batch-68 controle (FO-342 §7)
  VERLONINGSCONTROLE_KLAAR:                    'Verloningscontrole berekend',
  VERLONINGSCONTROLE_GEWIJZIGD_NA_AKKOORD:     'Verloningscontrole - gegevens gewijzigd na akkoord',
  VERLONINGSCONTROLE_HERINNERING:              'Verloningscontrole - nog geen akkoord',
  VOERTUIG_APK_NIET_BIJ_RDW:                   'APK gefactureerd maar niet bij RDW geregistreerd',
  VOERTUIG_SERVICE_INDICATOR_NIET_GERESET:     'Beurt geregistreerd, service-indicator niet gereset',
  VOERTUIG_ONDERHOUD_TERUGDRAAIEN_CONFLICT:    'Onderhoudsregistratie teruggedraaid, interval niet hersteld',
  // batch-68 handtekening (FO-345)
  DOCUMENT_ONDERTEKENEN_AAN_DE_BEURT:     'Document: jij bent aan de beurt om te tekenen',
  DOCUMENT_HANDTEKENING_VERVALLEN:        'Document gewijzigd: teken opnieuw',
  DOCUMENT_ONDERTEKENDEADLINE_VERSTREKEN: 'Ondertekendeadline verstreken',
  // batch-69 bestelflow (FO-512)
  UITRUSTING_BESTELLING_INGEDIEND:          'Uitrustingbestelling ingediend (te beoordelen)',
  UITRUSTING_BESTELLING_STATUS_GOEDKEURDER: 'Status van een door jou goedgekeurde bestelling',
  SKILLMATRIX_INGEVULD: 'Medewerker heeft de skillmatrix ingevuld',
  // batch-69 km-balans (FO-515)
  KILOMETERBALANS_WERK_BOVEN_TOTAAL: 'Kilometerbalans: voertuigen om te controleren (werk hoger dan het totaal, of een genegeerde kilometerstand)',
  KILOMETERBALANS_BOVEN_DREMPEL:     'Kilometerbalans: privé-indicatie structureel boven de drempel',
  // batch-69 planbord-afspraak (FO-523)
  AFSPRAAK_HERINNERING:                   'Afspraak - herinnering vooraf',
  AFSPRAAK_VERSLAG_GEVRAAGD:              'Afspraak afgelopen - maak je gespreksverslag',
  AFSPRAAK_VERSLAG_HERINNERING:           'Afspraak - verslag nog niet gemaakt',
  AFSPRAAK_VERSLAG_ESCALATIE:             'Afspraak - verslag na 48 uur nog niet gemaakt',
  // batch-69 samenstellen (FO-509)
  SAMENSTELLING_KLAAR:  'Samenstel-opdracht compleet: klaar om samen te stellen',
  SAMENSTELLING_RISICO: 'Uitrusting niet compleet voor de startdatum',
  // batch-69 samenstellen (FO-510)
  SET_SAMENSTELLEN_NODIG: 'Sets samenstellen: te weinig complete koffers op voorraad',
  // batch-69 samenstellen (FO-516)
  INNAME_TER_GOEDKEURING_HR: 'Inname: niet ingeleverde spullen ter beoordeling',
  INNAME_TE_VERREKENEN: 'Inname: te verrekenen via het loon',
  INNAME_BEVESTIGD: 'Je inname-bevestiging staat klaar',
  // batch-69 werkbon-pauze (FO-511)
  KLANTPAUZE_ONBEVESTIGD: 'Werkbon zonder bevestigde pauzeregel van de klant',
  // batch-69 actiepunten (FO-514)
  ACTIEPUNTEN_SAMENVATTING: 'Actiepunten app - samenvatting',
  ACTIEPUNT_HEROPEND:       'Actiepunt app heropend (bron weer leeg)',
  // batch-69 tvt-saldi (FO-522 §C9, FO-513 §A2)
  VERLOF_SALDO_NEGATIEF_MET_TVT: 'Verlofsaldo in de min terwijl er TVT is',
  VERLOF_SALDO_VERPLAATST:       'Je verlofsaldo is gecorrigeerd',
  // batch-69 tvt-saldi (FO-513 §C8)
  TVT_OPBOUW_GEDAALD:            'TVT-opbouw gedaald na de boeking',
  // batch-69 magazijn-cijfers (FO-521)
  BESTELADVIES_HERINNERING:         'Besteladvies wacht op goedkeuring',
  BESTELADVIES_ESCALATIE:           'Besteladvies blijft liggen',
  BESTELADVIES_AUTOMATISCH_BESTELD: 'Automatisch besteld (samenvatting)',
  VOORRAAD_PRIJS_VOORSTEL:          'Voorstel nieuwe inkoopprijs',
  // batch-69 magazijn-cijfers (FO-520)
  TELLING_GOEDKEURING_NODIG:        'Tellingcorrectie wacht op goedkeuring',
  TELLING_AFGEROND:                 'Voorraadtelling afgerond',
  TELLING_OPEN_HERINNERING:         'Voorraadtelling staat nog open',
  // batch-70 vakantiecampagne (FO-525)
  CAMPAGNE_SALDO_GEWIJZIGD:         'Vakantieaanvraag: je saldo is aangepast',
  VERLOF_SALDO_RECONCILIATIE:       'Jaaroverzicht verlof sluit niet aan',
  CAMPAGNE_IN_OVERLEG:              'Vakantiewens in overleg',
  CAMPAGNE_WIJZIGING_INGEDIEND:     'Vakantiewens gewijzigd na indienen',
  CAMPAGNE_TVT_DECEMBER_LAAT:       'TVT-keuze per 1 december: nog vóór de december-payroll verwerken',
  CAMPAGNE_UITKOMST:                'Uitkomst vakantieaanvraag',
  // batch-70 uitrusting-controle (FO-531)
  CAMPAGNE_UITRUSTING_START:           'Uitrustingscontrole - start',
  CAMPAGNE_UITRUSTING_HERINNERING:     'Uitrustingscontrole - herinnering',
  UITRUSTING_AANGEVULD_TEKENEN:        'Uitrusting aangevuld, overeenkomst klaar om te tekenen',
  UITRUSTING_ONTBREKEND_NIEUW:         'Uitrustingscontrole - nieuwe meldingen voor het magazijn',
  UITRUSTING_KOFFER_OP_VERKEERDE_NAAM: 'Koffer staat op de verkeerde naam',
  UITRUSTING_TEAM_WEEKLIJST:           'Uitrustingscontrole - weeklijst van je team',
  // batch-70 voertuig-meldingen (FO-527)
  VOERTUIG_MELDING_MISLUKT:              'Voertuigmelding vraagt actie',
  VOERTUIG_MELDING_NIET_BEVESTIGD:       'Voertuigmelding niet bevestigd',
  VOERTUIG_CARTRACKER_CORRECTIE_MISLUKT: 'Cartracker-correctie mislukt',
  VOERTUIG_CARTRACKER_HANDMATIG_NODIG:   'Bestuurder in Cartracker aanpassen',
  LOONSTROOK_BIJTELLING_AFWIJKING:       'Loonstrook: bijtelling klopt niet met de voertuigmelding',
  // batch-70 wagenpark-week (FO-528 + FO-529)
  VOERTUIG_KM_ONBETROUWBAAR:        'Km-standen: verdacht of ontbrekend',
  VOERTUIG_CONTRACT_KM_OVERSCHRIJDING_VERWACHT: 'Contract-km: overschrijding verwacht',
  VOERTUIG_PRIVE_KM_HOOG:           'Privé-km boven de drempel',
  VOERTUIG_TOELICHTING_GEVRAAGD:    'Ritten en tankpas: toelichting gevraagd',
  TANKPAS_HOUDER_GEWIJZIGD:         'Tankpas op jouw naam of overgedragen',
  TANKPAS_REGIME_ONBEKEND:          'Tankpas: bijtellingsregime onbekend',
  TANKPAS_500KM_GRENS:              'Tankpas: 500 km-grens privégebruik',
  WAGENPARK_WEEKRAPPORT:            'Wagenpark-weekrapport',
  // batch-70 app-dashboard (FO-526)
  KENNISMAKING_GEPLAND:             'Kennismaking nieuwe medewerker gepland',
  KENNISMAKING_HERINNERING:         'Herinnering: kennismaking nieuwe medewerker',
  KENNISMAKING_DEADLINE_VERLOPEN:   'Kennismaking niet op tijd afgerond',
  KENNISMAKING_ACTIEPUNT:           'Actiepunt uit de kennismaking',
  KENNISMAKING_SAMENVATTING:        'Wat we hebben besproken in de kennismaking',
  KENNISMAKING_AGENDA_MISLUKT:      'Kennismaking: agenda-item niet aangemaakt',
  APP_DEPLOY_LIVE:                  'Nieuwe versie van de app live',
  APP_DEPLOY_NIET_GEREGISTREERD:    'Deploy niet geregistreerd in het App dashboard',
  APP_DASHBOARD_WEEKSAMENVATTING:   'App dashboard - weeksamenvatting',
  // batch-71 kiosk-magazijn (FO-532 §7)
  KIOSK_PIN_GEBLOKKEERD: 'Magazijn-tablet: PIN geblokkeerd',
  KIOSK_SESSIE_GEREVOCEERD: 'Magazijn-tablet: op afstand uitgelogd',
  // batch-71 workflow-sjablonen (FO-533 §15)
  WORKFLOW_GESTART: 'Stappenplan gestart',
  WORKFLOW_STAP_OPEN: 'Stappenplan: taken voor jouw doelgroep',
  WORKFLOW_STAP_VERLOPEN: 'Stappenplan: stap over tijd',
  WORKFLOW_STAP_MISLUKT: 'Stappenplan: stap vraagt actie',
  WORKFLOW_AFGEROND: 'Stappenplan afgerond',
  WORKFLOW_WACHT_EXTERN_HERINNERING: 'Stappenplan: geen bevestiging van de externe partij',
  // batch-71 gesprek-secties (FO-536, FO-537)
  ONTWIKKELDOEL_STREEFDATUM_NADERT: 'Ontwikkeldoel: streefdatum nadert',
  ONTWIKKELING_NIET_BESPROKEN: 'Ontwikkeling twee gesprekken op rij niet besproken',
  CONTROLE_AFWIJKING: 'Gesprekscontrole: afwijking gevonden',
  CONTROLE_NIET_UITGEVOERD: 'Gesprekscontrole niet uitgevoerd',
  TANKPAS_NIET_AANWEZIG: 'Tankpas niet aanwezig bij gesprekscontrole',
  // batch-71 verplichte-certificaten (FO-541)
  VERPLICHT_CERTIFICAAT_UPLOAD_GEVRAAGD:    'Upload je verplichte certificaat',
  VERPLICHT_CERTIFICAAT_UPLOAD_HERINNERING: 'Herinnering: upload je verplichte certificaat',
  VERPLICHT_CERTIFICAAT_NIET_AANGELEVERD:   'Verplicht certificaat niet aangeleverd',
  VERPLICHTE_CURSUS_NIET_GEPLAND:           'Verplichte cursussen om te plannen',
  VERPLICHT_CERTIFICAAT_NOG_NIET_GEVOLGD:   'Medewerker heeft een verplichte cursus nog niet gevolgd',
  // batch-71 ouderschap-verlof (FO-539 §8)
  OUDERSCHAP_VERVAL_GECORRIGEERD:       'Ouderschapsverlof: correctie van vervallen uren uitgevoerd',
  OUDERSCHAP_VERLOPEN:                  'Opnametermijn ouderschapsverlof verstreken',
  OUDERSCHAP_BESCHERMD_VOORBIJ_TERMIJN: 'Ouderschapsverlof voorbij de termijn: controleer de lijst',
}

// ─── Recipient-resolver keys ────────────────────────────────────────────────
//
// Compile-time union van alle resolver-keys die de API kent. Seeds en hooks
// moeten een waarde uit deze tuple gebruiken — typo's vallen aan de typchecker
// op in plaats van pas op runtime.
export const NOTIFICATIE_RECIPIENT_RESOLVER_KEYS = [
  'werkbon.assigned-employee',
  'werkbon.previous-employee',
  'leave.requester',
  'leave.approvers',
  // Procedure-correctie verlof (Deel 1, two-step) — VERLOF_INGEDIEND gaat eerst
  // ALLEEN naar de teamleider; HR krijgt zijn classificatie-item pas na
  // TL-goedkeur (doorstroming in leave-v2 approve-teamleider).
  'leave.teamleiders',
  'all-hr',
  'all-hr+salaris',
  'actor.requester',
  'intake.team',
  // PR-B (spec 18) — nieuwe resolvers voor employee-bound events
  // (certificaat/contract/proeftijd/rijbewijs) en VERJAARDAG_VANDAAG.
  'employee.self',
  'employee.self+hr',
  'employee.self+hr+tl',
  // Ronde 4 W7 (spec ronde-4/07 §13) — verplichte werkbon-meldingen. `self`
  // is hier de medewerker(s) op de werkbon (via dag-entries), bronEntiteitId =
  // werkbonId. `+tl` = alle teamleiders (heropend), `+salaris` = alle
  // salarisadministratie-users (payroll-correctie).
  'employee.self+tl',
  'employee.self+salaris',
  // FO-018 (batch-14) — werkbon-afrondmelding naar de teamleider(s) van de
  // monteur(s) op de werkbon (dept-scoped, zoals `taak.teamleider`).
  // bronEntiteitId = werkbonId. Geen afdelings-teamleider = leeg (was: alle
  // teamleiders); batch-67 (FO-500): leeg gaat via de leveringslaag naar de eigenaar van de soort, met een signaal aan beheer.
  'werkbon.teamleider',
  'hr+manager',
  // `configurable.recipients` leest INCLUDE-overrides uit
  // NotificatieVoorkeurOverride. Zonder INCLUDEs is de uitkomst leeg (was:
  // iedereen met portal-toegang, bv. voor VERJAARDAG_VANDAAG);
  // batch-67 (FO-500): leeg gaat via de leveringslaag naar de eigenaar van de soort, met een signaal aan beheer.
  'configurable.recipients',
  // PR-B follow-up (spec 18) — gesprek-flow (PR-M). Privacy-aware:
  // VERTROUWENSPERSOON-aanvragen worden UITSLUITEND naar de gekoppelde
  // partner-user geroute, NIET naar HR/TL. Andere partners (HR_MANAGER /
  // LEIDINGGEVENDE) gaan naar HR.
  'gesprek.recipient',
  // Verjaardag-keuze (jaarlijkse januari-campagne).
  //  - `verjaardag.medewerker`  → de medewerker zelf (employee.userId), via
  //    bronEntiteitId = VerjaardagKeuze.id (resolver navigeert door naar
  //    employee).
  //  - `verjaardag.teamleider`  → alle TEAMLEIDER-users in dezelfde afdeling
  //    als de medewerker (fan-out). Geen TL gekoppeld? → fail-closed: lege
  //    lijst, geen broadcast naar de hele organisatie.
  'verjaardag.medewerker',
  'verjaardag.teamleider',
  // ── Ronde 4 W8 (spec ronde-4/08 §2.1) — capaciteits-signalen ──────────────
  // Rol-fan-out doelgroepen voor de wekelijkse capacity-check. De bronEntiteit
  // is een employeeId (overbelast/onderbenut) of een vaste sentinel (team-
  // signalen); deze resolvers negeren de bron en fan-outten op rol.
  'planner',
  'planner+teamleider',
  'planner+hr',
  'manager',
  // ── batch-49 in-te-plannen (FO-398 §8) ────────────────────────────────────
  // `planning.planners`: iedereen die het vak "In te plannen" kan leegwerken =
  // rol PLANNER UNIE users met PLANNING.canEdit. Bewust niet de kale `planner`-
  // resolver: een organisatie die de PLANNER-rol niet gebruikt (alles via
  // module-permissies) zou dan naar NIEMAND resolven en het signaal nooit zien.
  // `hasBackofficeAccess` omdat de deep-link naar het backoffice-planbord gaat.
  'planning.planners',
  // ── Slimme signalering (spec 2026-05-26) — opvolging-resolvers ──────────────
  // CRM / relatie-verantwoordelijken en administratie-rol.
  'client.responsible',
  'lead.owner',
  'administratie',
  // batch-69 werkbon-pauze (FO-511, integrator-review H1) - `klantpauze.oplossers`:
  // backoffice met inzage in werkbonnen, zonder de accountant; dan per probleem (de
  // versturende module levert het aan): de pauzeregel bevestigen (CRM.canEdit) of de
  // factuurpauze herstellen (WERKBONNEN_FACTURATIE.canInvoice). Leeg -> eigenaar.
  'klantpauze.oplossers',
  // ── Veiligheid — opvolgers van onveilige-situatie-meldingen ─────────────────
  // Neutrale rol-fan-out naar TEAMLEIDER + HR (gelijk aan `leave.approvers`,
  // maar als eigen sleutel zodat de doelgroep semantisch los staat van verlof).
  'veiligheid.opvolgers',
  // ── Felicitaties + jubilea (B6 / C8 / C9) ──────────────────────────────────
  // `jubileum.hr+teamleider`: bronEntiteitId = employeeId → unie van alle HR +
  // alle TEAMLEIDER in de afdeling van die medewerker (dept-gated, fail-closed
  // op de TL-tak als de medewerker geen afdeling heeft; HR blijft altijd mee).
  'jubileum.hr+teamleider',
  // ── Vervanging bij verlof (B6) ─────────────────────────────────────────────
  // `vervanging.opvolgers`: bronEntiteitId = VervangingOpvolging.id → unie van
  // TEAMLEIDER in de afdeling van de afwezige medewerker (fail-closed) + alle
  // PLANNER. Een extra ontvanger (planning-coordinator) wordt later via de
  // bestaande INCLUDE-override-mechaniek geconfigureerd (geen hardcoded persoon).
  'vervanging.opvolgers',
  // ── Spec 30 Fase 8A — voorraad beheer ────────────────────────────────────
  // `voorraad.beheer`: users met VOORRAAD.canView (HR/magazijn-beheerder). Bij
  // NIET_RETOUR / SLEUTEL_TE_LANG_UIT ook de houder zelf (via extras).
  'voorraad.beheer',
  // Verwerkers van uitrustingbestellingen: rol ADMIN/TEAMLEIDER verenigd met
  // UITRUSTING.canEdit. Gelijk aan de magUitrustingVerwerken-gate, zodat er nooit
  // een taak landt bij iemand die hem niet mag uitvoeren.
  'uitrusting.verwerkers',
  // Picklijst: magazijn (VOORRAAD.canView) plus dezelfde verwerkers, claimbaar.
  'uitrusting.picklijst',
  // De besteller zelf. Leest de bestelling (niet de employee) als bron-entiteit.
  'uitrusting.besteller',
  // ── Ongevallen-module (§7) ─────────────────────────────────────────────────
  // `ongevallen.beheerders`: permissie-groep van users met ONGEVALLEN.canApprove
  // (deny-by-default; geen rol-fallback). Mirror van de `administratie`-resolver
  // die hetzelfde doet voor FACTUREN.canView.
  'ongevallen.beheerders',
  // ── Loonstroken-module (spec B, stap 4a) ──────────────────────────────────
  // `loonstrook.verantwoordelijken`: leest LoonstrookInstellingen.verantwoordelijke
  // UserIds; is die leeg, dan fan-out naar alle actieve users met rol SALARIS.
  // Voor de maandelijkse upload-herinnering (geen vaste bron-entiteit nodig).
  'loonstrook.verantwoordelijken',
  // #28 Deel 4 — `loonstrook.beheerders`: users met magLoonstrokenZien (Deel 3).
  // Fallback wanneer niemand de flag heeft: LoonstrookInstellingen.verantwoordelijken
  // → rol SALARIS. Voor de correctieverzoek/vraag-notificatie.
  'loonstrook.beheerders',
  // batch-56 (FO-417) — `loonstrook.verzoek-beoordelaars`: de mensen die een
  // medewerker-verzoek beoordelen (goedkeuren -> doorzetten naar de
  // salarisverwerker, of afwijzen met reden). Leest
  // LoonstrookInstellingen.verzoekBeoordelaarUserIds; leeg => dezelfde cascade
  // als `loonstrook.beheerders` (magLoonstrokenZien -> verantwoordelijken ->
  // rol SALARIS), zodat een verzoek nooit stilvalt en er nergens een persoon
  // in code staat. Na FO-416 óók centraal per event te overschrijven.
  'loonstrook.verzoek-beoordelaars',
  // ── Operationele takenmodule (spec 34 §4) ──────────────────────────────────
  // De escalatie wist `uitvoerderId` op de taak, dus de uitvoerder kan NIET uit
  // de bron-entiteit worden gelezen op resolve-moment. De caller geeft de
  // Employee-id van de (vorige) uitvoerder mee via `resolverExtras.employeeId`;
  // deze resolvers leiden daaruit de userId(s) af.
  //   - `taak.uitvoerder`         → de uitvoerder zelf (employeeId → userId).
  //   - `taak.teamleider`         → teamleider(s) van die employee (dept-scoped,
  //                                 fail-closed bij geen afdeling).
  //   - `taak.uitvoerder-tl`      → uitvoerder + zijn teamleider(s).
  //   - `taak.escalatie`          → teamleider(s) + de configureerbare escalatie-
  //                                 ontvanger (TaakInstelling 'taak.escalatieOntvangerId',
  //                                 waarde = Employee-id). Ontbreekt de instelling →
  //                                 alleen de teamleider(s) (geen crash).
  //   - `taak.escalatie-aanmaker` → taak.escalatie-ontvangers + de aanmaker van de
  //                                 taak (OperationeleTaak.createdById, via bronEntiteitId).
  'taak.uitvoerder',
  'taak.teamleider',
  'taak.uitvoerder-tl',
  'taak.escalatie',
  'taak.escalatie-aanmaker',
  // batch-62 taken-ux (FO-464) — `taak.aanmaker`: alleen de aanmaker (aanvrager)
  // van de taak (OperationeleTaak.createdById via bronEntiteitId). Voor
  // TAAK_GEREED_GEMELD ("de aanvrager krijgt bericht").
  'taak.aanmaker',
  // ── Spec 35 — Vertrouwenspersoon-module ────────────────────────────────────
  // `all-vertrouwenspersonen`: alle actieve users met `isVertrouwenspersoon`
  // (de flag is de enige bron van wie VP is — geen rol-fallback). Gebruikt
  // door VP_ROL_GEWIJZIGD; notificaties bevatten nooit casusinhoud.
  'all-vertrouwenspersonen',
  // ── Batch-6 hrm — HRM gespreksagenda ───────────────────────────────────────
  // `hr-gesprek.betrokkenen`: de deelnemers (employee → userId) + de
  // gesprekspartner van het HrGesprek (bronEntiteitId = HrGesprek.id).
  // Bewust persoon-only: geen functie-/rol-fan-out (privacy verzuimgesprek).
  'hr-gesprek.betrokkenen',
  // ── Batch-6 cert — certificaten-opvolgflow ─────────────────────────────────
  // `cert.betrokkenen`: de medewerker (bronEntiteitId = Employee.id) + de
  //   verantwoordelijke (extras.requesterUserId, bv. de sessie-planner).
  // `cert.hr+verantwoordelijke`: alle HR-users + de verantwoordelijke
  //   (extras.requesterUserId) — voor opvolg-signalen die HR-werkvoorraad zijn.
  'cert.betrokkenen',
  'cert.hr+verantwoordelijke',
  // ── Batch-7 recruitment ─────────────────────────────────────────────────────
  // `acquisitie.checkin-verantwoordelijke`: de verantwoordelijke van de
  //   LeadCheckIn (bronEntiteitId = LeadCheckIn.id; employee → userId).
  // `kandidaat.recruiter-of-hr`: de recruiter van de kandidaat
  //   (bronEntiteitId = Kandidaat.id); zonder recruiter leeg (was: alle
  //   HR-users). batch-67 (FO-500): leeg gaat via de leveringslaag naar de eigenaar van de soort, met een signaal aan beheer.
  //   Kandidaatdata is persoonsgegeven — geen bredere fan-out.
  'acquisitie.checkin-verantwoordelijke',
  // ── batch-53 acquisitie-vervolg (FO-407 §C) ─────────────────────────────────
  // `acquisitie.vervolg-eigenaar`: de eigenaar van de leads in ÉÉN dagbundel.
  // Bewust caller-supplied (`extras.requesterUserId`) en niet `lead.owner`: de
  // bundel gaat over n leads tegelijk, dus er is geen enkele bron-lead om de
  // eigenaar uit af te leiden. De cron groepeert zelf op eigenaar (eigenaarId,
  // terugval acquisitieEigenaarId) en vuurt per eigenaar één keer.
  'acquisitie.vervolg-eigenaar',
  'kandidaat.recruiter-of-hr',
  // FO-517 — `kandidaat.beoordelaars`: de second-review-doelgroepen (HR, TL,
  //   directie; instelbaar, standaard per rol). `kandidaat.besluitnemers`:
  //   de besluitnemers (instelbaar, standaard directie). `kandidaat.info-verzoek`:
  //   de persoon van wie info gevraagd wordt (nieuwste open verzoek; bronEntiteitId = Kandidaat.id),
  //   anders de recruiter.
  'kandidaat.beoordelaars',
  'kandidaat.besluitnemers',
  'kandidaat.info-verzoek',
  // ── Batch-8 rapportage — reistijd-afwijking-week-signaal ────────────────────
  // `reisuren.teamleider`: de teamleider(s) van de medewerker (dept-scoped,
  // zelfde patroon als `taak.teamleider`). Heeft de medewerker geen afdeling of
  // teamleider, dan is de uitkomst leeg (was: alle planners);
  // batch-67 (FO-500): leeg gaat via de leveringslaag naar de eigenaar van de soort, met een signaal aan beheer.
  // De caller geeft de Employee-id mee via `resolverExtras.employeeId`.
  'reisuren.teamleider',
  // ── Batch-10 portal-stream #24 — ideeenbox ─────────────────────────────────
  // `idee.ontvangers`: leden van de KWALITEIT-functie-inbox (UserInboxFunctie).
  // Per-user fan-out + functie-target (RESOLVER_TO_FUNCTIES -> ['KWALITEIT']).
  // Geen leden -> lege lijst. Het gedeelde functie-item telt alleen als
  // ontvanger zolang de inbox een bruikbaar lid heeft (batch-67: een beheerder
  // is geen impliciet lid meer van elke functie-inbox); anders gaat de melding
  // naar de eigenaar van de soort.
  'idee.ontvangers',
  // ── FO-003 (batch-12) — onboarding-startworkflow ───────────────────────────
  // `onboarding.hr+medewerker`: de nieuwe medewerker zelf (via Onboarding →
  // employee.userId) + alle HR-users. bronEntiteitId = Onboarding.id. Voor de
  // deadline-signalen van de start-workflow (HR bewaakt, medewerker handelt).
  'onboarding.hr+medewerker',
  // ── FO-016 (batch-14) — ideeenbox ──────────────────────────────────────────
  // `idee.indiener`: de indiener van een Idee (bronEntiteitId = Idee.id ->
  //   requester.employee.userId). Voor de statuswijziging-terugkoppeling.
  // `idee.reactie-teamleider`: de teamleider(s) van de reagerende medewerker
  //   (bronEntiteitId = IdeeVoorstelReactie.id -> employee -> dept-scoped TL,
  //   zelfde patroon als `taak.teamleider`). Fail-closed bij geen afdeling/TL;
  //   de moderatie-wachtrij blijft hoe dan ook zichtbaar voor HR/ADMIN.
  // `idee.organisatie`: alle actieve portal-medewerkers (gepubliceerd voorstel,
  //   stil in-app). Geen functie-fan-out.
  'idee.indiener',
  'idee.reactie-teamleider',
  'idee.organisatie',
  // ── FO-146 (batch-28, stream mail-melding) ─────────────────────────────────
  // `mail.afzender-of-admin`: de afzender van de mislukte mail
  //   (extras.requesterUserId = OutgoingEmail.createdById); is er geen menselijke
  //   afzender (systeem-/cron-mail), dan alle actieve ADMIN-users als fallback.
  //   Persoon-only (geen functie-inbox-fan-out).
  'mail.afzender-of-admin',
  // ── FO-307-A2 (batch-33) — consignatie-ruil: navigeren vanaf de ruil-id naar
  //   de partijen (aanvrager/collega via de relatie) + fan-out naar planners.
  'consignatie.ruil-collega+planner',
  'consignatie.ruil-aanvrager+planner',
  'consignatie.ruil-aanvrager+collega',
  // ── FO-326 (batch-37 schadeformulier) — wagenparkbeheer ────────────────────
  // `voertuigen.beheer`: users met VOERTUIGEN.canEdit (wagenparkbeheer), zelfde
  //   permissie-groep-patroon als `voorraad.beheer`. Deny-by-default.
  // `schade.bestuurder`: de bestuurder van de schademelding (bronEntiteitId =
  //   SchadeMelding.id → bestuurderEmployee.userId). Persoon-only.
  'voertuigen.beheer',
  'schade.bestuurder',
  // ── FO-345 (batch-40) — vaste verzuim-contactpersonen ──────────────────────
  // `verzuim.contactpersonen`: de vaste contactpersonen uit VerzuimInstelling
  // VERENIGD met `all-hr`, met fallback-ladder (leeg → all-hr → ADMIN) en een
  // `VERZUIM_DOELGROEP_LEEG`-signaal als er ná alle fallbacks niemand overblijft.
  // Bewust een UNIE en geen rol-filter: `all-hr` filtert hard op `role: 'HR'`,
  // dus één rolwijziging (HR-er wordt ADMIN) liet de verzuimmeldingen stil
  // stoppen. Wie in de lijst staat krijgt ze, ongeacht rol.
  'verzuim.contactpersonen',
  // `all-admins`: alle actieve beheerders. Voor vangnet-signalen over kapotte
  // configuratie (o.a. VERZUIM_DOELGROEP_LEEG) - een doelgroep die per definitie
  // niet leeg kan raken door een configuratiefout.
  'all-admins',
  // ── batch-46 indexatie (FO-391) — gate-houders tarief-indexatie ────────────
  // `facturen.approvers`: permissie-groep van users met FACTUREN.canApprove
  // (deny-by-default; geen rol-fallback). ADMIN valt hier automatisch onder
  // (gematerialiseerde rechtenrijen). Mirror van de `administratie`-resolver
  // (FACTUREN.canView), maar strikter: alleen wie mag goedkeuren.
  'facturen.approvers',
  // ── FO-392 (batch-46) — inkoopbudget ──────────────────────────────────────
  // `inkoopbudget.budgethouder`: de budgethouder (Employee) van het budget
  // (bronEntiteitId = InkoopBudget.id -> budgethouder.userId). Persoon-only.
  // Zonder budgethouder leeg (was: de administratie-permissiegroep);
  // batch-67 (FO-500): leeg gaat via de leveringslaag naar de eigenaar van de soort, met een signaal aan beheer.
  'inkoopbudget.budgethouder',
  // FO-393 (batch-46) — inkoop 3-way-match. `inkoop.behandelaar`: de aanmaker/
  // behandelaar van de order (via de factuur). Persoon-only. Zonder
  // order-aanmaker leeg (was: iedereen met INKOOP.canEdit);
  // batch-67 (FO-500): leeg gaat via de leveringslaag naar de eigenaar van de soort, met een signaal aan beheer.
  'inkoop.behandelaar',
  // ── batch-46 adres-reis (FO-390) — reisafwijking-beoordelaars ──────────────
  // `reisafwijking.beoordelaars`: wie de structurele reiscorrecties beheert -
  // exact de gate van POST /reisuren/correcties (REISAFSTANDEN.canApprove OF
  // WERKBONNEN.canApprove), backoffice-only (de deeplink is backoffice). Zelfde
  // permissie-groep-patroon als `voertuigen.beheer`.
  'reisafwijking.beoordelaars',
  // ── batch-59 onboarding-review (FO-437) ───────────────────────────────────
  // `relatie-onboarding.beoordelaars`: wie een ingediende relatie-onboarding
  // beoordeelt. Exact de gate van POST /client-onboarding/:id/review
  // (CRM.canEdit) VERENIGD met CRM.canApprove, backoffice-only (de deeplink is
  // backoffice), plus de twee persoonlijk betrokkenen: de medewerker die de
  // onboarding startte (`aangemaaktDoor`) en de relatiebeheerder
  // (`Client.responsibleEmployee`). bronEntiteitId = ClientOnboarding.id;
  // fail-soft (onbekende bron -> alleen de permissie-groep). Na FO-416 centraal
  // per event te overschrijven.
  'relatie-onboarding.beoordelaars',
  // ── batch-60 klantportaal-zichtbaar-verzoeken (FO-439b K4) ──────────────
  // `klantverzoeken.ontvangers`: de instelbare ontvangersgroep "Klantverzoeken"
  // (NotificatieInstelling `klantverzoeken.ontvangerUserIds`; initieel via het
  // scherm gevuld, NOOIT een persoon in code), terugval intake.team + WARN.
  // `klantverzoeken.escalatie`: wie de gebundelde onbeantwoord-escalatie krijgt
  // (instelling `klantverzoeken.escalatieUserIds`). Zonder actieve ingestelde
  // ontvanger leeg (was: rol MANAGER, daarna de ontvangersgroep);
  // batch-67 (FO-500): leeg gaat via de leveringslaag naar de eigenaar van de soort, met een signaal aan beheer.
  // Beide na FO-416 per event te overschrijven.
  'klantverzoeken.ontvangers',
  'klantverzoeken.escalatie',
  // ── batch-62 telefoonabonnementen (FO-463) ──────────────────────────────
  // `abonnementen.beheer`: wie het abonnementen-/telefonie-register beheert -
  // exact de gate van de register-mutaties (ASSETS.canEdit), backoffice-only
  // (de deeplink is backoffice). Permissie-groep-patroon zoals
  // `voertuigen.beheer`; functie-fan-out naar ADMINISTRATIE zodat de
  // gebundelde signalen één werkvoorraad-regel per dag worden. Na FO-416
  // centraal per event te overschrijven.
  'abonnementen.beheer',
  // ── batch-65 voorraad-besteladvies (FO-481) ─────────────────────────────
  // `inkoop.goedkeurders`: wie een besteladvies mag beoordelen en afronden -
  // exact de gate van die routes (INKOOP.canApprove), backoffice-only (de
  // deeplink is backoffice). Permissie-groep, persoon-only (er is geen INKOOP-
  // functie-inbox). De PO staat hier na FO-416 per event in te stellen.
  'inkoop.goedkeurders',
  // `voorraad.minimumbeheer`: wie een minimumniveau mag wijzigen (VOORRAAD.canEdit,
  // backoffice) - de gate van de minimumvoorstel-acties. `voorraad.beheer` is
  // canView: dan kreeg ook wie niets kan beslissen de taak.
  'voorraad.minimumbeheer',
  // ── batch-65 uitrusting-flow (FO-480) ─────────────────────────────────────
  // `uitrusting.levering.beoordelaar`: wie de bestelling(en) in een lever-/
  // afhaalafspraak goedkeurde (UitrustingBestelling.beoordeeldDoorId via
  // bronEntiteitId = UitrustingLevering.id), met terugval op de verwerkers.
  // Voor het signaal "herhaald niet verschenen". Persoon-only.
  'uitrusting.levering.beoordelaar',
  // ── FO-508 (spec 208) ──────────────────────────────────────────────────
  // `middel-uitzondering.beoordelaars`: wie een losse koppeling telefoon/
  // simkaart mag goedkeuren = exact de gate van de goedkeur-route (rol HR of
  // ADMIN, backoffice). Functie-fan-out naar HR (werkvoorraad).
  'middel-uitzondering.beoordelaars',
  // ── batch-66 contract-mijlpalen (FO-496) ──────────────────────────────────
  // `directie`: de leden van de directie = medewerkers met `Employee.isDirectie`.
  // Staat de vlag bij niemand, dan vallen de beheerders (rol ADMIN) in, zodat
  // een directiemelding nooit stil verdwijnt. Persoon-only.
  'directie',
  // `facilities`: wie de middelen van een nieuwe medewerker klaarzet = de
  // verwerkers van uitrustingbestellingen (`uitrusting.verwerkers`) verenigd met
  // wagenparkbeheer (`voertuigen.beheer`). Persoon-only.
  'facilities',
  // `contract-mijlpaal.doelgroep`: de doelgroepen van een mijlpaal zoals het
  // sjabloon ze noemt (HR / teamleider / directie / facilities / recruitment),
  // bronEntiteitId = ContractMijlpaal.id. De teamleider is die van het contract
  // (`teamleiderEmployeeId`), anders die van de afdeling; is er geen, dan gaat
  // de melding naar HR (fail-closed, nooit stil). Na FO-416 per event te
  // overschrijven.
  'contract-mijlpaal.doelgroep',
  // `contract-mijlpaal.escalatie`: idem voor de escalatie-doelgroepen van de
  // mijlpaal (M3 naar HR, M8 naar HR en directie).
  'contract-mijlpaal.escalatie',
  // `contract-mijlpaal.hr`: HR voor een melding over het contract als geheel
  // (bronEntiteitId = EmploymentContract.id: "teamleider toewijzen"). Anders dan
  // `all-hr` nooit de medewerker van dat contract zelf, en met de beheerders als
  // terugval wanneer er geen HR-gebruiker overblijft. Persoon-only.
  'contract-mijlpaal.hr',
  // ── batch-66 kluis-niveaus (FO-495) ───────────────────────────────────────
  // `kluis.eigenaar`: de eigenaar van het geheim (bronEntiteitId = KluisItem.id),
  // plus de caller-supplied `requesterUserId` (bv. wie een tijdelijke toegang
  // verleende). Geen bereikbare eigenaar -> de beheerders, zodat een kluismelding
  // nooit stil verdwijnt. Persoon-only (geen functie-inbox: het gaat om één geheim).
  'kluis.eigenaar',
  // `kluis.vrijgave.beoordelaars`: wie een 4-ogen-aanvraag mag beoordelen
  // (bronEntiteitId = KluisVrijgaveAanvraag.id): de eigenaar en elke beheerder,
  // nooit de aanvrager zelf. Exact de gate van de beoordeel-route.
  'kluis.vrijgave.beoordelaars',
  // `kluis.vrijgave.betrokkenen`: de aanvrager (requesterUserId) en de eigenaar
  // van het geheim (bronEntiteitId = KluisItem.id). De eigenaar hoort het ook
  // als een tweede beheerder besliste.
  'kluis.vrijgave.betrokkenen',
  // ── batch-66 uren-loonstroken (FO-492) ────────────────────────────────────
  // `loonstrook.vrijgave-bewakers`: wie hoort dat er stroken niet vanzelf
  // vrijkomen. De loonstrook-verantwoordelijken (met hun eigen terugval op rol
  // SALARIS); is er niemand, dan wie het loonstroken-overzicht kan openen en
  // bewerken (PAYROLL.canEdit plus de inzage-vlag magLoonstrokenZien,
  // backoffice). Een signaal met een deadline mag niet bij niemand aankomen
  // omdat de verantwoordelijken nooit zijn ingevuld.
  'loonstrook.vrijgave-bewakers',
  // ── batch-66 inkoop-afwijkingen (FO-497) ─────────────────────────────────
  // `boetes.rijgedrag`: HR plus de teamleider(s) van DEZE medewerker
  // (bronEntiteitId = Employee.id, afdeling-gescoped en fail-closed). Het signaal
  // noemt een medewerker bij naam: persoon-only, bewust ZONDER functie-inboxen
  // (via HR en TEAMLEIDER zag elke teamleider en elke beheerder het, ongeacht de
  // afdeling).
  'boetes.rijgedrag',
  // ── batch-67 taken-engine (FO-498 §2.5) ──────────────────────────────────
  // `taak.deadline-escalatie`: de aanmaker (aanvrager) van de taak, mits dat een
  // bestaand en actief account is (OperationeleTaak.createdById via
  // bronEntiteitId). Is de aanmaker het systeem (bv. 'system:contract-mijlpalen')
  // of een inactief account, dan de ingestelde escalatie-ontvanger van de
  // takenmodule (TaakInstelling 'taak.escalatieOntvangerId'). Persoon-only en
  // nooit een rol-brede terugval. Bewust een eigen sleutel: `taak.aanmaker`
  // (TAAK_GEREED_GEMELD) houdt zijn aflevering.
  'taak.deadline-escalatie',
  // ── batch-67 hr-brieven (FO-506) ──────────────────────────────────────────
  // `hr-besluit.controleurs`: wie de 4-ogen-controle van een besluitworkflow mag
  // doen (bronEntiteitId = HrBesluitWorkflow.id): de directie bij een VSO en bij
  // ontslag op staande voet, anders HR; nooit wie de workflow startte.
  'hr-besluit.controleurs',
  // `hr-besluit.stap-verantwoordelijke`: de rol van een open stap (bronEntiteitId
  // = HrBesluitStap.id): HR, directie, salaris, de teamleider van de medewerker,
  // facilities of planning. Fail-closed op HR.
  'hr-besluit.stap-verantwoordelijke',
  // De drie doelgroepen hieronder vervangen de brede groepen (`all-hr`,
  // `hr+manager`, `all-hr+salaris`) voor de HR-besluiten: een besluit is
  // need-to-know, dus nooit de medewerker zelf en nooit een rol die het besluit
  // niet mag zien. Bron = HrBesluitStap.id of HrBesluitWorkflow.id.
  // `hr-besluit.behandelaars`: HR (termijn nadert).
  'hr-besluit.behandelaars',
  // `hr-besluit.behandelaars+directie`: HR plus de directie (termijn verstreken).
  'hr-besluit.behandelaars+directie',
  // `hr-besluit.behandelaars+salaris`: HR, plus de salarisadministratie als zij
  // in dit besluit al een stap heeft afgerond (VSO herroepen).
  'hr-besluit.behandelaars+salaris',
  // ── batch-67 middelen-inname (FO-499 + FO-505) ────────────────────────────
  // `middel.overdracht-ontvanger`: de medewerker die een overdracht moet
  // bevestigen (bronEntiteitId = MiddelOverdrachtVerzoek.id). Persoon-only.
  'middel.overdracht-ontvanger',
  // `reparatie.goedkeurders`: de instelbare lijst goedkeurders van
  // reparatieverzoeken (MiddelenInstellingen); leeg = de beheerders (rol ADMIN
  // met backoffice-toegang). Nooit de indiener zelf (die klem zit in de route).
  'reparatie.goedkeurders',
  // -- batch-67 campagnes (FO-501/502/503) --
  // `campagne.deelnemer`: de medewerker van DEZE deelname (bronEntiteitId =
  // CampagneDeelname.id), persoon-only. `campagne.beheerders`: wie de campagne
  // beoordeelt (bronEntiteitId = campagne- of deelname-id; vakantie: VERLOF-
  // beoordelaars, bijtelling: HR + wagenparkbeheer, evenement: HR) plus ADMIN.
  // `evenement.organisatie`: HR + ADMIN (FO-416-instelbaar).
  'campagne.deelnemer',
  'campagne.beheerders',
  'evenement.organisatie',
  // -- batch-68 controle (FO-342 §7) --
  // `payroll.approvers`: wie de verloning mag accorderen (PAYROLL.canApprove),
  // zonder teamleiders (FO-342 §4: geen toegang tot de controle) en zonder de
  // accountant. Permissiegroep, leeg -> eigenaar.
  'payroll.approvers',
  // -- batch-69 bestelflow (FO-512) --
  // `uitrusting.beoordelaars`: wie ingediende uitrustingbestellingen beoordeelt:
  // de expliciete UITRUSTING.canApprove-rijen (actief, backoffice) plus de
  // eigenaar uit BedrijfsContact (zelfde bron als `isKasperOfHR`). Bewust NIET
  // heel HR/ADMIN (dat is wel de gate, maar een melding aan elke HR-medewerker
  // per bestelling is ruis). De vaste beoordelaars zet beheer via de FO-416-
  // override. Permissiegroep, leeg -> eigenaar.
  'uitrusting.beoordelaars',
  // `uitrusting.goedkeurder`: wie DEZE bestelling goedkeurde (bronEntiteitId =
  // UitrustingBestelling.id, `beoordeeldDoorId`), alleen als die nog actief is.
  // Persoon-only: is hij weg, dan hoeft niemand deze statusmelding.
  'uitrusting.goedkeurder',
  // -- batch-69 planbord-afspraak (FO-523) --
  // `afspraak.deelnemers`: de deelnemers van DEZE afspraak (bronEntiteitId =
  // PlanningAfspraak.id) met portaltoegang. Persoon-only: de tekst staat in de
  // tweede persoon ("maak je verslag") en landt nooit bij een ander.
  // `afspraak.escalatie`: de teamleider(s) van de deelnemers (die op het lopende
  // arbeidscontract, anders die van de afdeling) plus de accountmanager
  // (eindverantwoordelijke, anders eerste contactpersoon) van de klant; nooit een
  // deelnemer, alleen wie de link kan openen (backoffice plus PLANNING- of
  // CRM-leesrecht). Gericht; leeg -> eigenaar van de soort (FO-500).
  'afspraak.deelnemers',
  'afspraak.escalatie',
  // -- batch-69 km-balans (FO-515) --
  // `voertuigen.beheer+kostprijs`: wagenparkbeheer MET kostprijsinzage (VOERTUIGEN.canEdit
  // plus rol ADMIN/MANAGER/HR): wie de kilometerbalans mag openen. Permissiegroep, leeg ->
  // eigenaar (ADMIN).
  'voertuigen.beheer+kostprijs',
  // `skills.beoordelaars`: wie de skillmatrix mag beoordelen (SKILLS.canEdit, backoffice).
  // Permissiegroep, leeg -> eigenaar (HR).
  'skills.beoordelaars',
  // batch-71 gesprek-secties (FO-536): de eigenaar van een ontwikkeldoel; weg = eigenaar (HR).
  'ontwikkeldoel.eigenaar',
  // batch-71 gesprek-secties (FO-537): de houders Wagenpark of Magazijn uit de module-
  // instelling (`resolverExtras.controleHouder`); leeg -> eigenaar (ADMIN).
  'gesprekcontrole.houders',
  // -- batch-69 actiepunten (FO-514) --
  // `actiepunt.verantwoordelijke`: wie een punt van het register "Actiepunten app"
  // moet oppakken (bronEntiteitId = SysteemActiepunt.id): de toegewezen persoon,
  // anders de dragers van de verantwoordelijke rol (ACTIEPUNT_ROL_GEBRUIKERSROLLEN,
  // backoffice-toegang). Leeg (geen drager, bv. wagenpark) -> de eigenaar (ADMIN).
  'actiepunt.verantwoordelijke',
  // `actiepunten.samenvatting`: de samenvatting van het register. Het weekoverzicht
  // gaat naar de directie (isDirectie-vlag, terugval de beheerders); "nieuwe punten
  // na een oplevering" daarnaast naar de dragers van elke rol die nieuwe punten
  // kreeg (spec 214 AC4: rollen krijgen een melding bij nieuwe punten). De rollen
  // komen mee in de resolver-extras (`actiepuntRollen`).
  'actiepunten.samenvatting',
  // -- batch-69 magazijn-cijfers (FO-520) --
  // `voorraad.goedkeurders`: wie een tellingcorrectie boven de drempel mag
  // goedkeuren (VOORRAAD.canApprove, backoffice, geen accountant). Permissiegroep,
  // leeg -> eigenaar. De doelgroepen Voorraad-goedkeurders/-rapportage zijn
  // FO-416-overrides per event.
  'voorraad.goedkeurders',
  // -- batch-70 voertuig-meldingen (FO-527) --
  // `voertuig-melding.doelgroep`: per melding-opdracht (bronEntiteitId =
  // VoertuigMeldingOpdracht.id) de doelgroep van het kanaal: de
  // loonstrook-verantwoordelijken (doelgroep Salaris) voor de loonstrookcorrectie
  // (NAW), anders wagenparkbeheer (doelgroep Wagenpark, VOERTUIGEN bewerken).
  // Groep; leeg -> eigenaar (ADMIN).
  'voertuig-melding.doelgroep',
  // -- batch-70 app-dashboard (FO-526) --
  // `app-dashboard.doelgroep`: de doelgroep "App dashboard". De beheerders (ADMIN,
  // backoffice) plus wie bij de melding APP_DEPLOY_LIVE als ontvanger is ingesteld
  // (FO-416: personen of rollen, met backoffice-toegang; nooit de accountant).
  // Dezelfde kring mag het App dashboard openen (spec 226 §3.A1, A3).
  'app-dashboard.doelgroep',
  // batch-71 kiosk-magazijn (FO-532 §7) - HR en ADMIN: de beheerders van de magazijn-tablet.
  'kiosk.beheerders',
  // -- batch-71 workflow-sjablonen (FO-533) --
  // `workflow.doelgroep`: de lijsten op WorkflowInstelling (geen generiek doelgroep-
  // model, ADDENDA 2). De aanroeper geeft de doelgroepen mee; zonder: de eigenaar van
  // de run (bronEntiteitId). Groep; leeg -> eigenaar van het event (ADMIN).
  'workflow.doelgroep',
  // -- batch-71 kennismaking (FO-534) --
  // `kennismaking.verantwoordelijke`: de verantwoordelijke van de nalooplijst
  // (bronEntiteitId = KennismakingChecklist.id of ContractMijlpaal.id).
  'kennismaking.verantwoordelijke',
  // `kennismaking.verantwoordelijke+hr`: idem plus HR, nooit de medewerker zelf.
  'kennismaking.verantwoordelijke+hr',
  // `kennismaking.escalatie`: de escalatie-doelgroepen van de mijlpaal; bij de
  // weeklijst (bronEntiteitId = 'week-<jjjj-Www>') HR.
  'kennismaking.escalatie',
  // `kennismaking.actiepunt-houder`: de app-houders van het actiepunt
  // (bronEntiteitId = GespreksverslagAfspraak.id).
  'kennismaking.actiepunt-houder',
  // -- batch-71 verplichte-certificaten (FO-541) --
  // `verplichte-certificaten.hr+tl`: HR plus de teamleider van de medewerker (bronEntiteitId =
  // Employee.id): de teamleider op het lopende contract, anders die van de afdeling. Nooit
  // de medewerker zelf. Gericht; leeg -> eigenaar (HR).
  'verplichte-certificaten.hr+tl',
] as const
export type NotificatieRecipientResolverKey = (typeof NOTIFICATIE_RECIPIENT_RESOLVER_KEYS)[number]

// ─── DTOs ───────────────────────────────────────────────────────────────────

export interface NotificatieAfleveringDTO {
  id: string
  kanaal: NotificatieKanaalType
  status: NotificatieAfleveringStatus
  geprobeerdOp: string | null
  geleverdOp: string | null
  foutmelding: string | null
}

export interface NotificatieListRowDTO {
  id: string
  categorie: NotificatieCategorie
  niveau: NotificatieNiveau
  /** FO-126 — event-type van de melding; nodig voor het leesrapport (groepeert op eventTypeKey + bronEntiteitId). */
  eventTypeKey: NotificatieEventTypeKey | null
  titel: string
  inhoud: string
  link: string | null
  bronModule: string | null
  bronEntiteitId: string | null
  gelezenOp: string | null
  createdAt: string
  /**
   * FO-014 §6 — actie-vereiste/compliance-melding (afgeleid van het event-type
   * via `determineInboxItemType`). Beschermde meldingen horen bij Taken/Postvak
   * In en mogen NIET uit de wegveegbare bel verwijderd worden (geen handmatig
   * verwijderen, niet via "alles wissen"); de auto-opschoning ruimt ze wél op
   * zodra ze gelezen + oud genoeg zijn.
   */
  beschermd: boolean
  /**
   * batch-67 meldingen-ontvangers (FO-500) - waarom deze ontvanger deze melding
   * kreeg (resolver, soort, override, escalatieniveau, postvak-functie). `null`
   * voor meldingen van voor batch-67 of een pad dat geen spoor schrijft.
   */
  ontvangerBron: NotificatieOntvangerBronDTO | null
}

export interface NotificatieDetailDTO extends NotificatieListRowDTO {
  user: {
    id: string
    name: string | null
    email: string
  }
  afleveringen: NotificatieAfleveringDTO[]
}

// ─── FO-126 (batch-24) — leesrapport "wie heeft belangrijke notificatie X gelezen" ──
// De per-user Notificatie-rijen worden gegroepeerd op (eventTypeKey,
// bronEntiteitId); `gelezenOp != null` betekent dat die ontvanger het las.
export interface NotificatieLeesrapportRowDTO {
  userId: string
  naam: string | null
  email: string
  /** ISO datetime waarop deze ontvanger de notificatie las; null = nog ongelezen. */
  gelezenOp: string | null
  /** ISO datetime waarop de notificatie-rij voor deze ontvanger is aangemaakt. */
  ontvangenOp: string
}

export interface NotificatieLeesrapportDTO {
  eventTypeKey: NotificatieEventTypeKey | null
  bronEntiteitId: string | null
  /** Representatieve titel (van de eerste rij in de groep). */
  titel: string
  /** Totaal aantal ontvangers (rijen) in de groep. */
  totaal: number
  /** Aantal ontvangers met gelezenOp gezet. */
  gelezen: number
  rows: NotificatieLeesrapportRowDTO[]
}

/**
 * Push subscription DTO — bevat NOOIT de p256dh/auth keys (security).
 * Alleen identificerende metadata zodat de user zijn devices kan beheren.
 */
export interface PushSubscriptionDTO {
  id: string
  userAgent: string | null
  lastUsedAt: string
  createdAt: string
}

export interface NotificatieVoorkeurDTO {
  eventTypeKey: NotificatieEventTypeKey
  inApp: boolean | null
  push: boolean | null
  email: boolean | null
}

// ─── Zod request-body schemas ────────────────────────────────────────────────

/**
 * Subscribe-payload zoals geleverd door PushSubscription.toJSON() in de browser.
 * endpoint moet uniek zijn — server doet upsert per (userId, endpoint).
 */
export const subscribePushSchema = z.object({
  endpoint: z.string().url().max(2048),
  keys: z.object({
    p256dh: z.string().min(1).max(512),
    auth: z.string().min(1).max(512),
  }),
  userAgent: z.string().trim().max(500).optional(),
})

export type SubscribePushInput = z.infer<typeof subscribePushSchema>

export const updateVoorkeurSchema = z.object({
  eventTypeKey: z.enum(NOTIFICATIE_EVENT_TYPE_KEY),
  inApp: z.boolean().nullable(),
  push: z.boolean().nullable(),
  email: z.boolean().nullable(),
})

export type UpdateVoorkeurInput = z.infer<typeof updateVoorkeurSchema>

// ─── Dempen per bron (2026-10) ───────────────────────────────────────────────
// Een herhalende melding over EEN bron (bv. de dagelijkse aanzegtermijn-
// escalatie van een contract) stilzetten: voor jezelf (MIJ) of, met het juiste
// modulerecht, voor iedereen (IEDEREEN = uitgeschakeld voor deze bron). Alleen
// templates met `dempbaarPerBron` in de registry zijn hier toegestaan; de API
// weigert andere events met NIET_DEMPBAAR. Gedeeld schema: het form valideert
// er client-side mee, de API met safeParse.

export const NOTIFICATIE_DEMPING_BEREIK = ['MIJ', 'IEDEREEN'] as const
export type NotificatieDempingBereik = (typeof NOTIFICATIE_DEMPING_BEREIK)[number]

export const createNotificatieDempingSchema = z.object({
  eventTypeKey: z.enum(NOTIFICATIE_EVENT_TYPE_KEY),
  bronEntiteitId: z.string().trim().min(1).max(64),
  bereik: z.enum(NOTIFICATIE_DEMPING_BEREIK),
  /** ISO-datum (YYYY-MM-DD, NL-dag t/m); null/weg = tot de demping wordt opgeheven. */
  totDatum: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum als JJJJ-MM-DD')
    .nullable()
    .optional(),
  /** Verplicht bij IEDEREEN (herleidbaar in het overzicht), vrij bij MIJ. */
  reden: z.string().trim().max(500).nullable().optional(),
}).superRefine((v, ctx) => {
  if (v.bereik === 'IEDEREEN' && !(v.reden && v.reden.length > 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['reden'],
      message: 'Geef een reden op als je de melding voor iedereen uitschakelt.',
    })
  }
})
export type CreateNotificatieDempingInput = z.infer<typeof createNotificatieDempingSchema>

export const notificatieDempingBronQuerySchema = z.object({
  eventTypeKey: z.enum(NOTIFICATIE_EVENT_TYPE_KEY),
  bronEntiteitId: z.string().trim().min(1).max(64),
})

export interface NotificatieDempingDTO {
  id: string
  eventTypeKey: NotificatieEventTypeKey
  bronEntiteitId: string
  bereik: NotificatieDempingBereik
  /** Alleen bij MIJ: de gebruiker voor wie de demping geldt. */
  userId: string | null
  /** ISO-datum (YYYY-MM-DD) t/m, of null = tot opheffen. */
  totDatum: string | null
  reden: string | null
  doorNaam: string | null
  createdAt: string
  /** Mag de aanvrager deze demping opheffen (eigen MIJ, of IEDEREEN met recht)? */
  magOpheffen: boolean
}

/** Antwoord van GET /notificaties/dempingen: de dempingen op een bron + wat de aanvrager mag. */
export interface NotificatieDempingBronDTO {
  eventTypeKey: NotificatieEventTypeKey
  bronEntiteitId: string
  /** False = dit event kent geen dempen per bron (API weigert POST met NIET_DEMPBAAR). */
  dempbaar: boolean
  /** Mag de aanvrager de melding voor IEDEREEN uitschakelen (modulerecht van het event)? */
  magIedereen: boolean
  dempingen: NotificatieDempingDTO[]
}

// Server-sorteerbare kolommen — moeten 1-op-1 mappen op een echte DB-kolom van
// het Notificatie-model (zie orderBy-mapping in routes/notificaties.ts). De
// "status"-kolom sorteert op `gelezenOp` (gelezen/ongelezen). Afgeleide of
// niet-kolom headers staan NIET in deze lijst.
export const NOTIFICATIE_SORT_FIELD = [
  'createdAt',
  'categorie',
  'titel',
  'inhoud',
  'bronModule',
  'status',
] as const
export type NotificatieSortField = (typeof NOTIFICATIE_SORT_FIELD)[number]

export const listNotificatiesQuerySchema = z.object({
  categorie:    z.enum(NOTIFICATIE_CATEGORIE).optional(),
  ongelezen:    queryBoolean().optional(),
  userId:       idParam().optional(),
  // Org-breed backoffice-overzicht (alle users). Alleen gehonoreerd voor een
  // echte NOTIFICATIES-viewer; zonder dit blijft de lijst op de eigen user.
  alle:         queryBoolean().optional(),
  vanaf:        z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  totEnMet:     z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  search:       z.string().trim().max(100).optional(),
  sortField:    z.enum(NOTIFICATIE_SORT_FIELD).optional(),
  sortDir:      z.enum(['asc', 'desc']).optional(),
  page:         z.coerce.number().int().positive().default(1),
  pageSize:     z.coerce.number().int().positive().max(100).default(20),
})

export type ListNotificatiesQuery = z.infer<typeof listNotificatiesQuerySchema>

/**
 * KPI's voor de backoffice admin-dashboard.
 *  - vandaagVerzonden: aantal notificaties met createdAt >= vandaag 00:00
 *  - vandaagGefaald: aantal afleveringen status=GEFAALD met createdAt vandaag
 *  - bouncePct: gefaald / (gefaald + geleverd) * 100, 0 als noemer 0
 */
export interface NotificatieAdminStatsDTO {
  vandaagVerzonden: number
  vandaagGefaald: number
  bouncePct: number
}

// ─── Notificatie-instellingen (FO-014 §8 — auto-opschoning, instelbaar) ───────
//
// Org-brede, instelbare retentie voor bel-notificaties: gelezen meldingen ouder
// dan `retentieDagen` worden automatisch (soft) verwijderd. Default 30 dagen
// (CC.0b). Ongelezen meldingen blijven altijd staan.
export const NOTIFICATIE_RETENTIE_DAGEN_DEFAULT = 30

export interface NotificatieInstellingenDTO {
  /** Na hoeveel dagen gelezen meldingen automatisch worden opgeschoond. */
  retentieDagen: number
}

export const notificatieInstellingenSchema = z
  .object({
    retentieDagen: z
      .number()
      .int('Hele dagen graag')
      .min(1, 'Minimaal 1 dag')
      .max(365, 'Maximaal 365 dagen'),
  })
  .strict()
export type NotificatieInstellingenInput = z.infer<typeof notificatieInstellingenSchema>

// ─── Event-type DTOs + Zod schemas ───────────────────────────────────────────

export const notificatieEventDtoSchema = z.object({
  key: z.enum(NOTIFICATIE_EVENT_TYPE_KEY),
  name: z.string(),
  description: z.string(),
  module: z.string(),
  defaultCategorie: z.enum(NOTIFICATIE_CATEGORIE),
  defaultInApp: z.boolean(),
  defaultPush: z.boolean(),
  defaultEmail: z.boolean(),
  recipientResolver: z.string(),
  actief: z.boolean(),
})
export type NotificatieEventDTO = z.infer<typeof notificatieEventDtoSchema>

export const notificatieEventUpdateSchema = z.object({
  defaultCategorie: z.enum(NOTIFICATIE_CATEGORIE).optional(),
  defaultInApp: z.boolean().optional(),
  defaultPush: z.boolean().optional(),
  defaultEmail: z.boolean().optional(),
  actief: z.boolean().optional(),
})

export const USER_ROLE_FOR_DEFAULT = ['MONTEUR', 'TEAMLEIDER', 'PLANNER', 'HR', 'SALARIS', 'MANAGER', 'ADMIN'] as const
export type UserRoleForDefault = (typeof USER_ROLE_FOR_DEFAULT)[number]

export const notificatieEventRoleDefaultDtoSchema = z.object({
  eventTypeKey: z.enum(NOTIFICATIE_EVENT_TYPE_KEY),
  role: z.enum(USER_ROLE_FOR_DEFAULT),
  inApp: z.boolean().nullable(),
  push: z.boolean().nullable(),
  email: z.boolean().nullable(),
})
export type NotificatieEventRoleDefaultDTO = z.infer<typeof notificatieEventRoleDefaultDtoSchema>

export const notificatieEventRoleDefaultUpsertSchema = z.object({
  eventTypeKey: z.enum(NOTIFICATIE_EVENT_TYPE_KEY),
  role: z.enum(USER_ROLE_FOR_DEFAULT),
  inApp: z.boolean().nullable(),
  push: z.boolean().nullable(),
  email: z.boolean().nullable(),
})

export const testFireEventSchema = z.object({
  eventTypeKey: z.enum(NOTIFICATIE_EVENT_TYPE_KEY),
  targetUserId: idParam(),
})

// ─── PR-B (spec 18) — Voorkeur-override DTOs ─────────────────────────────────
//
// Een override is een admin-instelling die per (event × user) een gebruiker
// expliciet INCLUDEert (extra ontvanger) of EXCLUDEert (uitgesloten ontvanger).
// Optioneel kanaal-specifiek via inApp/push/email-velden (null = volg de
// 3-laags resolutie). De `configurable.recipients`-resolver leest INCLUDEs
// als doelgroep voor events zonder vaste doelgroep (bv. VERJAARDAG_VANDAAG).
// batch-67 (FO-500): zonder INCLUDEs gaat zo'n melding naar de eigenaar van de
// soort, niet meer naar iedereen met portal-toegang.

export const RECIPIENT_OVERRIDE_TYPE = ['INCLUDE', 'EXCLUDE'] as const
export type RecipientOverrideType = (typeof RECIPIENT_OVERRIDE_TYPE)[number]

export interface NotificatieVoorkeurOverrideDTO {
  id: string
  eventTypeKey: NotificatieEventTypeKey
  userId: string
  userName: string | null
  userEmail: string
  type: RecipientOverrideType
  inApp: boolean | null
  push: boolean | null
  email: boolean | null
  ingesteldDoorId: string | null
  ingesteldDoorNaam: string | null
  createdAt: string
}

export const createVoorkeurOverrideSchema = z.object({
  eventTypeKey: z.enum(NOTIFICATIE_EVENT_TYPE_KEY),
  userId: idParam(),
  type: z.enum(RECIPIENT_OVERRIDE_TYPE),
  inApp: z.boolean().nullable().optional(),
  push: z.boolean().nullable().optional(),
  email: z.boolean().nullable().optional(),
})
export type CreateVoorkeurOverrideInput = z.infer<typeof createVoorkeurOverrideSchema>

export const updateVoorkeurOverrideSchema = z.object({
  type: z.enum(RECIPIENT_OVERRIDE_TYPE).optional(),
  inApp: z.boolean().nullable().optional(),
  push: z.boolean().nullable().optional(),
  email: z.boolean().nullable().optional(),
})
export type UpdateVoorkeurOverrideInput = z.infer<typeof updateVoorkeurOverrideSchema>

// PR-B (spec 18) — admin/test-trigger via API: vuur willekeurig event af
// vanuit `/instellingen/notificaties` zonder dat een bron-entiteit bestaat.
export const triggerEventSchema = z.object({
  bronEntiteitId: z.string().min(1).optional(),
  targetUserId: idParam().optional(),
  templateVars: z
    .record(z.string(), z.union([z.string(), z.number(), z.null()]))
    .optional(),
})
export type TriggerEventInput = z.infer<typeof triggerEventSchema>
